import {
  ADCODE_REQUEST_COMMENT_MAX_LENGTH,
  ADJUST_REASON_CATEGORIES,
  ADVERTISER_REVIEW_MODES,
  ADVERTISER_REVIEW_VERDICTS,
  AgentConnectionType,
  AgentStatus,
  ApprovedOriginalFileOrigin,
  ApprovedOriginalReceivedVia,
  ApprovedOriginalSource,
  AttendanceStatus,
  BLOCK_REASON_KINDS,
  BoostingStage,
  CAMPAIGN_AD_CODE_PATTERN,
  CAMPAIGN_DIFFICULTY_CONDITIONS,
  CAMPAIGN_TIMELINE_LABEL_MAX,
  CAMPAIGN_TIMELINE_MAX_ENTRIES,
  CHANNEL_NOTIFY_LEVELS,
  CONTRACT_PARTY_KINDS,
  CREATOR_RELATION_SOURCE_SYSTEM,
  CampaignDifficulty,
  CampaignTimelineMilestone,
  CheckpointRejectReasonKind,
  CompensationType,
  DISTRIBUTION_KINDS,
  EmailSchema,
  HolidayPaidType,
  Iso8601Schema,
  IsoCountryCodeSchema,
  LeaveType,
  LoginTicketSchema,
  MESSAGE_CONTENT_MAX_LENGTH,
  MappingWarningCode,
  NavigationContextSchema,
  OUTREACH_DATE_FORMATS,
  OWNER_CHANGE_CAUSES,
  POINTS_MIN,
  PROOF_DESCRIPTION_MAX_LENGTH,
  PieceworkAudienceScope,
  PieceworkCycle,
  QuestDifficulty,
  QuestProposalDescriptionSchema,
  QuestProposalTitleSchema,
  QuestScope,
  QuestTargetType,
  RELATION_HISTORY_ORIGINS,
  RELATION_HISTORY_TYPES,
  RELATION_STATES,
  REPLY_TEMPLATE_KINDS,
  REPLY_TEMPLATE_LAYERS,
  REPLY_TEMPLATE_STAGES,
  REWARD_POINTS_MIN,
  ROSTER_CONTENT_REVIEWS,
  ROSTER_MANUAL_DROP_KINDS,
  ROSTER_PRICE_PROPOSERS,
  ROSTER_TAKEDOWN_REQUESTERS,
  RosterStatus,
  SCENARIO_CREATOR_COUNT_MAX,
  SCENARIO_CREATOR_COUNT_MIN,
  SCENARIO_OPERATIONAL_ISSUE_REPORT_COMMENT_MAX_LENGTH,
  SCENARIO_SEED_QUEST_ORIGIN,
  SCENARIO_TARGET_TOTAL_VIEWS_MAX,
  SCENARIO_TEMPLATE_CREATOR_COUNT_MAX,
  SCENARIO_TEMPLATE_CREATOR_COUNT_MIN,
  SCENARIO_TEMPLATE_DURATION_DAYS_MIN,
  SCENARIO_TEMPLATE_TARGET_REGION_MAX,
  SETTLEMENT_AMOUNT_PATTERN,
  SETTLEMENT_CURRENCY_PATTERN,
  SETTLEMENT_REASON_REQUIRED_STATUSES,
  SETTLEMENT_REQUESTABLE_KINDS,
  STOP_RULES_MAX,
  ScenarioCampaignType,
  ScenarioContentDeliveryIssueReason,
  ScenarioOperationalIssueArea,
  ScenarioOperationalIssueReportStatus,
  ScenarioRecruitingIssueReason,
  SettlementPayoutMethod,
  SettlementStatus,
  StopRuleDirection,
  StopRuleMetric,
  TeamCapability,
  TeamRole,
  TeamType,
  TimelineEventType,
  UserLevel,
  UserRole,
  UserStatus,
  VIDEO_RIGHT_AXES,
  VIDEO_RIGHT_VALUES,
  WARM_POOL_BASES,
  WARM_POOL_STATES,
  WIKI_DISCUSSION_STATUSES,
  WIKI_DOCUMENT_KINDS,
  WorkType,
  creatorEmailSchema,
  deriveCampaignDifficulty,
  findUnknownTemplateVariables,
  hasEmailOrTicket,
  isAllowedCropAspectRatio,
  isValidDateOnly,
  isWikiPageTitle,
  normalizeWikiTitle,
  paypalMeSchema,
  resolveWikiArea,
  rosterScheduleConfirmInputSchema,
  targetSlotOverridesSchema,
  targetSlotParticipantConditionsSchema,
  targetSlotRequirementsSchema,
  teamDifficultyPricingSchema
} from "./chunk-WAJJ5T7C.mjs";

// src/dto/advertiser.ts
import { z } from "zod";
var advertiserContactSchema = z.object({
  name: z.string().min(1).max(40),
  roleTitle: z.string().max(40).optional(),
  email: z.email().optional(),
  phone: z.string().max(40).optional(),
  isPrimary: z.boolean()
}).strict();
var createAdvertiserSchema = z.object({
  name: z.string().min(1).max(80),
  country: IsoCountryCodeSchema,
  firstMeetingAt: Iso8601Schema.optional(),
  note: z.string().max(500).nullable().optional(),
  contractPartyKind: z.enum(CONTRACT_PARTY_KINDS).optional(),
  /** @deprecated 옛 번들 호환. contractPartyKind 가 없을 때 true 만 AGENCY 로 읽는다 */
  isAgency: z.boolean().optional(),
  contacts: z.array(advertiserContactSchema).optional()
}).strict();
var mergeFromDraftSchema = createAdvertiserSchema;
var mergeAdvertiserSchema = z.object({
  targetId: z.uuid()
}).strict();
var updateAdvertiserContactSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().min(1).max(40),
  roleTitle: z.string().max(40).optional(),
  email: z.email().optional(),
  phone: z.string().max(40).optional(),
  isPrimary: z.boolean(),
  ended: z.boolean().optional(),
  /** 소속 브랜드. 그 광고주의 브랜드여야 한다. null 이면 광고주 쪽 사람으로 되돌린다. */
  brandId: z.uuid().nullable().optional()
}).strict();
var updateAdvertiserSchema = z.object({
  displayName: z.string().max(255).nullable().optional(),
  note: z.string().max(2048).nullable().optional(),
  country: IsoCountryCodeSchema.nullable().optional(),
  firstMeetingAt: Iso8601Schema.nullable().optional(),
  contractPartyKind: z.enum(CONTRACT_PARTY_KINDS).optional(),
  /** @deprecated 옛 번들 호환. contractPartyKind 가 없을 때 true 만 AGENCY 로 읽는다 */
  isAgency: z.boolean().optional(),
  contacts: z.array(updateAdvertiserContactSchema).optional()
}).strict();

// src/dto/advertiser-brand.ts
import { z as z2 } from "zod";
var advertiserBrandBodySchema = z2.object({
  name: z2.string().trim().min(1).max(80)
}).strict();

// src/dto/attendance.ts
import { z as z3 } from "zod";
var createCorrectionSchema = z3.object({
  attendanceId: z3.uuid(),
  reason: z3.string().min(5, "\uC815\uC815 \uC0AC\uC720\uB294 5\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").max(500, "\uC815\uC815 \uC0AC\uC720\uB294 500\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"),
  fieldName: z3.enum(["clockInAt", "clockOutAt", "status"]),
  newValue: z3.string().max(100, "\uC815\uC815 \uAC12\uC740 100\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4")
}).strict();
var reviewCorrectionSchema = z3.object({
  comment: z3.string().max(1e3, "\uCF54\uBA58\uD2B8\uB294 1000\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4").nullable().optional()
}).strict();
var approveCorrectionSchema = z3.object({
  comment: z3.string().max(1e3, "\uCF54\uBA58\uD2B8\uB294 1000\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4").nullable().optional(),
  seenRevisions: z3.record(z3.uuid(), z3.number().int().min(0)).refine((revisions) => Object.keys(revisions).length > 0, {
    message: "\uAC80\uD1A0\uD55C \uC815\uC815 \uC694\uCCAD\uC758 \uD310 \uBC88\uD638\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4"
  })
}).strict();
var resubmitCorrectionSchema = z3.object({
  attendanceId: z3.uuid(),
  reason: z3.string().min(5, "\uC815\uC815 \uC0AC\uC720\uB294 5\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").max(500, "\uC815\uC815 \uC0AC\uC720\uB294 500\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"),
  values: z3.object({
    clockInAt: z3.string().max(100, "\uC815\uC815 \uAC12\uC740 100\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4").optional(),
    clockOutAt: z3.string().max(100, "\uC815\uC815 \uAC12\uC740 100\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4").optional(),
    status: z3.string().max(100, "\uC815\uC815 \uAC12\uC740 100\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4").optional()
  }).strict().refine((values) => Object.values(values).some((v) => v !== void 0), {
    message: "\uB2E4\uC2DC \uC2E0\uCCAD\uD560 \uC815\uC815 \uAC12\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"
  })
}).strict();
var createSegmentCorrectionSchema = z3.object({
  attendanceId: z3.uuid(),
  segmentId: z3.uuid(),
  reason: z3.string().min(5, "\uC815\uC815 \uC0AC\uC720\uB294 5\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").max(500, "\uC815\uC815 \uC0AC\uC720\uB294 500\uC790\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"),
  newType: z3.enum(["WORK", "BREAK"])
}).strict();
var AUDIT_LOG_EXPORT_MAX_ROWS = 1e4;
var clockInSchema = z3.object({
  occurredAt: Iso8601Schema.optional()
}).strict().default({});
var clockOutSchema = z3.object({
  occurredAt: Iso8601Schema.optional()
}).strict().default({});
var resolveMissedClockOutSchema = z3.object({
  staleAttendanceId: z3.uuid(),
  clockOutTime: Iso8601Schema
}).strict();
var resolveMissedClockInSchema = z3.object({
  clockInTime: Iso8601Schema,
  clockOutTime: Iso8601Schema
}).strict();
var segmentEditItemSchema = z3.object({
  id: z3.string().optional(),
  type: z3.enum(["WORK", "BREAK"]),
  startAt: Iso8601Schema,
  endAt: Iso8601Schema.nullable()
}).strict();
var editAttendanceSchema = z3.object({
  clockInAt: Iso8601Schema.optional(),
  clockOutAt: Iso8601Schema.optional(),
  status: z3.enum([
    AttendanceStatus.CLOCKED_IN,
    AttendanceStatus.ON_BREAK,
    AttendanceStatus.CLOCKED_OUT,
    AttendanceStatus.ABSENT
  ]).optional(),
  note: z3.string().optional(),
  reason: z3.string().min(2),
  segments: z3.array(segmentEditItemSchema).optional()
}).strict();
var createAttendanceSchema = z3.object({
  userId: z3.string(),
  date: z3.string(),
  clockInAt: Iso8601Schema,
  clockOutAt: Iso8601Schema.optional(),
  status: z3.enum([
    AttendanceStatus.CLOCKED_IN,
    AttendanceStatus.ON_BREAK,
    AttendanceStatus.CLOCKED_OUT,
    AttendanceStatus.ABSENT
  ]).optional(),
  note: z3.string().optional(),
  reason: z3.string().min(2),
  segments: z3.array(segmentEditItemSchema).optional()
}).strict();
var reassignSegmentSchema = z3.object({
  segmentId: z3.uuid(),
  direction: z3.enum(["prev", "next"])
}).strict();
var findAggregateAttendancesSchema = z3.object({
  startDate: Iso8601Schema,
  endDate: Iso8601Schema
}).strict();
var reviewOffDaySchema = z3.object({
  comment: z3.string().nullable().optional()
}).strict();
var batchReviewOffDaySchema = z3.object({
  requestIds: z3.array(z3.string()).min(1),
  comment: z3.string().nullable().optional()
}).strict();

// src/dto/compensation.ts
import { z as z4 } from "zod";
var createCompensationSchema = z4.object({
  type: z4.enum([CompensationType.HOURLY, CompensationType.MONTHLY]),
  amount: z4.number().int().positive(),
  effectiveFrom: Iso8601Schema
}).strict();

// src/dto/attendance-query.ts
import { z as z5 } from "zod";
var weeklySummaryQuerySchema = z5.object({
  weekStart: Iso8601Schema
}).strict();
var periodDateSchema = z5.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4");
var periodSummaryQuerySchema = z5.object({
  startDate: periodDateSchema,
  endDate: periodDateSchema
}).strict().refine((v) => v.startDate <= v.endDate, {
  message: "\uC885\uB8CC\uC77C\uC740 \uC2DC\uC791\uC77C\uACFC \uAC19\uAC70\uB098 \uB4A4\uC5EC\uC57C \uD569\uB2C8\uB2E4",
  path: ["endDate"]
});
var REVIEWED_HISTORY_MAX_DAYS = 366;

// src/dto/attendance-day-schedule.ts
import { z as z6 } from "zod";
var dayScheduleDateSchema = z6.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "date \uB294 YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4");
var dayScheduleMonthSchema = z6.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month \uB294 YYYY-MM \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4");
var dayScheduleContentSchema = z6.string().trim().min(1).max(500);
var dayScheduleSharedTeamSchema = z6.string().uuid().nullable();
var endsAfterStart = (v) => !v.date || !v.endsOn || v.endsOn > v.date;
var ENDS_AFTER_START = "\uB05D \uB0A0\uC9DC\uB294 \uC2DC\uC791 \uB0A0\uC9DC\uBCF4\uB2E4 \uB4A4\uC5EC\uC57C \uD569\uB2C8\uB2E4.";
var createDayScheduleSchema = z6.object({
  date: dayScheduleDateSchema,
  /** 기간 일정의 마지막 날. 하루짜리는 비운다 */
  endsOn: dayScheduleDateSchema.nullable().optional(),
  content: dayScheduleContentSchema,
  sharedTeamId: dayScheduleSharedTeamSchema.optional(),
  /** 알림 받기. 안 보내면 꺼짐 */
  notifyEnabled: z6.boolean().optional(),
  /**
   * 방금 지운 일정을 「되돌리기」로 다시 만드는 요청이다. 서버가 그 일정의 취소 알림을 받은
   * 사람에게 「일정이 다시 등록됐어요」를 보낸다
   */
  undoDelete: z6.literal(true).optional()
}).strict().refine(endsAfterStart, { message: ENDS_AFTER_START, path: ["endsOn"] });
var updateDayScheduleSchema = z6.object({
  content: dayScheduleContentSchema.optional(),
  /** null 이면 하루 일정으로 되돌린다. 시작 날짜보다 뒤인지는 서버가 저장된 시작과 맞춰 본다 */
  endsOn: dayScheduleDateSchema.nullable().optional(),
  /** null 이면 공유를 끈다 */
  sharedTeamId: dayScheduleSharedTeamSchema.optional(),
  /** 끄면 남은 회차가 안 가고, 다시 켜면 아직 안 지난 회차부터 간다 */
  notifyEnabled: z6.boolean().optional()
}).strict();

// src/dto/audit.ts
import { z as z7 } from "zod";
var queryAuditLogsSchema = z7.object({
  resource: z7.string().optional(),
  // 자동 실행분을 되찾는 유일한 경로. 크론 판정 결과가 감사 행으로만 남는데
  // 이 필터가 없으면 .strict() 라 조회 자체가 400 이라, 남긴 결과를 아무도 못 연다.
  operation: z7.string().optional(),
  actorId: z7.uuid().optional(),
  startDate: Iso8601Schema.optional(),
  endDate: Iso8601Schema.optional(),
  page: z7.coerce.number().int().min(1).default(1),
  limit: z7.coerce.number().int().min(1).max(100).default(20)
}).strict();
var AUDIT_LOG_PII_RETENTION_DAYS = 90;
var AUDIT_LOG_ROW_RETENTION_DAYS = 730;
var AUDIT_LOG_SYSTEM_ACTION = "SYSTEM";
var AUDIT_LOG_ACCESS_ACTION = "READ";
var AUDIT_PATH_SCOPED_SENSITIVE_KEYS = [
  {
    // 검색 질문은 보관하지 않고 별도 READ 행에 반환 문서·판 ID만 남긴다.
    resource: "knowledge",
    operation: "search",
    keys: ["query"]
  },
  {
    // PermissionGuard의 거부 감사는 라우트 작업명 대신 요구 권한을 기록한다.
    resource: "knowledge",
    operation: "read",
    keys: ["query"]
  },
  {
    resource: "scenario",
    operation: "settlement_execute",
    keys: ["reason"]
  },
  {
    resource: "campaign-roster",
    operation: "manual_communication_create",
    keys: ["note"]
  },
  {
    // 수정한 본문의 정본은 ChatMessage.content 이고 채널 멤버만 읽는다.
    resource: "chat",
    operation: "message_edit",
    keys: ["content"]
  }
];
var APPROVAL_HISTORY_AUDIT_TARGETS = [
  { resource: "quest", operation: "approve" },
  { resource: "quest", operation: "reject" },
  { resource: "budget-ledger", operation: "budget-confirm" }
];
var AUDIT_LOG_ROW_RETENTION_EXEMPTIONS = [
  ...APPROVAL_HISTORY_AUDIT_TARGETS,
  { resource: "budget-ledger", operationPrefix: "cron-" }
];

// src/dto/auth-passkey.ts
import { z as z8 } from "zod";
var registrationResponseSchema = z8.object({
  clientDataJSON: z8.string().min(1),
  attestationObject: z8.string().min(1),
  authenticatorData: z8.string().optional(),
  transports: z8.array(z8.string()).optional(),
  publicKeyAlgorithm: z8.number().optional(),
  publicKey: z8.string().optional()
}).strict();
var verifyPasskeyRegistrationSchema = z8.object({
  id: z8.string().min(1),
  rawId: z8.string().min(1),
  type: z8.string().min(1),
  response: registrationResponseSchema,
  // 외부 webauthn payload — permissive 유지. 확장 미사용 authenticator(react-native-passkey 등)는
  // clientExtensionResults 를 아예 안 보내기도 하므로 optional (필수로 두면 모바일 등록이 400).
  clientExtensionResults: z8.record(z8.string(), z8.unknown()).optional(),
  authenticatorAttachment: z8.string().optional(),
  friendlyName: z8.string().max(100).optional()
}).strict();
var renamePasskeySchema = z8.object({
  friendlyName: z8.string().min(1).max(100)
}).strict();
var passkeyLoginOptionsSchema = z8.object({
  email: EmailSchema.optional(),
  ticket: LoginTicketSchema.optional()
}).strict().refine(hasEmailOrTicket, "\uC774\uBA54\uC77C\uACFC \uB85C\uADF8\uC778 \uD45C \uC911 \uD558\uB098\uB9CC \uBCF4\uB0B4 \uC8FC\uC138\uC694");
var passkeyLoginVerifySchema = z8.object({
  email: EmailSchema.optional(),
  ticket: LoginTicketSchema.optional(),
  // 구 @Allow AuthenticationResponseJSON — 외부 webauthn payload 라 permissive 유지.
  credential: z8.unknown()
}).strict().refine(hasEmailOrTicket, "\uC774\uBA54\uC77C\uACFC \uB85C\uADF8\uC778 \uD45C \uC911 \uD558\uB098\uB9CC \uBCF4\uB0B4 \uC8FC\uC138\uC694");

// src/dto/bug-report.ts
import { z as z10 } from "zod";

// src/dto/quest.ts
import { z as z9 } from "zod";
var rejectQuestSchema = z9.object({
  reason: z9.string().min(1)
}).strict();
var revokeWorldQuestSchema = z9.object({
  reason: z9.string().min(1)
}).strict();
var withdrawQuestSchema = z9.object({
  reason: z9.string().trim().min(1).max(500)
}).strict();
var EXPIRY_CLOSE_EVENT_PREFIX = "EXPIRY_CLOSE:";
var EXPIRY_CLOSE_DISPOSITIONS = ["JUSTIFIED", "NEGLECTED"];
var expiryCloseQuestSchema = z9.object({
  disposition: z9.enum(EXPIRY_CLOSE_DISPOSITIONS),
  reason: z9.string().trim().min(1).max(500)
}).strict();
var addQuestDependencySchema = z9.object({
  dependsOnId: z9.string().uuid(),
  reason: z9.string().trim().min(1).max(2e3).optional(),
  groupKey: z9.string().trim().min(1).nullable().optional()
}).strict();
var PRICING_REASON_CODES = [
  "SCOPE_LARGER",
  "SCOPE_SMALLER",
  "PROPOSAL_ADOPTED",
  "OTHER"
];
var PRICING_GROUND_PREFIX = "\uC6B0\uC120\uC21C\uC704 \uADFC\uAC70:";
var pricingGroundSchema = z9.object({
  questions: z9.array(z9.union([z9.literal(1), z9.literal(2), z9.literal(3)])).min(1).refine((q) => new Set(q).size === q.length, "\uBB38\uD56D \uBC88\uD638\uAC00 \uACB9\uCE69\uB2C8\uB2E4"),
  reason: z9.string().trim().min(1).max(200),
  /** 기준값을 벗어난 금액(밴드 끝값 포함)이면 필수 — 서버가 단가표로 판정한다 */
  reasonCode: z9.enum(PRICING_REASON_CODES).optional(),
  /** reasonCode 가 OTHER 면 필수인 자유문 이유 */
  reasonNote: z9.string().trim().min(1).max(200).optional()
}).strict();
var formatPricingGround = (g) => `${PRICING_GROUND_PREFIX} [${[...g.questions].sort().join(",")}] ${g.reason}` + (g.reasonCode ? `
\uB2E8\uAC00 \uC0AC\uC720: ${g.reasonCode}${g.reasonNote ? ` \u2014 ${g.reasonNote}` : ""}` : "");
var approveQuestSchema = z9.object({
  difficulty: z9.enum([
    QuestDifficulty.COMMON,
    QuestDifficulty.UNCOMMON,
    QuestDifficulty.RARE,
    QuestDifficulty.EPIC,
    QuestDifficulty.LEGENDARY
  ]).optional(),
  /** 대표 수정 승인 — 단가 override (design §4.2·§4.3 [단가·레어리티 수정 후 승인]) */
  rewardPoints: z9.number().int().positive().optional(),
  /** 수정 사유 (선택) — QuestApprovalEvent.note 기록 */
  note: z9.string().trim().min(1).max(500).optional(),
  /** 승인하며 단가·등급을 바꾸면 필수 */
  pricingGround: pricingGroundSchema.optional()
}).strict().default({});
var createQuestAttachmentSchema = z9.object({
  type: z9.enum(["FILE", "URL", "IMAGE"]),
  path: z9.string(),
  metadata: z9.unknown().optional()
}).strict();
var createQuestBaseSchema = z9.object({
  title: z9.string(),
  description: z9.string().nullable().optional(),
  creatorId: z9.string(),
  rewardPoints: z9.number().min(0),
  deadline: Iso8601Schema.nullable().optional(),
  scope: z9.enum([QuestScope.TEAM, QuestScope.GLOBAL, QuestScope.SYSTEM]),
  targetType: z9.enum([QuestTargetType.USER, QuestTargetType.TEAM]),
  teamId: z9.string().nullable().optional(),
  scenarioId: z9.string().nullable().optional(),
  /**
   * 사내 제품 묶음(개발 퀘스트 창). 생성 컨텍스트가 정한다 — 제품 탭을 고른 채 만들면 그 제품이
   * 붙고, 그 외에는 null(미지정)이다. 시나리오와 같은 규칙이라 사용자가 고르는 입력이 아니다.
   */
  productId: z9.string().nullable().optional(),
  difficulty: z9.enum([
    QuestDifficulty.COMMON,
    QuestDifficulty.UNCOMMON,
    QuestDifficulty.RARE,
    QuestDifficulty.EPIC,
    QuestDifficulty.LEGENDARY
  ]).nullable().optional(),
  attachments: z9.array(createQuestAttachmentSchema).optional(),
  /**
   * true 면 ACTIVE 전이 시 QUEST_PUBLISHED 개별 fan-out 을 억제한다.
   * 시나리오 시드 퀘스트(배치 생성)의 알림 폭탄 방지용 — 시드 정책 확정 전 한시 플래그.
   * `scenarioId != null` 로 추론하지 않는다(팀장이 시나리오에 태깅해 손으로 만든 정상 퀘스트도
   * scenarioId 를 갖는다). 미지정(기본값)은 발송이라 기존 호출부는 무변경.
   */
  suppressPublishNotification: z9.boolean().optional(),
  /**
   * 출처 표식. 클라이언트가 보낼 수 있는 값은 시나리오 시드(`SCENARIO_SEED`) 하나뿐이다 —
   * 시드는 프론트 훅이 만들기 때문이다. 다른 출처(ADCODE_REQUEST · VA_REQUEST · ONBOARDING)는
   * 서버가 박으므로 여기서 거부된다. 미지정은 팀이 만든 퀘스트(origin null).
   */
  origin: z9.literal(SCENARIO_SEED_QUEST_ORIGIN).optional(),
  creationAttemptId: z9.uuid().optional(),
  creationSubmissionId: z9.uuid().optional(),
  /** 유단가(rewardPoints>0)면 필수 — 서버가 검사한다 */
  pricingGround: pricingGroundSchema.optional()
}).strict();
var validateQuestCreationIds = (value, ctx) => {
  if (value.creationAttemptId === void 0 === (value.creationSubmissionId === void 0)) {
    return;
  }
  ctx.addIssue({
    code: "custom",
    path: [value.creationAttemptId === void 0 ? "creationAttemptId" : "creationSubmissionId"],
    message: "creationAttemptId\uC640 creationSubmissionId\uB294 \uD568\uAED8 \uBCF4\uB0B4\uC57C \uD569\uB2C8\uB2E4"
  });
};
var createQuestSchema = createQuestBaseSchema.superRefine(validateQuestCreationIds);
var CHECKPOINT_UNIT_MAX = 8;
var createCheckpointSchema = z9.object({
  questId: z9.string(),
  title: z9.string(),
  description: z9.string().nullable().optional(),
  order: z9.number().min(0),
  rewardPoints: z9.number().min(0),
  deadline: z9.string().nullable().optional(),
  chainedToPrevious: z9.boolean().optional(),
  checkpointType: z9.string().optional(),
  targetValue: z9.number().nullable().optional(),
  metricKey: z9.string().optional(),
  unit: z9.string().trim().max(CHECKPOINT_UNIT_MAX).nullable().optional()
}).strict();
var updateQuestSchema = z9.object({
  title: z9.string().optional(),
  description: z9.string().nullable().optional(),
  deadline: Iso8601Schema.nullable().optional(),
  rewardPoints: z9.number().int().min(1).optional(),
  /** ACTIVE 무단가 승격 시 기존 체크포인트 전체의 보상을 같은 트랜잭션으로 배분한다. */
  checkpointRewards: z9.array(
    z9.object({
      id: z9.string().uuid(),
      rewardPoints: z9.number().int().min(0),
      order: z9.number().int().min(0).optional()
    }).strict()
  ).refine((items) => new Set(items.map((item) => item.id)).size === items.length, {
    message: "\uCCB4\uD06C\uD3EC\uC778\uD2B8 ID\uB294 \uC911\uBCF5\uB420 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"
  }).optional(),
  checkpointCreates: z9.array(
    createCheckpointSchema.omit({ questId: true }).extend({
      rewardPoints: z9.number().int().min(0),
      order: z9.number().int().min(0),
      deadline: Iso8601Schema.nullable().optional()
    })
  ).optional(),
  checkpointDeletes: z9.array(z9.string().uuid()).refine((ids) => new Set(ids).size === ids.length, {
    message: "\uC0AD\uC81C\uD560 \uCCB4\uD06C\uD3EC\uC778\uD2B8 ID\uB294 \uC911\uBCF5\uB420 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"
  }).optional(),
  /**
   * 사내 제품 재지정. 생성과 달리 **수정은 열어 둔다** — 제품 축이 들어오기 전에 만들어진
   * 개발팀 퀘스트가 전부 미지정이라, 소급해 붙일 경로가 없으면 제품 축이 영영 빈다.
   * null 은 미지정으로 되돌리기.
   */
  productId: z9.string().nullable().optional(),
  scope: z9.enum([QuestScope.TEAM, QuestScope.GLOBAL, QuestScope.SYSTEM]).optional(),
  difficulty: z9.enum([
    QuestDifficulty.COMMON,
    QuestDifficulty.UNCOMMON,
    QuestDifficulty.RARE,
    QuestDifficulty.EPIC,
    QuestDifficulty.LEGENDARY
  ]).optional(),
  // 관측 난이도(D_ob) — 완료 후 회고 판정. 발행 등급과 달리 재기록을 막지 않는다(관측이라
  // 다시 볼 수 있다). null 로 지운다. 가중치·배당에는 안 들어간다(성과 지표 §4.1).
  observedDifficulty: z9.enum([
    QuestDifficulty.COMMON,
    QuestDifficulty.UNCOMMON,
    QuestDifficulty.RARE,
    QuestDifficulty.EPIC,
    QuestDifficulty.LEGENDARY
  ]).nullable().optional(),
  attachments: z9.array(z9.union([z9.object({ id: z9.string() }).strict(), createQuestAttachmentSchema])).optional(),
  /**
   * 수정 사유 (선택) — 이 PATCH 가 남기는 QuestApprovalEvent 전부의 note 에 실린다.
   * 단가 정책이 우선순위 3문 중 무엇으로 값을 붙였는지 남기라고 요구하는데
   * (quest-pricing-policy §2) 그 자리가 승인 경로에만 있어 PATCH 승격은 사유 없이
   * 기록됐다. approveQuestSchema.note 와 같은 제약.
   */
  note: z9.string().trim().min(1).max(500).optional(),
  /** 단가·등급을 바꾸는 수정이면 필수 — 서버가 검사한다 */
  pricingGround: pricingGroundSchema.optional()
}).strict();
var updateCheckpointSchema = z9.object({
  title: z9.string().optional(),
  description: z9.string().nullable().optional(),
  rewardPoints: z9.number().min(0).optional(),
  order: z9.number().optional(),
  deadline: z9.string().nullable().optional(),
  chainedToPrevious: z9.boolean().optional(),
  checkpointType: z9.string().optional(),
  targetValue: z9.number().nullable().optional(),
  metricKey: z9.string().nullable().optional(),
  unit: z9.string().trim().max(CHECKPOINT_UNIT_MAX).nullable().optional()
}).strict();
var acceptQuestSchema = z9.object({
  userId: z9.string()
}).strict();
var assignCheckpointSchema = z9.object({
  userId: z9.string()
}).strict();
var rejectCheckpointSchema = z9.object({
  reason: z9.string().min(1),
  rejectReasonKind: z9.enum([
    CheckpointRejectReasonKind.SPEC,
    CheckpointRejectReasonKind.EXECUTION
  ])
}).strict();
var approveCheckpointSchema = z9.object({
  reviewedRevision: z9.number().int().min(0).optional(),
  guidelineDraft: z9.object({
    url: z9.string().trim().max(2048).regex(/^https?:\/\/\S+$/i, "http(s) \uC8FC\uC18C\uB9CC \uC62C\uB9B4 \uC218 \uC788\uC2B5\uB2C8\uB2E4"),
    source: z9.enum(["candidate", "manual"])
  }).strict().nullable().optional()
}).strict();
var reorderCheckpointsSchema = z9.object({
  questId: z9.string(),
  orderedIds: z9.array(z9.string())
}).strict();
var challengeCandidateSchema = z9.object({
  checkpointId: z9.string(),
  questId: z9.string(),
  title: z9.string(),
  questName: z9.string(),
  remaining: z9.number(),
  target: z9.number(),
  scenarioId: z9.string().nullable(),
  scenarioName: z9.string().nullable(),
  deadline: Iso8601Schema.nullable(),
  lastResponseAt: Iso8601Schema.nullable()
}).strict();
var BULK_ARCHIVE_MAX_IDS = 50;
var bulkArchiveQuestsSchema = z9.object({
  questIds: z9.array(z9.uuid()).min(1).max(BULK_ARCHIVE_MAX_IDS)
}).strict();
var bulkArchiveQuestsResponseSchema = z9.object({
  succeeded: z9.array(z9.uuid()),
  failed: z9.array(
    z9.object({
      questId: z9.uuid(),
      message: z9.string()
    }).strict()
  )
}).strict();
var distributionKindSchema = z9.enum(DISTRIBUTION_KINDS);
var questDistributionKindSchema = z9.enum([
  "auto",
  "fallback_proof_single",
  "no_op"
]);

// src/dto/bug-report.ts
var attachmentSchema = z10.object({
  path: z10.string(),
  originalName: z10.string()
}).strict();
var createBugReportSchema = z10.object({
  content: z10.string().min(5),
  pageContext: z10.string(),
  /** 기존 클라이언트는 type 없이 보낸다 — default 로 하위호환 유지. */
  type: z10.enum(["BUG", "FEATURE"]).default("BUG"),
  attachments: z10.array(attachmentSchema).optional()
}).strict();
var createEventSchema = z10.object({
  content: z10.string().min(1)
}).strict();
var editBugReportSchema = z10.object({
  content: z10.string().optional(),
  attachments: z10.array(attachmentSchema).optional()
}).strict();
var updateBugReportSchema = z10.object({
  status: z10.string().optional(),
  assigneeId: z10.uuid().nullable().optional(),
  resolution: z10.string().optional()
}).strict();
var convertBugReportToQuestSchema = createQuestBaseSchema.omit({
  creatorId: true,
  teamId: true
}).superRefine(validateQuestCreationIds);

// src/dto/campaign-adcode.ts
import { z as z11 } from "zod";
var issueCampaignAdcodeSchema = z11.object({
  adCode: z11.string().regex(CAMPAIGN_AD_CODE_PATTERN)
}).strict();
var updateBoostingStageSchema = z11.object({
  toStage: z11.enum([
    BoostingStage.ISSUED,
    BoostingStage.BOOSTING,
    BoostingStage.STOP_REQUESTED,
    BoostingStage.STOPPED
  ]),
  reason: z11.string().max(500).optional()
}).strict().superRefine((value, ctx) => {
  if (value.toStage !== BoostingStage.STOP_REQUESTED) {
    return;
  }
  if (!value.reason || !value.reason.trim()) {
    ctx.addIssue({
      code: z11.ZodIssueCode.custom,
      path: ["reason"],
      message: "reason is required when requesting a stop"
    });
  }
});
var metricCount = z11.number().int().min(0).nullable();
var updateAdcodeMetricsSchema = z11.object({
  views: metricCount.optional(),
  likes: metricCount.optional(),
  comments: metricCount.optional(),
  shares: metricCount.optional(),
  saves: metricCount.optional()
}).strict().refine((value) => Object.keys(value).length > 0, {
  message: "at least one metric is required"
});
var BOOSTING_STAGE_VALUES = [
  BoostingStage.ISSUED,
  BoostingStage.BOOSTING,
  BoostingStage.STOP_REQUESTED,
  BoostingStage.STOPPED
];
var MAPPING_WARNING_CODES = Object.values(MappingWarningCode);
var mappingWarningSchema = z11.object({
  code: z11.enum(MAPPING_WARNING_CODES),
  label: z11.string().max(200),
  key: z11.string().max(100).nullable()
}).strict();
var sheetCount = z11.number().int().min(0).nullable();
var campaignAdcodeDryRunRowSchema = z11.object({
  adcode: z11.object({
    adCode: z11.string().regex(CAMPAIGN_AD_CODE_PATTERN).nullable(),
    boostingStage: z11.enum(BOOSTING_STAGE_VALUES).nullable(),
    views: sheetCount,
    likes: sheetCount,
    comments: sheetCount,
    shares: sheetCount,
    saves: sheetCount
  }).strict(),
  rosterLookup: z11.object({
    videoUrl: z11.string().max(2048).nullable(),
    creatorHandleCandidate: z11.string().max(200).nullable(),
    canCrossCheckCreator: z11.boolean()
  }).strict(),
  warnings: z11.array(mappingWarningSchema).max(100)
}).strict();
var CAMPAIGN_ADCODE_DRY_RUN_MAX_ROWS = 2e3;
var campaignAdcodeDryRunSchema = z11.object({
  rows: z11.array(campaignAdcodeDryRunRowSchema).min(1).max(CAMPAIGN_ADCODE_DRY_RUN_MAX_ROWS)
}).strict();
var CampaignAdcodeDryRunOutcome = {
  CREATED: "CREATED",
  UPDATED: "UPDATED",
  SKIPPED: "SKIPPED",
  CONFLICT: "CONFLICT",
  UNRESOLVED: "UNRESOLVED"
};
var CampaignAdcodeDryRunConflictCode = {
  /** adCode 는 있는데 다른 영상 행에 붙어 있다 — 귀속을 옮기지 않는다 (D-7 거울상). */
  AD_CODE_BOUND_TO_OTHER_VIDEO: "AD_CODE_BOUND_TO_OTHER_VIDEO",
  /** 묶인 영상 행에 이미 다른 adCode 가 있다 — 영상 per 1 (D-7). */
  VIDEO_HAS_OTHER_AD_CODE: "VIDEO_HAS_OTHER_AD_CODE",
  /** 같은 배치 안에 같은 adCode 가 앞서 나왔다 — 실행했다면 두 번째가 409 였다. */
  DUPLICATE_AD_CODE_IN_BATCH: "DUPLICATE_AD_CODE_IN_BATCH"
};
var CampaignAdcodeDryRunUnresolvedCode = {
  /** 바인딩 키(영상 링크) 자체가 없다. */
  VIDEO_URL_MISSING: "VIDEO_URL_MISSING",
  /** `(scenarioId, 정규화 videoUrl)` 후보 0건. */
  ROSTER_ENTRY_NOT_FOUND: "ROSTER_ENTRY_NOT_FOUND",
  /** 후보 2건 이상 — 정규화 후 같은 링크가 둘이면 고르지 않는다. */
  ROSTER_ENTRY_AMBIGUOUS: "ROSTER_ENTRY_AMBIGUOUS",
  /** 링크는 맞는데 시트의 핸들이 로스터 스냅샷과 다르다 (D-5 교차검증). */
  CREATOR_HANDLE_MISMATCH: "CREATOR_HANDLE_MISMATCH",
  /** 묶였지만 그 행의 크리에이터가 미해소라 애드코드를 연결할 수 없다 (§7-1). */
  CREATOR_UNRESOLVED: "CREATOR_UNRESOLVED"
};
var CAMPAIGN_ADCODE_DRY_RUN_PREVIEW_LIMIT = 20;
var campaignAdcodeImportSchema = z11.object({
  rows: z11.array(campaignAdcodeDryRunRowSchema).min(1).max(CAMPAIGN_ADCODE_DRY_RUN_MAX_ROWS)
}).strict();
var CampaignAdcodeImportOutcome = {
  CREATED: "CREATED",
  UPDATED: "UPDATED",
  SKIPPED: "SKIPPED",
  FAILED: "FAILED"
};

// src/dto/campaign-roster/communication.ts
import { z as z12 } from "zod";
var ROSTER_MANUAL_COMMUNICATION_CHANNELS = ["KAKAO", "PHONE", "OTHER"];
var rosterManualCommunicationChannelSchema = z12.enum(ROSTER_MANUAL_COMMUNICATION_CHANNELS);
var ROSTER_MANUAL_COMMUNICATION_EVENT_TYPES = [
  "MANUAL_CONTACT_REQUESTED",
  "EXTERNAL_RESPONSE_RECORDED",
  "RESPONSIBILITY_HANDED_OFF",
  "COMMUNICATION_RESOLVED"
];
var rosterManualCommunicationEventTypeSchema = z12.enum(
  ROSTER_MANUAL_COMMUNICATION_EVENT_TYPES
);
var ROSTER_MANUAL_COMMUNICATION_TARGET_ROLES = ["CREATOR", "ADVERTISER"];
var rosterManualCommunicationTargetRoleSchema = z12.enum(
  ROSTER_MANUAL_COMMUNICATION_TARGET_ROLES
);
var createCampaignRosterManualCommunicationSchema = z12.object({
  eventType: rosterManualCommunicationEventTypeSchema.optional(),
  channel: rosterManualCommunicationChannelSchema.nullable().optional(),
  targetRole: rosterManualCommunicationTargetRoleSchema.nullable().optional(),
  requestEventId: z12.string().uuid().nullable().optional(),
  fromResponsibleId: z12.string().uuid().nullable().optional(),
  toResponsibleId: z12.string().uuid().nullable().optional(),
  occurredAt: z12.coerce.date().refine((value) => value.getTime() <= Date.now(), {
    message: "\uC18C\uD1B5 \uC2DC\uAC01\uC740 \uBBF8\uB798\uC77C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
  }),
  note: z12.string().trim().max(2e3).nullable().optional()
}).strict().superRefine((value, context) => {
  const eventType = value.eventType ?? "MANUAL_CONTACT_REQUESTED";
  const requestEventId = value.requestEventId ?? null;
  const fromResponsibleId = value.fromResponsibleId ?? null;
  const toResponsibleId = value.toResponsibleId ?? null;
  if (eventType === "MANUAL_CONTACT_REQUESTED") {
    if (value.channel === void 0 || value.channel === null) {
      context.addIssue({
        code: z12.ZodIssueCode.custom,
        path: ["channel"],
        message: "\uC678\uBD80 \uD655\uC778 \uC694\uCCAD\uC5D0\uB294 \uCC44\uB110\uC774 \uD544\uC694\uD569\uB2C8\uB2E4."
      });
    }
    if (requestEventId !== null || fromResponsibleId !== null || toResponsibleId !== null) {
      context.addIssue({
        code: z12.ZodIssueCode.custom,
        message: "\uC678\uBD80 \uD655\uC778 \uC694\uCCAD\uC5D0\uB294 \uC120\uD589 \uC694\uCCAD\uC774\uB098 \uC774\uAD00 \uCC45\uC784\uC790\uB97C \uC9C0\uC815\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
      });
    }
    return;
  }
  if (requestEventId === null) {
    context.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["requestEventId"],
      message: "\uC751\uB2F5\xB7\uC774\uAD00\xB7\uC885\uB8CC \uC0AC\uAC74\uC5D0\uB294 \uC120\uD589 \uC694\uCCAD\uC774 \uD544\uC694\uD569\uB2C8\uB2E4."
    });
  }
  if (eventType === "RESPONSIBILITY_HANDED_OFF") {
    if (fromResponsibleId === null || toResponsibleId === null) {
      context.addIssue({
        code: z12.ZodIssueCode.custom,
        message: "\uB0B4\uBD80 \uC774\uAD00\uC5D0\uB294 \uC774\uC804 \uCC45\uC784\uC790\uC640 \uB2E4\uC74C \uCC45\uC784\uC790\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4."
      });
    } else if (fromResponsibleId === toResponsibleId) {
      context.addIssue({
        code: z12.ZodIssueCode.custom,
        path: ["toResponsibleId"],
        message: "\uB2E4\uC74C \uCC45\uC784\uC790\uB294 \uC774\uC804 \uCC45\uC784\uC790\uC640 \uB2EC\uB77C\uC57C \uD569\uB2C8\uB2E4."
      });
    }
  } else if (fromResponsibleId !== null || toResponsibleId !== null) {
    context.addIssue({
      code: z12.ZodIssueCode.custom,
      message: "\uCC45\uC784\uC790 \uD544\uB4DC\uB294 \uB0B4\uBD80 \uC774\uAD00 \uC0AC\uAC74\uC5D0\uC11C\uB9CC \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."
    });
  }
  if (value.channel !== void 0 && value.channel !== null) {
    context.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["channel"],
      message: "\uD6C4\uC18D \uC0AC\uAC74\uC758 \uCC44\uB110\uC740 \uC120\uD589 \uC694\uCCAD\uC5D0\uC11C \uC0C1\uC18D\uD569\uB2C8\uB2E4."
    });
  }
  if (value.targetRole !== void 0 && value.targetRole !== null) {
    context.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["targetRole"],
      message: "\uD6C4\uC18D \uC0AC\uAC74\uC758 \uC678\uBD80 \uB300\uC0C1\uC740 \uC120\uD589 \uC694\uCCAD\uC5D0\uC11C \uC0C1\uC18D\uD569\uB2C8\uB2E4."
    });
  }
}).transform((value) => {
  const eventType = value.eventType ?? "MANUAL_CONTACT_REQUESTED";
  return {
    eventType,
    channel: value.channel ?? null,
    targetRole: eventType === "MANUAL_CONTACT_REQUESTED" ? value.targetRole ?? "CREATOR" : null,
    requestEventId: value.requestEventId ?? null,
    fromResponsibleId: value.fromResponsibleId ?? null,
    toResponsibleId: value.toResponsibleId ?? null,
    occurredAt: value.occurredAt,
    note: value.note ?? null
  };
});
var KST_DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
var rosterManualCommunicationStatsQuerySchema = z12.object({
  teamId: z12.string().uuid().optional(),
  from: z12.string().regex(KST_DATE_PATTERN, "from \uC740 YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").optional(),
  to: z12.string().regex(KST_DATE_PATTERN, "to \uB294 YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").optional()
}).strict().refine((value) => !value.from || !value.to || value.from <= value.to, {
  message: "\uC2DC\uC791\uC77C\uC740 \uC885\uB8CC\uC77C\uBCF4\uB2E4 \uB2A6\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
  path: ["from"]
});

// src/dto/campaign-roster/entry-write.ts
import { z as z13 } from "zod";
var writableRosterStatus = z13.enum([
  RosterStatus.LISTED,
  RosterStatus.PRICE_NEGOTIATION,
  RosterStatus.GUIDELINE_SENT,
  RosterStatus.REVISION_REQUESTED,
  RosterStatus.UPLOAD_REQUESTED
]);
var rosterPriceFields = {
  negotiatedAmount: z13.string().regex(SETTLEMENT_AMOUNT_PATTERN, "negotiatedAmount must be a decimal(14,2) string").nullable().optional(),
  negotiatedCurrency: z13.string().regex(SETTLEMENT_CURRENCY_PATTERN, "negotiatedCurrency must be an ISO 4217 code").nullable().optional()
};
function refineNegotiatedPricePair(value, ctx) {
  const hasAmount = value.negotiatedAmount != null;
  const hasCurrency = value.negotiatedCurrency != null;
  if (hasAmount === hasCurrency) return;
  ctx.addIssue({
    code: "custom",
    path: [hasAmount ? "negotiatedCurrency" : "negotiatedAmount"],
    message: "negotiatedAmount and negotiatedCurrency must be set together"
  });
}
var rosterInternalProposalFields = {
  internalProposalAmount: z13.string().regex(SETTLEMENT_AMOUNT_PATTERN, "internalProposalAmount must be a decimal(14,2) string").nullable().optional(),
  internalProposalCurrency: z13.string().regex(SETTLEMENT_CURRENCY_PATTERN, "internalProposalCurrency must be an ISO 4217 code").nullable().optional()
};
function refineInternalProposalPair(value, ctx) {
  const amount = value.internalProposalAmount;
  const currency = value.internalProposalCurrency;
  if (amount === void 0 && currency === void 0) return;
  const hasAmount = amount != null;
  const hasCurrency = currency != null;
  if (amount !== void 0 && currency !== void 0 && hasAmount === hasCurrency) return;
  ctx.addIssue({
    code: "custom",
    path: [hasAmount ? "internalProposalCurrency" : "internalProposalAmount"],
    message: "internalProposalAmount and internalProposalCurrency must be set together"
  });
}
var isoDate = z13.string().regex(/^\d{4}-\d{2}-\d{2}$/, "uploadDate must be YYYY-MM-DD").nullable().optional();
var optionalText = (max) => z13.string().max(max).nullable().optional();
var optionalEmail = () => creatorEmailSchema.nullable().optional();
var createCampaignRosterEntrySchema = z13.object({
  rosterStatus: writableRosterStatus.nullable().optional(),
  // 캠페인 시점 불변 스냅샷. 정정은 update 가 아니라 revision append 로만 (QE-3b).
  creatorNicknameSnapshot: z13.string().min(1).max(200),
  creatorHandleSnapshot: z13.string().min(1).max(200),
  tiktokUrlSnapshot: z13.string().min(1).max(2048),
  followersSnapshot: z13.number().int().min(0).nullable().optional(),
  // 느슨한 외부 정본 참조 — 외부 DB 직접 FK 금지 (D-2).
  sourceSystem: optionalText(200),
  externalCreatorId: optionalText(200),
  uploadDate: isoDate,
  // nullable — videoUrl 없는 섭외 시점 행 허용 (QD-3 M-10 확정).
  videoUrl: optionalText(2048),
  /**
   * 이 영상 행의 조회수 목표 (goal-placement §3-2). 합계 목표는 Scenario 에 따로 선다.
   *
   * `min(1)` — 목표 없음은 `0` 이 아니라 미설정(생략·null)이다. 섭외 시점 행에는 목표가
   * 없고 그것이 정상 경로다. 배분 규칙은 팀마다 정반대라(2팀 팔로워 구간별 / 4팀 무관)
   * 저장하지 않고 규칙이 푼 결과만 담는다.
   */
  targetViews: z13.number().int().min(1).nullable().optional(),
  cleanFile: z13.boolean().nullable().optional(),
  cleanFileUrl: optionalText(2048),
  issueNote: optionalText(2e3),
  /**
   * 제품 칸에서 갈라낸 두 칸 (QD-3 M-1 · 회신 §7-7 · N-10). 비고를 대신하지 않는다 —
   * 비고는 팀장 피드백·정산·소통을 함께 담는 다목적 칸으로 남고 이 둘은 그 옆의 별개
   * 사실이다. 셋을 한 칸에 접으면 되돌릴 수 없다는 것이 갈라 둔 이유다.
   *
   * `guideline` 의 기본값은 캠페인이 든다(`Scenario.guidelineUrl`·`guidelineNote`,
   * 2차 회신 §1). 행의 이 칸은 크리에이터마다 갈릴 때만 적는 예외값이라 비는 것이 정상이다.
   *
   * 둘 다 비고와 같은 층의 자유 텍스트다 — 코드값을 넣지 말 것 (`issueNote` 선례).
   */
  guideline: optionalText(2e3),
  accountNote: optionalText(2e3),
  // 국내형(DOMESTIC) 전용 — campaignType 분기 검증은 서비스 레이어.
  kakaoChannelNo: optionalText(200),
  kakaoChannelName: optionalText(200),
  // 글로벌(GLOBAL) 전용 — 같은 창구 슬롯의 반대쪽. 분기 검증도 같은 자리에서 본다.
  // 자유 텍스트지만 **라벨**이다: 주소는 옆 칸(`creatorEmail`)이 따로 든다. 둘은 다른
  // 층이라 서로를 대신하지 않는다 — 라벨은 사람이 "어느 왕복인지" 알아보려고 적는 표시고,
  // 주소는 발신이 읽는 값이다 (console-ia §1, PR #2159).
  mailThreadLabel: optionalText(200),
  // 글로벌(GLOBAL) 전용 — 크리에이터 주소. 2026-08-28(#2124)에 이메일이 §7-5 PII 그물에서
  // 빠지면서 행에 담을 수 있게 됐다. **시트 임포트는 계속 버린다** — 이 칸이 열리는 것은
  // AXIOM 입력 표면과 설문 응답 경로뿐이다 (be6af75c).
  creatorEmail: optionalEmail(),
  // 단가 — 국내/글로벌 공용. 통화가 값과 **같은 층**에 있다 (아래 rosterPriceFields 주석).
  ...rosterPriceFields,
  // 국내/글로벌 공용 — 별칭 흡수는 shared normalizeRosterLabel (§8).
  crossPlatform: optionalText(2e3),
  // 캠페인 홍보 대상물(ScenarioProduct) 해소 입력. 표시 이력은 연결 실패해도 보존한다.
  productVariantSnapshot: optionalText(500)
}).strict().superRefine(refineNegotiatedPricePair);
var ROSTER_LOCKABLE_FIELDS = [
  "rosterStatus",
  "uploadDate",
  "videoUrl",
  "cleanFile",
  "cleanFileUrl",
  "issueNote",
  "guideline",
  "accountNote",
  "targetViews",
  "kakaoChannelNo",
  "kakaoChannelName",
  "mailThreadLabel",
  // `creatorEmail` 은 2026-09-07 판정으로 여기서 빠졌다 (퀘스트 1e647251). 칸 잠금은 "로스터
  // 행의 그 칸이 화면이 읽은 값 그대로인가" 를 묻는데 주소의 정본이 관계 행으로 옮겨져 행에
  // 그 값이 없다 — 남겨 두면 관계 행에서 읽은 값을 행 컬럼과 대조해 두 번째 수정마다 없는
  // 충돌이 난다. 같은 사람의 주소를 두 캠페인에서 동시에 고치는 것이 이 값의 실제 경합이라
  // 행 단위 잠금으로는 애초에 못 덮는다.
  "crossPlatform",
  "productVariantSnapshot",
  "dropKind",
  "contentReview",
  "internalProposalAmount",
  "internalProposalCurrency"
];
var CAMPAIGN_ROSTER_SENSITIVE_KEYS = ["kakaoChannelName"];
var rosterExpectedCellsSchema = z13.object(
  Object.fromEntries(
    ROSTER_LOCKABLE_FIELDS.map((field) => [
      field,
      z13.union([z13.string(), z13.number(), z13.boolean(), z13.null()]).optional()
    ])
  )
).strict();
var updateCampaignRosterEntrySchema = z13.object({
  rosterStatus: writableRosterStatus.optional(),
  uploadDate: isoDate,
  videoUrl: optionalText(2048),
  cleanFile: z13.boolean().nullable().optional(),
  cleanFileUrl: optionalText(2048),
  issueNote: optionalText(2e3),
  /** 비고와 같은 층이라 행이 사는 내내 고쳐 적는다 (생성 스키마 주석 참조). */
  guideline: optionalText(2e3),
  accountNote: optionalText(2e3),
  /** 영상 게시 이후에도 적을 수 있다 — 시트 이관분은 목표가 나중에 온다. null = 해제. */
  targetViews: z13.number().int().min(1).nullable().optional(),
  kakaoChannelNo: optionalText(200),
  kakaoChannelName: optionalText(200),
  mailThreadLabel: optionalText(200),
  creatorEmail: optionalEmail(),
  crossPlatform: optionalText(2e3),
  productVariantSnapshot: optionalText(500),
  /*
    이탈(`dropKind`)은 여기 없다 — 종료는 값 수정이 아니라 **사건**이라 전용 경로
    (`createCampaignRosterDropSchema` · `createCampaignRosterDropRestoreSchema`)로만 받는다
    (섭외 IA §5, 퀘스트 5b8ba30f). 미등록 판정·은닉이 PATCH 에 안 열린 것과 같은 규율이다.
    PATCH 로 열면 주체 필수·사유·복구 권한이 값 하나로 접힌다.
  */
  /**
   * 검토 표시 — 사람이 고르는 두 값과 해제(`null`)만 받는다 (섭외 IA §4-1).
   *
   * `미검토`·`검토 지남` 은 저장값이 아니라 단계에서 파생하는 표시다. 쓰기로 받으면 기존 행을
   * 백필하지 않기로 한 결정이 요청 하나로 조용히 뒤집힌다.
   */
  contentReview: z13.enum(ROSTER_CONTENT_REVIEWS).nullable().optional(),
  ...rosterInternalProposalFields,
  /**
   * 화면이 **읽은 시점의 칸 값**. 서버는 이 값이 아직 저장돼 있을 때만 그 칸을 고친다
   * (조건부 갱신), 아니면 409 `ROSTER_CELL_CONFLICT` 다.
   *
   * **행이 아니라 칸이 단위인 이유**가 이 스키마의 내용이다. 화면은 남의 수정을 밀어서
   * 받지 않으므로(구독·폴링 없음, 갱신은 자기 쓰기 뒤 무효화뿐) 행 단위로 잠그면 옆자리가
   * 그 행의 **아무 칸이나** 건드린 순간 내 사본이 낡아, 겹치지도 않은 칸을 고치는데 막힌다.
   * 팀원 전원이 하루 종일 열어 두는 표에서 그것은 상시 오발화다. 근거는
   * `docs/archive/roster-conflict-409-history/cp1-investigation-2026-09-03.md` §2.
   *
   * 비워 보내면 잠금이 안 선다 — 잠금은 화면이 읽은 값을 실을 때만 서고, 임포트·스크립트
   * 처럼 읽은 값이 없는 경로를 막지 않는다.
   */
  expected: rosterExpectedCellsSchema.optional(),
  /**
   * 이 전이의 몫이 갈 사람 — 대신 옮길 때만 고른다 (퀘스트 3c015add, `creditedActorOf`).
   *
   * 안 실으면 입력자 자신이라 평소 입력은 지금과 같다. **단계와 짝으로만 온다**: 단계가
   * 안 움직이면 적을 전이가 없어, 받아 두면 화면은 보냈는데 서버는 아무것도 안 쓰는
   * 경로가 생긴다 (아래 refine 이 막는다).
   *
   * 값의 경계(참여팀 안인가)는 서버가 본다 — 후보 목록이 그 경계로 내려가지만 요청은
   * 그 목록을 안 거치고도 올 수 있다.
   */
  creditedToId: z13.uuid().optional()
}).strict().refine((value) => Object.keys(value).length > 0, {
  message: "at least one field is required"
}).refine((value) => value.creditedToId === void 0 || value.rosterStatus !== void 0, {
  message: "creditedToId requires rosterStatus"
}).superRefine(refineInternalProposalPair).refine(
  (value) => value.expected === void 0 || Object.keys(value.expected).every((key) => key in value),
  {
    message: "expected must only name fields that are being written"
  }
);

// src/dto/campaign-roster/realtime-lock.ts
var ROSTER_LOCK_ACTIONS = ["LOCK", "UNLOCK"];

// src/dto/campaign-roster/revisions.ts
import { z as z14 } from "zod";
var confirmCreatorResolutionSchema = z14.object({
  /**
   * 후보 승인 경로 — 화면이 실어 보낸 후보의 번호를 그대로 되돌려 준다. 상한 200 은 컬럼 계약과 같다.
   *
   * `trim().min(1)` — 공백만 실은 확정은 미해소를 미해소로 덮으면서 판정축만 non-null 로
   * 만들어, 애드코드 발급 게이트(`externalCreatorId` non-null)를 빈 값으로 여는 구멍이 된다.
   */
  externalCreatorId: z14.string().trim().min(1).max(200).optional(),
  /** 손입력 경로 — 크리에이터 틱톡 ID. `@` 는 서버가 벗기므로 붙여 보내도 된다. */
  handle: z14.string().trim().min(1).max(200).optional(),
  sourceSystem: z14.string().trim().min(1).max(200).optional()
}).strict().refine((value) => value.externalCreatorId === void 0 !== (value.handle === void 0), {
  // 둘 다 실으면 어느 쪽이 사람의 확인인지가 사라진다 — 번호와 핸들이 다른 사람을 가리킬 때
  // 서버가 조용히 하나를 고르게 된다. 하나만 받고 나머지는 그 값에서 파생시킨다.
  message: "externalCreatorId \uC640 handle \uC911 \uC815\uD655\uD788 \uD558\uB098\uB9CC \uC2E4\uC5B4\uC57C \uD569\uB2C8\uB2E4"
});
var relinkRosterCreatorSchema = z14.object({
  handle: z14.string().trim().min(1).max(200)
}).strict();
var createCampaignRosterSnapshotRevisionSchema = z14.object({
  /** 왜 고쳤는지 — 정정 이력이 존재하는 이유라 필수다. */
  reason: z14.string().trim().min(1).max(2e3),
  creatorHandleSnapshot: z14.string().min(1).max(200).optional(),
  creatorNicknameSnapshot: z14.string().min(1).max(200).optional(),
  tiktokUrlSnapshot: z14.string().min(1).max(2048).optional(),
  followersSnapshot: z14.number().int().min(0).optional()
}).strict().refine(
  (value) => value.creatorHandleSnapshot !== void 0 || value.creatorNicknameSnapshot !== void 0 || value.tiktokUrlSnapshot !== void 0 || value.followersSnapshot !== void 0,
  { message: "at least one snapshot field is required" }
);
var createCampaignRosterPriceRevisionSchema = z14.object({
  amount: z14.string().regex(SETTLEMENT_AMOUNT_PATTERN, "amount must be a decimal(14,2) string"),
  currency: z14.string().regex(SETTLEMENT_CURRENCY_PATTERN, "currency must be an ISO 4217 code"),
  /** 누가 부른 값인가 — 기록한 사람(`actorId`)과 다른 축이다. */
  proposedBy: z14.enum(ROSTER_PRICE_PROPOSERS),
  /** 왜 움직였는지. 자유 텍스트라 코드값을 넣지 말 것 (`issueNote` 선례). */
  note: z14.string().trim().max(2e3).optional()
}).strict();
var createCampaignRosterScheduleRevisionSchema = rosterScheduleConfirmInputSchema.omit({
  actorId: true
});
var createConditionAgreementSchema = z14.object({
  occurredAt: z14.coerce.date().refine((value) => value.getTime() <= Date.now(), {
    message: "\uD569\uC758 \uC2DC\uAC01\uC740 \uBBF8\uB798\uC77C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
  }),
  note: z14.string().trim().min(1).max(500),
  /**
   * 근거가 메일 한 통일 때 — 아카이브 계정의 Gmail 메시지 id. 대화 좌표가 아닌 것은 대화가
   * (캠페인 × 관계) 단위라 한 행에 1:1 로 안 붙기 때문이다.
   */
  messageId: z14.string().trim().min(1).max(998).nullable().default(null),
  /** 근거가 메일 밖일 때 — 수동 소통 기록 한 줄. */
  manualCommunicationId: z14.string().uuid().nullable().default(null),
  /** 업로드일이 함께 정해졌으면 그 날짜. 없으면 약속 줄을 세우지 않는다. */
  plannedUploadDate: z14.iso.date().nullable().default(null),
  /** 날짜가 올 때만 본다 — 누가 부른 값인가. */
  proposedBy: z14.enum(ROSTER_PRICE_PROPOSERS).optional()
}).strict().superRefine((value, context) => {
  if (value.messageId === null && value.manualCommunicationId === null)
    context.addIssue({
      code: z14.ZodIssueCode.custom,
      path: ["messageId"],
      message: "\uD569\uC758 \uADFC\uAC70\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4 \u2014 \uD68C\uC2E0 \uBA54\uC77C\uC774\uB098 \uC678\uBD80 \uC18C\uD1B5 \uAE30\uB85D\uC744 \uACE0\uB974\uC138\uC694."
    });
  if (value.plannedUploadDate !== null && value.proposedBy === void 0)
    context.addIssue({
      code: z14.ZodIssueCode.custom,
      path: ["proposedBy"],
      message: "\uC57D\uC18D \uB0A0\uC9DC\uB97C \uD568\uAED8 \uC801\uC744 \uB54C\uB294 \uB204\uAC00 \uBD80\uB978 \uAC12\uC778\uC9C0 \uD544\uC694\uD569\uB2C8\uB2E4."
    });
});
var createCampaignRosterUploadTakedownSchema = z14.object({
  /** 누가 물었는가 — 기록한 사람(`actorId`)과 다른 축이다. */
  requestedBy: z14.enum(ROSTER_TAKEDOWN_REQUESTERS),
  /** 왜 내렸는지. 자유 텍스트라 코드값을 넣지 말 것 (`issueNote` 선례). */
  note: z14.string().trim().max(2e3).optional()
}).strict();
var createCampaignRosterDropSchema = z14.object({
  dropKind: z14.enum(ROSTER_MANUAL_DROP_KINDS),
  /** 상대가 말한 사유 — 크리에이터·광고주가 말한 것. 광고주 기각은 보통 비어 있는 것이 정상이다. */
  reason: z14.string().trim().max(500).optional(),
  /** 내부 메모 — 우리 해석. 사유와 칸을 가른다 (IA §6-2). */
  note: z14.string().trim().max(2e3).optional()
}).strict();
var createCampaignRosterDropRestoreSchema = z14.object({
  note: z14.string().trim().max(2e3).optional()
}).strict();
var reassignCampaignRosterAssigneeSchema = z14.object({
  assigneeId: z14.string().uuid()
}).strict();
var campaignRosterProfileLinkResolveSchema = z14.object({ urls: z14.array(z14.string().url().max(2048)).min(1).max(100) }).strict();

// src/dto/campaign-roster/entry-response.ts
import { z as z15 } from "zod";
var CampaignRosterWarningCode = {
  /** 외부 정본을 찾지 못했다 — 외부 ID 를 줬는데 사본이 없거나, 핸들 후보가 0건. */
  CREATOR_UNRESOLVED: "CREATOR_UNRESOLVED",
  /** 핸들 후보가 2건 이상이다. 자동 병합하지 않는다 (§7-1). */
  CREATOR_AMBIGUOUS: "CREATOR_AMBIGUOUS",
  /**
   * 핸들 후보가 정확히 1건인데 번호가 빈 행이다. 새 행은 해소가 바로 확정하므로(2026-09-19)
   * 이 판정은 그 전에 선 행이나, 행이 선 뒤에 후보가 한 명으로 찾아진 행에만 남는다.
   */
  CREATOR_MANUAL_CONFIRM_REQUIRED: "CREATOR_MANUAL_CONFIRM_REQUIRED",
  /** 같은 캠페인에서 동명 홍보 대상물을 찾지 못했다. 자동 생성하지 않는다. */
  PRODUCT_UNRESOLVED: "PRODUCT_UNRESOLVED",
  /**
   * 카채널 닉네임에서 닉네임이 아닌 조각(괄호 안·접두·꼬리 메모)을 버렸다. 실명일 수 있어
   * 값은 응답에도 싣지 않는다 (§7-5, 퀘스트 8639d3ee).
   */
  KAKAO_CHANNEL_NAME_DISCARDED: "KAKAO_CHANNEL_NAME_DISCARDED",
  /** 카채널 닉네임이 스냅샷 닉네임·핸들과 맞지 않는다 — 실명일 수 있어 사람이 확인한다. */
  KAKAO_CHANNEL_NAME_UNVERIFIED: "KAKAO_CHANNEL_NAME_UNVERIFIED",
  /**
   * 시트 임포트가 페이팔을 버렸다 — 개인정보 열람 권한(`creator:pii:view`)이 없는 사람이 돌렸다.
   * 페이팔은 사람 단위 한 벌이라 다른 캠페인 정산도 읽는다. 행은 그대로 들어간다 (퀘스트 5fc3111f).
   */
  PAYOUT_DISCARDED: "PAYOUT_DISCARDED"
};
var CREATOR_COLLECTION_STATES = ["queued", "found", "absent", "failed"];
var requestRosterCreatorCollectionSchema = z15.object({
  handle: z15.string().trim().min(1).max(100)
}).strict();

// src/dto/campaign-roster/export-files.ts
import { z as z16 } from "zod";
var ROSTER_SHIPPING_EXPORT_FIELDS = [
  "exportNo",
  "creatorNickname",
  "creatorHandle",
  "tiktokUrl",
  "productOption",
  "recipientName",
  "address",
  "phone"
];
var trackingImportRequestSchema = z16.object({
  lines: z16.array(
    z16.object({
      no: z16.number().int().nonnegative().nullable(),
      handle: z16.string().max(200).nullable(),
      carrier: z16.string().max(100).nullable(),
      trackingNumber: z16.string().max(100).nullable()
    })
  ).min(1).max(2e3)
});

// src/dto/campaign-roster/sheet-import.ts
import { z as z17 } from "zod";
var MAPPING_WARNING_CODES2 = Object.values(MappingWarningCode);
var mappingWarningSchema2 = z17.object({
  code: z17.enum(MAPPING_WARNING_CODES2),
  label: z17.string().max(200),
  key: z17.string().max(100).nullable()
}).strict();
var sheetText = (max) => z17.string().max(max).nullable();
var campaignRosterImportRowSchema = z17.object({
  entry: z17.object({
    creatorNicknameSnapshot: sheetText(200),
    creatorHandleSnapshot: sheetText(200),
    tiktokUrlSnapshot: sheetText(2048),
    followersSnapshot: z17.number().int().min(0).nullable(),
    uploadDate: z17.string().regex(/^\d{4}-\d{2}-\d{2}$/, "uploadDate must be YYYY-MM-DD").nullable(),
    videoUrl: sheetText(2048),
    adCode: sheetText(100),
    cleanFile: z17.boolean().nullable(),
    cleanFileUrl: sheetText(2048),
    issueNote: sheetText(2e3),
    negotiatedAmount: z17.number().nullable(),
    negotiatedCurrency: sheetText(3),
    kakaoChannelNo: sheetText(200),
    kakaoChannelName: sheetText(200),
    // 주소는 `sheetText` 가 아니라 형식까지 본다 — 매핑이 이미 같은 스키마로 걸렀으므로
    // 여기서 걸리는 값은 매핑을 안 거친 요청이고, 그건 조용히 통과시키면 안 된다.
    creatorEmail: creatorEmailSchema.nullable(),
    crossPlatform: sheetText(2e3),
    productVariantSnapshot: sheetText(500),
    guideline: sheetText(2e3),
    accountNote: sheetText(2e3),
    settlement: sheetText(2e3)
  }).strict(),
  // 수취 식별자는 로스터 행이 아니라 관계 층 PII 로 간다 (2026-09-18). 키 이름은 감사 가림이
  // 보는 `paypalEmail`·`paypalMe` 그대로여야 하고, 형식은 매핑과 같은 스키마를 본다.
  payout: z17.object({
    paypalEmail: creatorEmailSchema.nullable(),
    paypalMe: paypalMeSchema.nullable()
  }).strict(),
  creatorLookup: z17.object({
    externalCreatorId: sheetText(200),
    handleCandidate: sheetText(200),
    tiktokUrlCandidate: sheetText(2048)
  }).strict(),
  warnings: z17.array(mappingWarningSchema2).max(100)
}).strict();
var CAMPAIGN_ROSTER_IMPORT_MAX_ROWS = 2e3;
var campaignRosterImportSchema = z17.object({
  rows: z17.array(campaignRosterImportRowSchema).min(1).max(CAMPAIGN_ROSTER_IMPORT_MAX_ROWS)
}).strict();
var CampaignRosterImportOutcome = {
  CREATED: "CREATED",
  UPDATED: "UPDATED",
  SKIPPED: "SKIPPED",
  FAILED: "FAILED"
};

// src/dto/campaign-roster/change-history.ts
var ROSTER_CHANGE_KINDS = [
  /** 단계가 옮겨졌다. `fromValue: null` 은 행이 선 것이다 — 감사 로그로는 못 찾는 줄이다. */
  "STAGE_TRANSITION",
  "PRICE_REVISION",
  "UPLOAD_TAKEDOWN",
  "SNAPSHOT_REVISION",
  "NUDGE",
  /** 평범한 칸 수정. 감사 로그의 요청 본문이 원천이라 옛 값이 없다. */
  "FIELD_EDIT",
  /** 편입 당시 조회수 지표 — 행당 한 줄, 갱신 없음 (퀘스트 aad3fb38). 행위자는 시스템 계정이다. */
  "VIEW_METRICS_SNAPSHOT",
  /** 종료·복구 사건 (섭외 IA §5, 퀘스트 5b8ba30f). 복구가 종료 줄을 지우지 않아 둘 다 남는다. */
  "DROP_EVENT",
  /** 영상별 권리 한 축의 확정 (campaign-video-rights-and-posts §4). 옛 값은 같은 축의 직전 줄이다. */
  "VIDEO_RIGHT",
  /** 자율 추가 게시물의 기록·내려감 확인·삭제 표시 (§3-2). 한 게시물이 최대 세 줄을 낸다. */
  "EXTRA_POST",
  /**
   * 담당이 바뀌었다 (creator-email-in-axiom §3-7). 값은 사람 이름이고 `toValue: null` 은 비운 것(퇴사)이다.
   * 비고가 원인이다 — 「가이드라인 전달을 맡아서」 · 「직접 넘김」 · 「퇴사로 비움」.
   */
  "ASSIGNEE_CHANGE"
];

// src/dto/campaign-roster/advertiser-review.ts
import { z as z18 } from "zod";
var openAdvertiserReviewRoundSchema = z18.object({
  rosterEntryIds: z18.array(z18.string().uuid()).min(1).refine((ids) => new Set(ids).size === ids.length, {
    message: "rosterEntryIds must not repeat a row"
  })
}).strict();
var recordAdvertiserReviewVerdictsSchema = z18.object({
  verdicts: z18.array(
    z18.object({
      rosterEntryId: z18.string().uuid(),
      verdict: z18.enum(ADVERTISER_REVIEW_VERDICTS)
    }).strict()
  ).min(1).refine((rows) => new Set(rows.map((row) => row.rosterEntryId)).size === rows.length, {
    message: "verdicts must not repeat a row"
  })
}).strict();
var changeAdvertiserReviewModeSchema = z18.object({
  mode: z18.enum(ADVERTISER_REVIEW_MODES),
  reason: z18.string().trim().max(500).optional()
}).strict();

// src/dto/campaign-roster-video.ts
import { z as z19 } from "zod";
var createCampaignRosterVideoRightSchema = z19.object({
  axis: z19.enum(VIDEO_RIGHT_AXES),
  value: z19.enum(VIDEO_RIGHT_VALUES),
  note: z19.string().trim().max(500).optional()
}).strict();
var bulkCreateCampaignRosterVideoRightSchema = createCampaignRosterVideoRightSchema.extend({
  entryIds: z19.array(z19.string().uuid()).min(1).max(500)
}).strict();
var createCampaignRosterExtraPostSchema = z19.object({
  url: z19.string().trim().min(1).max(2e3)
}).strict();

// src/dto/campaign-video-review.ts
import { z as z20 } from "zod";
var CampaignVideoReviewSourceType = {
  MANUAL: "MANUAL",
  GMAIL: "GMAIL"
};
var CampaignVideoReviewFileKind = {
  DRAFT: "DRAFT",
  CLEAN: "CLEAN"
};
var CampaignVideoReviewFileState = {
  PENDING: "PENDING",
  READY: "READY",
  CLEANUP_PENDING: "CLEANUP_PENDING",
  FAILED: "FAILED"
};
var fileKindSchema = z20.enum([
  CampaignVideoReviewFileKind.DRAFT,
  CampaignVideoReviewFileKind.CLEAN
]);
var MAX_VIDEO_REVIEW_FILE_BYTES = 5000000000n;
var SUPPORTED_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-matroska",
  "video/x-msvideo",
  "video/mpeg"
];
var createCampaignVideoReviewRevisionSchema = z20.object({
  // Gmail은 수신 어댑터가 내부에서 붙이는 출처다. 사람이 API body로 가장하지 못한다.
  sourceType: z20.literal(CampaignVideoReviewSourceType.MANUAL).optional(),
  receivedAt: z20.string().datetime({ offset: true }).optional(),
  guidelineEventId: z20.uuid().optional()
}).strict();
var createCampaignVideoReviewUploadSessionSchema = z20.object({
  fileKind: fileKindSchema,
  mimeType: z20.enum(SUPPORTED_VIDEO_MIME_TYPES),
  sizeBytes: z20.string().regex(/^[1-9]\d*$/, "sizeBytes must be a positive integer string").refine(
    (value) => !/^[1-9]\d*$/.test(value) || BigInt(value) <= MAX_VIDEO_REVIEW_FILE_BYTES,
    "sizeBytes exceeds the 5 GB video review limit"
  )
}).strict();

// src/dto/campaign-settlement.ts
import { z as z21 } from "zod";
var SETTLEMENT_REQUEST_MAX_ENTRIES = 500;
var settlementAmount = z21.string().regex(SETTLEMENT_AMOUNT_PATTERN, "amount must be a decimal(14,2) string").refine((value) => Number(value) > 0, "amount must be greater than zero");
var campaignSettlementEntryInputSchema = z21.object({
  /**
   * `z.uuid()` 는 미관이 아니다 — 검증 없는 id 가 `@db.Uuid` 컬럼으로 흘러가면 Prisma 가
   * 던지는 "Error creating UUID, invalid length" 가 `PrismaClientKnownRequestError` 가
   * 아니라서 전역 필터의 500 브랜치로 빠진다(레포에 알려진 크래시 클래스).
   */
  rosterEntryId: z21.uuid(),
  /**
   * 값을 다시 나열하지 않고 `SETTLEMENT_REQUESTABLE_KINDS` 를 소비한다.
   *
   * PRODUCT_SHIPMENT(제품 발송)이 빠진 이유는 값이 틀려서가 아니라 §5 가 **생성 주체·시점만**
   * 이월했기 때문이다 — 요청 API 를 열어두면 "마케팅팀 requester 가 요청 시점에 현물 행을
   * 만든다"를 확정해 버리는데 그 워크플로를 확인해 준 사람이 없다(§5 "지금 주체를 추정해
   * 넣으면 틀린 워크플로가 스키마에 새겨진다" · agreement §3-6 "제품 배송(현물) 케이스는
   * 실행자 소관이 아니다"). 게다가 `payoutMethod` 는 NOT NULL 에 기본값도 없고 값 2종이 둘 다
   * 송금 수단이라, 현물 행을 지금 만들면 거짓 지급 수단을 반드시 하나 골라 넣어야 한다.
   * 거부 사유는 "잘못된 값"이 아니라 "아직 열리지 않은 값"이다. 생성 주체가 확정되면 도메인
   * 상수에 한 줄이 늘고 이 스키마는 그대로 따라간다 — 리터럴로 적으면 그 연동이 끊긴다.
   */
  kind: z21.enum(SETTLEMENT_REQUESTABLE_KINDS),
  amount: settlementAmount,
  /**
   * 형식(3자 대문자)만 본다. 허용 목록 판정은 백엔드 정산 모듈이 주입하는
   * `createSettlementCurrencyValidator` 가 서비스 층에서 한다 — 둘은 층이 다르다
   * (settlement-currency.ts). DTO 에 목록을 박으면 §3-1 이 금지한 고정 열거가 되돌아와
   * 새 대상국 정산이 shared 릴리스를 기다린다. trim · toUpperCase 도 하지 않는다: 컬럼이
   * CHAR(3) 이라 2자 코드는 'US ' 로 패딩 저장돼 어떤 검증기로도 다시 못 잡는 행이 되고,
   * 4자는 400 이 아니라 500 이며, 소문자를 살려내면 시트 오염이 통화로 승격한다.
   */
  currency: z21.string().regex(SETTLEMENT_CURRENCY_PATTERN, "currency must be an ISO 4217 code"),
  /**
   * 카카오톡 요청 5종에 없는 필드다(확인된 §2 예외 1건째 — `currency` 가 2건째일 수 있다.
   * 위 정산건 주석 참조) — 이 설계에서 "폼을 키우지 마라"(agreement §2)와
   * 정면으로 만나는 지점이므로 근거를 남긴다. 컬럼은 NOT NULL 에 `@default` 도 없어
   * (T2: "기본값도 유도의 일종이다") INSERT 시점에 값이 반드시 있어야 하는데, 출처는 바디 아니면
   * 서버 상수뿐이다. 서버가 채울 근거는 없다 — 이 축은 kind 에서 나오지 않고 페이팔 미활성
   * 국가(일본·태국·동남아)면 국제 송금이라 **국가**에서 오며(§3-3), 그래서
   * `SETTLEMENT_KIND_ACCOUNTING_DEFAULTS` 도 이 축을 의도적으로 제외했고 로스터 행에는 국가
   * 컬럼조차 없다. 기본값 PAYPAL 을 두면 일본·태국·동남아 건 전량이 거짓 지급 수단으로
   * 원장에 확정 기록된다 — §2-1 이 막으려던 "과거 행의 회계 해석이 소급해서 달라지는" 사고와
   * 같은 급이다. 요청자는 사실상 이미 알고 있으므로(페이팔 이메일을 적었는지 송금 지시를
   * 적었는지가 곧 이 값이다) 폼은 새 구조가 아니라 2지 토글 하나만큼 는다.
   *
   * 미결(실행자 확인 1문항): 요청 시점에 요청자가 아는가, 실행자가 정하는가. 답이 "실행자"면
   * 고칠 자리는 이 스키마가 아니라 T2 스키마(nullable + 첫 전이 때 실행자 기입)다.
   *
   * `kind` 와 달리 선언된 2종이 **전부** 정당한 요청값이라 열거 객체를 통째로 소비한다 —
   * 값이 늘면 자동으로 열린다. 두 축의 fail 방향이 반대인 것이 요점이다.
   */
  payoutMethod: z21.enum(SettlementPayoutMethod)
}).strict();
var createCampaignSettlementRequestSchema = z21.object({
  // 한 업무 요청에 한 번 만들고, 응답 유실·타임아웃 재전송에도 같은 값을 보낸다.
  idempotencyKey: z21.uuid(),
  entries: z21.array(campaignSettlementEntryInputSchema).min(1).max(SETTLEMENT_REQUEST_MAX_ENTRIES)
}).strict();
var SETTLEMENT_MARKABLE_STATUSES = [
  SettlementStatus.ON_HOLD,
  SettlementStatus.PAID,
  SettlementStatus.FAILED,
  SettlementStatus.RETURNED
];
var settlementMarkingShape = {
  toStatus: z21.enum(SETTLEMENT_MARKABLE_STATUSES),
  reason: z21.string().max(500).optional()
};
function requireReasonForMarking(value, ctx) {
  if (!SETTLEMENT_REASON_REQUIRED_STATUSES.includes(value.toStatus)) {
    return;
  }
  if (!value.reason || !value.reason.trim()) {
    ctx.addIssue({
      code: z21.ZodIssueCode.custom,
      path: ["reason"],
      message: `reason is required when marking ${value.toStatus}`
    });
  }
}
var markCampaignSettlementStatusSchema = z21.object(settlementMarkingShape).strict().superRefine(requireReasonForMarking);
var bulkMarkCampaignSettlementStatusSchema = z21.object({
  entryIds: z21.array(z21.uuid()).min(1).max(SETTLEMENT_REQUEST_MAX_ENTRIES).refine((ids) => new Set(ids).size === ids.length, "entryIds must not repeat"),
  ...settlementMarkingShape
}).strict().superRefine(requireReasonForMarking);
var markSettlementDocumentsSchema = z21.object({ received: z21.boolean() }).strict();
var settlementPaidDateSchema = z21.string().regex(/^\d{4}-\d{2}-\d{2}$/, "\uC9C0\uAE09\uC77C\uC740 YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4").refine((value) => {
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}, "\uB2EC\uB825\uC5D0 \uC5C6\uB294 \uB0A0\uC9DC\uC785\uB2C8\uB2E4");
var settlementQueueQuerySchema = z21.object({
  status: z21.string().optional(),
  cursor: z21.string().uuid().optional(),
  handle: z21.string().trim().min(1).max(100).optional(),
  campaign: z21.string().trim().min(1).max(200).optional(),
  paidFrom: settlementPaidDateSchema.optional(),
  paidTo: settlementPaidDateSchema.optional()
}).strict();

// src/dto/creator-mail.ts
import { z as z22 } from "zod";
var CREATOR_MAIL_CONNECTION_STATUSES = ["NOT_CONNECTED", "CONNECTED", "BROKEN"];
var deepLinkView = z22.string().regex(/^[a-z][a-z0-9-]{0,40}$/, {
  message: "\uCC3D \uC774\uB984\uB9CC \uC62C \uC218 \uC788\uC2B5\uB2C8\uB2E4"
});
var startCreatorMailConnectionSchema = z22.object({
  returnTo: z22.object({
    view: deepLinkView,
    // 딥링크가 아는 칸만. 쿼리를 그대로 흘려보내지 않으려고 여기서는 strict 다.
    context: NavigationContextSchema.strict()
  }).strict().optional()
}).strict();
var CREATOR_MAIL_CONNECT_FAILURES = [
  "STATE_INVALID",
  "CONSENT_DENIED",
  "SCOPE_MISSING",
  "NO_REFRESH_TOKEN",
  "ACCOUNT_MISMATCH",
  "EXCHANGE_FAILED"
];
var creatorMailThreadRevisionSchema = z22.object({
  lastContactedAt: z22.iso.datetime().nullable(),
  lastRepliedAt: z22.iso.datetime().nullable()
}).strict();
var sendCreatorMailSchema = z22.object({
  subject: z22.string().trim().min(1).max(200),
  body: z22.string().trim().min(1).max(2e4),
  revision: creatorMailThreadRevisionSchema
}).strict();
var saveCreatorMailDraftSchema = z22.object({
  subject: z22.string().trim().min(1).max(200),
  body: z22.string().max(2e4),
  draftVersion: z22.number().int().min(0).max(2147483647),
  revision: creatorMailThreadRevisionSchema
}).strict();
var takeOverCreatorMailDraftSchema = z22.object({
  draftVersion: z22.number().int().min(0).max(2147483647),
  revision: creatorMailThreadRevisionSchema
}).strict();

// src/dto/creator-outreach.ts
import { z as z23 } from "zod";
var OUTREACH_ROUND_MAX_HANDLES = 1e3;
var OUTREACH_RETENTION_POLICY = "UNTIL_SCENARIO_DELETION";
var OUTREACH_HANDLE_FIELD_KEYS = ["tiktokerUrl", "handle"];
var outreachBulkSendSchema = z23.object({
  /** 이 초안으로 이미 나간 회차가 있으면 다시 안 보낸다. */
  draftId: z23.string().uuid(),
  /**
   * 주소를 공유하는 계정에게 따로 보낼 번호들. **이번 묶음에만 걸린다** — 다음 회차는 다시
   * 주소당 한 번이다(IA §3-3).
   */
  perCreatorSendIds: z23.array(z23.string().min(1).max(64)).max(1e3).optional(),
  /**
   * 회신·폼 제출로 기본 제외된 사람 중 **이번 묶음에 다시 넣을** 번호들(퀘스트 12bcfe19). 초안에는
   * 저장하지 않는다 — 다음 묶음·다른 초안·복제 초안에 전파되지 않는다(재포함 명세 §2-2).
   * 미리보기·발송·예약이 같은 값을 받는다.
   */
  reincludeIds: z23.array(z23.string().min(1).max(64)).max(1e3).optional(),
  /**
   * 재포함한 사람별로 미리보기가 돌려준 **마지막 사건 시각**. 서버가 지금 읽은 시각과 비교해 더 늦은
   * 회신·폼 제출이 있으면 요청 전체를 거절하고 바뀐 사람을 돌려준다(재포함 명세 §2-4).
   */
  reincludeEvidenceAt: z23.record(z23.string().min(1).max(64), z23.string().datetime({ offset: true })).optional(),
  senderName: z23.string().min(1).max(100),
  /** 내부 테스트 발송인가 — 폼 잠금이 이 값을 본다. 표시 없이 테스트하면 그 캠페인의 폼이 잠긴다. */
  isTest: z23.boolean().default(false)
}).strict();
var outreachBulkReserveSchema = outreachBulkSendSchema.extend({
  schedule: z23.object({
    local: z23.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
    timeZone: z23.string().min(1).max(64)
  }).strict()
});

// src/dto/creator-list.ts
import { z as z24 } from "zod";
var CREATOR_LIST_DOWNLOAD_ROUTE = "creator-lists/downloads/:downloadId/contacts";
function creatorListDownloadPath(downloadId) {
  return `/${CREATOR_LIST_DOWNLOAD_ROUTE.replace(":downloadId", downloadId)}`;
}
var CREATOR_LIST_CHANNELS = ["email", "instagram", "line"];
var CREATOR_LIST_FILE_LIMIT = 1e4;
var CREATOR_LIST_FILE_LIMIT_MAX = 15e3;
var CREATOR_LIST_CONTACT_BATCH = 1e3;
var CREATOR_LIST_RETENTION_DAYS = 730;
var CREATOR_LIST_REDOWNLOAD_DAYS = 30;
var CREATOR_LIST_CONTACT_LOG_NOTICE = "\uC5F0\uB77D \uAE30\uB85D \uCE78\uC5D0\uB294 AXIOM \uC5D0 \uB0A8\uC740 \uBA54\uC77C\uACFC \uD06C\uB9AC\uC5D0\uC774\uD130 \uAD00\uACC4 \uCC3D\uC5D0 \uB0A8\uAE34 \uC12D\uC678\uB9CC \uB4E4\uC5B4\uAC11\uB2C8\uB2E4. \uC2A4\uD2F0\uBE44 \uBC1C\uC1A1\uACFC \uB0A8\uAE30\uC9C0 \uC54A\uC740 DM\xB7\uB77C\uC778 \uC5F0\uB77D\uC740 \uB098\uC624\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.";
var creatorListQueryRequestSchema = z24.object({
  teamId: z24.string().uuid(),
  region: z24.string().regex(/^[A-Z]{2}$/),
  minFollowers: z24.number().int().min(0),
  maxFollowers: z24.number().int().min(0).optional()
}).strict().refine((v) => v.maxFollowers === void 0 || v.maxFollowers >= v.minFollowers, {
  path: ["maxFollowers"],
  message: "\uD314\uB85C\uC6CC \uC0C1\uD55C\uC740 \uD558\uD55C\uBCF4\uB2E4 \uC791\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
});
var creatorListBuildSchema = z24.object({
  teamId: z24.string().uuid(),
  queryRequestId: z24.string().uuid(),
  channels: z24.array(z24.enum(CREATOR_LIST_CHANNELS)).min(1),
  includeAlreadyDownloaded: z24.boolean(),
  resumeFromDownloadId: z24.string().uuid().optional()
}).strict();

// src/dto/creator-projection.ts
import { z as z25 } from "zod";
var CREATOR_SEARCH_QUERY_MIN = 2;
var CREATOR_SEARCH_QUERY_MAX = 100;
var followersBoundSchema = z25.coerce.number().int().min(0);
var searchCreatorProjectionsSchema = z25.object({
  /**
   * 핸들·표시명 부분 일치. 한 글자로 전체를 훑어 내려받는 것을 막기 위해 2자 이상을 받는다 —
   * autocomplete 는 원래 몇 자 친 뒤에 뜨는 물건이라 UX 손해가 없다.
   */
  q: z25.string().trim().min(CREATOR_SEARCH_QUERY_MIN).max(CREATOR_SEARCH_QUERY_MAX).optional(),
  /** 팔로워 구간 (양끝 포함). `followers` 가 null 인 행은 어느 구간에도 걸리지 않는다. */
  followersMin: followersBoundSchema.optional(),
  followersMax: followersBoundSchema.optional(),
  /**
   * 관계 층 상태. `UNCONTACTED` 만 뜻이 넓다 — 관계 행이 아예 없는 크리에이터도 미컨택이라
   * 서버가 "행 없음 ∪ state UNCONTACTED" 로 읽는다. 나머지는 행의 state 일치다.
   */
  relationState: z25.enum(RELATION_STATES).optional(),
  /**
   * 기한 안 차단자도 볼지. 조건 조회는 기본으로 빼고(roster-form-intake §7 "기본 제외하되 보이게"),
   * 핸들만으로 지목한 검색은 원래 빼지 않는다. 조건이 아니라 스위치라 아래 refine 에 들지 않는다.
   * 쿼리스트링이라 `"false"` 가 온다 — `z.coerce.boolean()` 은 그것을 true 로 읽는다.
   */
  includeBlocked: z25.stringbool().optional(),
  limit: z25.coerce.number().int().min(1).max(50).optional()
}).strict().refine(
  (v) => v.q !== void 0 || v.followersMin !== void 0 || v.followersMax !== void 0 || v.relationState !== void 0,
  { message: "q \uB610\uB294 \uC870\uAC74(followersMin \xB7 followersMax \xB7 relationState) \uC911 \uD558\uB098\uB294 \uC788\uC5B4\uC57C \uD569\uB2C8\uB2E4" }
).refine(
  (v) => v.followersMin === void 0 || v.followersMax === void 0 || v.followersMin <= v.followersMax,
  { message: "followersMin \uC740 followersMax \uC774\uD558\uC5EC\uC57C \uD569\uB2C8\uB2E4", path: ["followersMin"] }
);

// src/dto/creator-relation.ts
import { z as z28 } from "zod";

// src/dto/creator-relation-pii.ts
import { z as z26 } from "zod";
var CREATOR_PII_FIELD_KEYS = [
  "recipientName",
  "address",
  "phone",
  "paypalEmail",
  "paypalMe"
];
var CREATOR_PII_FIELD_MAX_LENGTH = 500;
var piiField = z26.string().trim().min(1).max(CREATOR_PII_FIELD_MAX_LENGTH);
var creatorRelationPiiSchema = z26.object({
  relationId: z26.uuid(),
  recipientName: piiField.nullable(),
  address: piiField.nullable(),
  phone: piiField.nullable(),
  paypalEmail: piiField.nullable(),
  paypalMe: piiField.nullable(),
  /** 파기 예정 — 마지막 접점 + 12개월. 크론의 입력이다. */
  purgeDueAt: Iso8601Schema.nullable(),
  /** 비운 시각. 있으면 "받았다가 지웠다"다. */
  purgedAt: Iso8601Schema.nullable()
}).strict();
var upsertCreatorRelationPiiSchema = z26.object({
  recipientName: piiField.optional(),
  address: piiField.optional(),
  phone: piiField.optional(),
  paypalEmail: piiField.optional(),
  paypalMe: piiField.optional()
}).strict();
var CREATOR_PII_RETENTION_MONTHS = 12;
var CREATOR_PII_PURGE_CAUSES = ["EXPIRED", "REQUESTED"];
var CREATOR_PII_PURGE_BLOCKED = "CREATOR_PII_PURGE_BLOCKED";

// src/dto/outreach-candidate.ts
import { z as z27 } from "zod";
var CANDIDATE_QUERY_STATES = ["queued", "running", "ready", "failed", "expired"];
var OUTREACH_REINCLUDE_REASONS = [
  "REPLIED",
  "FORM_SUBMITTED",
  "OTHER_TEAM_RECENT"
];
var OUTREACH_OUTLIER_GROUP_CODES = [
  /** 관계 정책 연락 거부·보류. 반송·수신거부는 결과 동기화(퀘스트 96445435) 뒤 `reason` 을 늘린다. */
  "DO_NOT_SEND",
  /**
   * 최근 `OUTREACH_OUTLIER_OTHER_TEAM_DAYS` 안에 다른 팀의 캠페인이 연락·진행 사건을 남긴 사람.
   * 재섭외 충돌을 막으려고 보인다. 참여팀이 이 캠페인과 하나라도 겹치는 캠페인은 같은 팀이라 들지
   * 않고, 참여팀이 없는 캠페인은 팀 모름으로 든다. 기간 안에 새 사건이 없는 진행 중 로스터는 안
   * 든다(캠페인이 최장 60.5일이라 드물다).
   */
  "OTHER_TEAM_RECENT",
  /** 이 캠페인에서 회신했거나 폼을 낸 사람 — 기존 기본 제외와 같은 판정이다. */
  "ALREADY_ANSWERED",
  /** 주소 하나를 여러 계정이 쓴다. 기본은 주소당 한 통이다. */
  "SHARED_ADDRESS",
  /** 주소가 없거나 원천이 그 번호를 모른다. */
  "NO_ADDRESS"
];
var OUTREACH_OUTLIER_PRECEDENCE = [
  "DO_NOT_SEND",
  "ALREADY_ANSWERED",
  "NO_ADDRESS",
  "OTHER_TEAM_RECENT",
  "SHARED_ADDRESS"
];
var OUTREACH_OUTLIER_GROUP_RULES = {
  // 차단·보류는 이 화면에서 풀지 않는다 — 관계 창에서 사람이 푼다(IA §3-3 기본 제외 끝 문장).
  DO_NOT_SEND: { defaultAction: "EXCLUDE", reversible: false },
  OTHER_TEAM_RECENT: { defaultAction: "EXCLUDE", reversible: true },
  ALREADY_ANSWERED: { defaultAction: "EXCLUDE", reversible: true },
  SHARED_ADDRESS: { defaultAction: "ONE_PER_ADDRESS", reversible: true },
  NO_ADDRESS: { defaultAction: "UNSENDABLE", reversible: false }
};
var OUTREACH_OUTLIER_OTHER_TEAM_EVENTS = [
  "CONTACT_SENT",
  "REPLY_RECEIVED",
  "ROSTER_JOINED",
  "PRICE_AGREED",
  "CONTENT_POSTED"
];
var OUTREACH_OUTLIER_OTHER_TEAM_DAYS = 60;
var OUTREACH_OUTLIER_PREVIEW_DEFAULT = 3;
var OUTREACH_OUTLIER_PAGE_MAX = 200;
var outreachOutlierSummaryRequestSchema = z27.object({
  draftId: z27.string().uuid(),
  previewLimit: z27.number().int().min(0).max(20).optional()
}).strict();
var outreachOutlierRowsRequestSchema = z27.object({
  draftId: z27.string().uuid(),
  code: z27.enum(OUTREACH_OUTLIER_GROUP_CODES),
  offset: z27.number().int().min(0).optional(),
  limit: z27.number().int().min(1).max(OUTREACH_OUTLIER_PAGE_MAX).optional()
}).strict();

// src/dto/creator-relation.ts
var createCreatorRelationSchema = z28.object({
  externalCreatorId: z28.string().min(1).max(200).optional(),
  sourceSystem: z28.literal(CREATOR_RELATION_SOURCE_SYSTEM).optional(),
  handle: z28.string().min(1).max(200).optional()
}).strict().refine((v) => v.externalCreatorId !== void 0 || v.handle !== void 0, {
  message: "externalCreatorId \uB610\uB294 handle \uD558\uB098\uB294 \uD544\uC218"
});
var RELATION_CONTACT_CHANNELS = ["DM", "KAKAO", "PHONE", "OTHER"];
var RELATION_CONTACT_CHANNEL_LABELS = {
  DM: "DM",
  KAKAO: "\uCE74\uD1A1",
  PHONE: "\uC804\uD654",
  OTHER: "\uAE30\uD0C0"
};
var recordOffRosterContactSchema = z28.object({
  handle: z28.string().trim().min(1).max(200),
  channel: z28.enum(RELATION_CONTACT_CHANNELS),
  contactedOn: z28.string().refine(isValidDateOnly, {
    message: "\uC5F0\uB77D\uD55C \uB0A0\uC740 YYYY-MM-DD \uB0A0\uC9DC\uC5EC\uC57C \uD569\uB2C8\uB2E4"
  })
}).strict();
var changeCreatorRelationStateSchema = z28.object({
  to: z28.enum(RELATION_STATES),
  stateChangeReason: z28.string().min(1).max(2e3).optional()
}).strict();
var blockCreatorRelationSchema = z28.object({
  blockReasonKind: z28.enum(BLOCK_REASON_KINDS),
  relationBlockReason: z28.string().trim().min(1).max(2e3),
  blockedUntil: Iso8601Schema.transform((value) => new Date(value))
}).strict();
var unblockCreatorRelationSchema = z28.object({
  relationUnblockReason: z28.string().trim().min(1).max(2e3)
}).strict();
var outreachBlockCreatorRelationSchema = z28.object({
  relationOutreachBlockReason: z28.string().trim().min(1).max(2e3)
}).strict();
var outreachUnblockCreatorRelationSchema = z28.object({
  relationOutreachUnblockReason: z28.string().trim().min(1).max(2e3)
}).strict();
var assignCreatorRelationOwnerSchema = z28.object({
  assigneeUserId: z28.uuid()
}).strict();
var createCreatorNotepadEntrySchema = z28.object({
  notepadBody: z28.string().min(1).max(4e3)
}).strict();
var createCreatorRelationNoteSchema = z28.object({
  relationNoteBody: z28.string().min(1).max(4e3)
}).strict();
var CREATOR_RELATION_SEEDING_TEAM_IDS_KEY = "creator_relation.seeding_team_ids";
var updateSeedingTeamIdsSchema = z28.object({
  teamIds: z28.array(z28.uuid())
}).strict();
var CREATOR_RELATION_SEEDING_TEAM_INVALID = "CREATOR_RELATION_SEEDING_TEAM_INVALID";
var CREATOR_RELATION_HANDLE_RELATIONS_OVERFLOW = "CREATOR_RELATION_HANDLE_RELATIONS_OVERFLOW";
function parseSeedingTeamIds(raw) {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return [];
  }
  const value = raw.teamIds;
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((id) => typeof id === "string" && id !== "");
}
var CREATOR_RELATION_SENSITIVE_KEYS = [
  "notepadBody",
  "relationNoteBody",
  "stateChangeReason",
  "relationBlockReason",
  "relationUnblockReason",
  "relationOutreachBlockReason",
  "relationOutreachUnblockReason",
  "relationReRecruitReason",
  // 배송·정산 개인정보 다섯 칸. 쓰기 body 가 감사에 평문으로 남지 않게 저장소를 세우는 지금
  // 함께 막는다 — 나중에 더하면 그 사이의 쓰기는 이미 평문으로 남아 있다.
  ...CREATOR_PII_FIELD_KEYS
];
var creatorRelationPersonSchema = z28.object({
  userId: z28.uuid(),
  displayName: z28.string(),
  departed: z28.boolean()
}).strict();
var creatorRelationRefSchema = z28.object({
  sourceSystem: z28.literal(CREATOR_RELATION_SOURCE_SYSTEM),
  externalCreatorId: z28.string().min(1).max(200)
}).strict();
var creatorRelationIntimacyMeasureSchema = z28.discriminatedUnion("key", [
  z28.object({ key: z28.literal("lastDirectAt"), value: Iso8601Schema.nullable() }).strict(),
  z28.object({ key: z28.literal("directCount"), value: z28.number().int().min(0) }).strict()
]);
var ownerSectionSchema = z28.discriminatedUnion("state", [
  z28.object({ state: z28.literal("PENDING_ASSIGNMENT") }).strict(),
  z28.object({ state: z28.literal("ASSIGNED"), user: creatorRelationPersonSchema }).strict()
]);
var intimacySectionSchema = z28.discriminatedUnion("state", [
  z28.object({ state: z28.literal("PENDING_ASSIGNMENT") }).strict(),
  z28.object({
    state: z28.literal("ASSIGNED"),
    viewerIsOwner: z28.boolean(),
    measures: z28.array(creatorRelationIntimacyMeasureSchema)
  }).strict()
]);
var idleWarningSectionSchema = z28.discriminatedUnion("state", [
  z28.object({ state: z28.literal("PENDING_ASSIGNMENT") }).strict(),
  z28.object({
    state: z28.literal("ASSIGNED"),
    idleDays: z28.number().int().min(0).nullable(),
    warn: z28.boolean()
  }).strict()
]);
var creatorCampaignPriceSchema = z28.object({
  scenarioId: z28.uuid(),
  amount: z28.string(),
  currency: z28.string().length(3)
}).strict();
var creatorRelationCardSectionsSchema = z28.object({
  discoverer: z28.object({ user: creatorRelationPersonSchema.nullable() }).strict(),
  // 단가가 적힌 캠페인이 없으면 키가 없다 — 등급 게이트는 걸리지 않는다(§3-8).
  campaignPrices: z28.array(creatorCampaignPriceSchema).nonempty().optional(),
  owner: ownerSectionSchema.optional(),
  intimacy: intimacySectionSchema.optional(),
  idleWarning: idleWarningSectionSchema.optional()
}).strict();
var creatorRelationCardSchema = z28.object({
  relationId: z28.uuid(),
  ref: creatorRelationRefSchema.nullable(),
  handle: z28.string().nullable(),
  nickname: z28.string().nullable(),
  tiktokUrl: z28.string().nullable(),
  followers: z28.number().int().nullable(),
  sourceUpdatedAt: Iso8601Schema.nullable(),
  tombstonedAt: Iso8601Schema.nullable(),
  state: z28.enum(RELATION_STATES),
  sections: creatorRelationCardSectionsSchema,
  /**
   * 참여한 캠페인 — 로스터 행마다 한 줄. 같은 캠페인의 영상별 단가는 `finalPrice`로
   * 그 행에 붙인다. `sections.campaignPrices`는 이전 소비자용이고 행별 결합에 쓰지 않는다.
   * 이름은 카드 권한으로 공개하고 상세 진입은 별도 판정한다. 추가 필드 생략은 이전 서버용이다.
   */
  campaigns: z28.array(
    z28.object({
      scenarioId: z28.uuid(),
      scenarioTitle: z28.string().optional(),
      rosterEntryId: z28.uuid().optional(),
      contractVideoNo: z28.number().int().positive().optional(),
      /** 확인된 캠페인 시작일(KST). null은 시작일 미확인이고 단가 유무와 무관하다. */
      campaignStartsOn: z28.iso.date().nullable().optional(),
      /**
       * 해당 로스터 행의 최종 합의 단가. null은 두 원천 칸이 모두 비었다는 뜻이다.
       * 한 칸만 남은 과거 누락도 보존한다. recordedAt은 같은 값의 최신 합의 단가 기록
       * 시각이며, 실제 적용일이나 캠페인 시작일이 아니다. 없으면 추정하지 않는다.
       */
      finalPrice: z28.object({
        amount: z28.string().nullable(),
        currency: z28.string().length(3).nullable(),
        recordedAt: Iso8601Schema.nullable()
      }).strict().nullable().optional(),
      rosterStatus: z28.string().nullable(),
      uploaded: z28.boolean(),
      dropKind: z28.string().nullable()
    }).strict()
  ),
  /** 최근 등록 50행 밖에 이력이 더 있는지. 생략은 이전 서버 응답이다. */
  campaignsHasMore: z28.boolean().optional(),
  /**
   * 합쳐진 핸들 관계의 id 로 열어 이 관계로 넘어왔을 때만 키가 있다(퀘스트 e261b316). 화면이
   * "어느 핸들이 언제 이 크리에이터로 합쳐졌는지" 를 한 줄로 말하는 근거다.
   */
  mergedFrom: z28.object({ handle: z28.string().nullable(), mergedAt: Iso8601Schema }).strict().optional()
}).strict();
var creatorRelationRegionSchema = z28.string().regex(/^[A-Z]{2}$/);
var creatorRelationListItemSchema = z28.object({
  relationId: z28.uuid(),
  ref: creatorRelationRefSchema.nullable(),
  handle: z28.string().nullable(),
  nickname: z28.string().nullable(),
  state: z28.enum(RELATION_STATES),
  lastTouchAt: Iso8601Schema.nullable(),
  tombstonedAt: Iso8601Schema.nullable(),
  premiumSheet: z28.boolean(),
  warmPool: z28.object({
    state: z28.enum(WARM_POOL_STATES),
    basis: z28.enum(WARM_POOL_BASES),
    sourceScenarioId: z28.uuid().nullable(),
    qualifiedAt: Iso8601Schema
  }).strict().nullable(),
  recruitmentVerdict: z28.enum(["ELIGIBLE", "HELD", "BLOCKED"]),
  /**
   * 마지막 크리에이터 하차 — 하차 보기(`view=dropped`)에서만 채운다. 다른 보기에서는 이력을
   * 읽지 않으므로 null 이다. 캠페인 이름은 시나리오 열람이 다른 권한 축이라 화면이 참조로 푼다.
   */
  dropped: z28.object({
    scenarioId: z28.uuid().nullable(),
    stage: z28.string().nullable(),
    droppedAt: Iso8601Schema
  }).strict().nullable(),
  owner: creatorRelationPersonSchema.nullable(),
  /**
   * 사본의 리전 — 대문자 ISO 3166-1 alpha-2, 사본이 없거나 원천이 비었으면 null (read 계약
   * §3.5). 질의의 리전 조건·선택지 표면과 같은 값 모양이라 소문자·세 글자를 들이면 선택지와
   * 행 태그가 갈라진다.
   */
  region: creatorRelationRegionSchema.nullable()
}).strict();
var creatorRelationListViewerSchema = z28.object({
  premiumAccess: z28.boolean(),
  canRaiseState: z28.boolean(),
  canAssignOwner: z28.boolean()
}).strict();
var CREATOR_RELATION_LIST_TAKE = 200;
var CREATOR_RELATION_LIST_SORTS = ["registered", "lastContact"];
var CREATOR_RELATION_VIEWS = ["all", "warm", "dropped"];
var creatorRelationListQuerySchema = z28.object({
  q: z28.string().trim().min(CREATOR_SEARCH_QUERY_MIN).max(CREATOR_SEARCH_QUERY_MAX).optional(),
  limit: z28.coerce.number().int().min(1).max(CREATOR_RELATION_LIST_TAKE).default(CREATOR_RELATION_LIST_TAKE),
  sort: z28.enum(CREATOR_RELATION_LIST_SORTS).default("registered"),
  view: z28.enum(CREATOR_RELATION_VIEWS).optional(),
  // 단계 탭 — 목록이 한 장씩 오므로 받은 행 위에서 거르면 첫 장 밖의 행을 못 찾는다(리전과 같은 이유).
  state: z28.enum(RELATION_STATES).optional(),
  region: creatorRelationRegionSchema.optional(),
  cursor: z28.uuid().optional(),
  cursorLastContactAt: Iso8601Schema.optional()
}).strict().refine(
  (v) => v.cursorLastContactAt === void 0 || v.cursor !== void 0 && v.sort === "lastContact",
  { message: "cursorLastContactAt \uC740 sort=lastContact \uC758 cursor \uC640 \uD568\uAED8 \uC635\uB2C8\uB2E4" }
).refine((v) => v.view !== "warm" || v.region === void 0, {
  message: "\uC6CC\uBC0D\uD480 \uBCF4\uAE30\uC5D0\uB294 \uB9AC\uC804 \uC870\uAC74\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"
});
var creatorRelationListCursorSchema = z28.object({
  id: z28.uuid(),
  lastContactAt: Iso8601Schema.nullable()
}).strict();
var creatorRelationListResponseSchema = z28.object({
  items: z28.array(creatorRelationListItemSchema),
  viewer: creatorRelationListViewerSchema,
  nextCursor: creatorRelationListCursorSchema.nullable()
}).strict();
var creatorRelationSummaryQuerySchema = z28.object({
  q: z28.string().trim().min(CREATOR_SEARCH_QUERY_MIN).max(CREATOR_SEARCH_QUERY_MAX).optional(),
  region: creatorRelationRegionSchema.optional()
}).strict();
var creatorRelationRegionsResponseSchema = z28.object({
  regions: z28.array(creatorRelationRegionSchema)
}).strict();
var creatorRelationSummaryResponseSchema = z28.object({
  byState: z28.record(z28.enum(RELATION_STATES), z28.number().int().nonnegative())
}).strict();
var creatorRelationOtherTeamContactsQuerySchema = z28.object({
  handle: z28.string().trim().min(1).max(CREATOR_SEARCH_QUERY_MAX)
}).strict();
var creatorRelationOtherTeamContactSchema = z28.object({
  team: z28.string().nullable(),
  kind: z28.enum(OUTREACH_OUTLIER_OTHER_TEAM_EVENTS),
  date: z28.string().regex(/^\d{4}-\d{2}-\d{2}$/)
}).strict();
var creatorRelationOtherTeamContactsResponseSchema = z28.object({
  assignees: z28.array(creatorRelationPersonSchema),
  contacts: z28.array(creatorRelationOtherTeamContactSchema)
}).strict();
var creatorRelationQueueResponseSchema = z28.object({
  items: z28.array(creatorRelationListItemSchema)
}).strict();
var creatorRelationTimelineQuerySchema = z28.object({
  limit: z28.coerce.number().int().min(1).max(100).default(20),
  cursorOccurredAt: Iso8601Schema.optional(),
  cursorId: z28.uuid().optional()
}).strict().refine((v) => v.cursorOccurredAt === void 0 === (v.cursorId === void 0), {
  message: "cursorOccurredAt \uACFC cursorId \uB294 \uD55C \uC30D\uC785\uB2C8\uB2E4"
});
var creatorRelationHistoryCursorSchema = z28.object({
  occurredAt: Iso8601Schema,
  id: z28.uuid()
}).strict();
var creatorRelationHistoryItemSchema = z28.object({
  id: z28.uuid(),
  type: z28.enum(RELATION_HISTORY_TYPES),
  origin: z28.enum(RELATION_HISTORY_ORIGINS),
  occurredAt: Iso8601Schema,
  actor: creatorRelationPersonSchema.nullable(),
  from: z28.enum(RELATION_STATES).optional(),
  to: z28.enum(RELATION_STATES).optional(),
  reason: z28.string().optional(),
  scenarioId: z28.uuid().optional(),
  rosterEntryId: z28.uuid().optional(),
  fromUser: creatorRelationPersonSchema.nullable().optional(),
  toUser: creatorRelationPersonSchema.nullable().optional(),
  cause: z28.enum(OWNER_CHANGE_CAUSES).optional(),
  discoveredBy: creatorRelationPersonSchema.nullable().optional(),
  /** `HISTORY_REVOKED` 에만 — 무효로 돌린 원 사건 id. 화면 파생이 원 사건을 빼는 데 쓴다. */
  revokedHistoryId: z28.uuid().optional(),
  /** `RELATION_MERGED` 에만 — 이 관계로 합쳐진 핸들 관계의 핸들. 여러 번 합쳐도 줄이 구별된다. */
  mergedFromHandle: z28.string().nullable().optional(),
  /**
   * 단가 줄이 드는 값 (`PRICE_AGREED` 에만). 사건 payload 에는 금액이 없고
   * (creator-price-relation-ownership.md §3-7) 응답 한 겹이 짝 왕복 행을 조인해 싣는다 —
   * 줄 문법의 「값」 칸을 그대로 쓰기 위해서다(관계 시안 §2.2).
   */
  price: z28.object({
    amount: z28.string(),
    currency: z28.string().length(3),
    proposedBy: z28.enum(ROSTER_PRICE_PROPOSERS)
  }).strict().optional()
}).strict();
var creatorRelationHistoryPageSchema = z28.object({
  items: z28.array(creatorRelationHistoryItemSchema),
  nextCursor: creatorRelationHistoryCursorSchema.nullable(),
  /**
   * 합쳐진 핸들 관계의 id 로 열었을 때만(퀘스트 e261b316) — 합쳐진 핸들·시각과 합쳐 들어간 관계의
   * 핸들. 로스터의 관계 이력 패널이 제목과 안내를 합쳐 들어간 쪽으로 바꾸는 근거다.
   */
  mergedFrom: z28.object({
    handle: z28.string().nullable(),
    mergedAt: Iso8601Schema,
    intoHandle: z28.string().nullable()
  }).strict().optional()
}).strict();
var creatorNotepadEntryResponseSchema = z28.object({
  id: z28.uuid(),
  relationId: z28.uuid(),
  author: creatorRelationPersonSchema,
  body: z28.string(),
  createdAt: Iso8601Schema,
  promotedHistoryId: z28.uuid().nullable()
}).strict();

// src/dto/creator-view-metrics.ts
import { z as z29 } from "zod";
var VIEW_METRICS_FRESHNESS = ["FRESH", "STALE", "PENDING"];
var VIEW_METRICS_LAST_COLLECTION = ["DONE", "FAILED", "NONE"];
var VIEW_METRICS_REFRESH_STATES = ["QUEUED", "RUNNING", "HELD"];
var VIEW_METRICS_COVERAGE = ["COMPLETE", "PARTIAL"];
var VIEW_METRICS_SAMPLE_STATUS = ["SUFFICIENT", "INSUFFICIENT"];
var CREATOR_VIEW_METRICS_IDS_MAX = 200;
var creatorViewMetricsQuerySchema = z29.object({
  ids: z29.preprocess(
    (value) => typeof value === "string" ? [
      ...new Set(
        value.split(",").map((part) => part.trim()).filter((part) => part !== "")
      )
    ] : value,
    z29.array(z29.string().min(1)).min(1).max(CREATOR_VIEW_METRICS_IDS_MAX)
  )
}).strict();
var VIEW_METRICS_DISPLAY_STATES = [
  "AVAILABLE",
  "REFRESHING",
  "COMPUTING",
  "COLLECTION_FAILED",
  "NO_PUBLIC_VIDEOS"
];
function deriveViewMetricsDisplayState(item) {
  if (item.freshness === "PENDING") {
    return item.lastCollection === "FAILED" ? "COLLECTION_FAILED" : "COMPUTING";
  }
  if (item.sampleCount === 0) return "NO_PUBLIC_VIDEOS";
  return item.freshness === "STALE" ? "REFRESHING" : "AVAILABLE";
}
function isViewMetricsSampleInsufficient(item) {
  return item.sampleStatus === "INSUFFICIENT";
}
var VIEW_METRICS_SNAPSHOT_OUTCOMES = ["CAPTURED", "UNAVAILABLE"];
var VIEW_METRICS_SNAPSHOT_CAPTURED_ON = ["ROSTER_JOINED", "RESOLVED"];
var VIEW_METRICS_UNAVAILABLE_REASONS = [
  "SOURCE_UNCONFIGURED",
  "SOURCE_MISSING",
  "SOURCE_ERROR"
];

// src/dto/chat.ts
import { z as z30 } from "zod";
var createChannelSchema = z30.object({
  name: z30.string().min(1).max(100),
  type: z30.enum(["TEAM", "DIRECT", "PUBLIC"]),
  isPrivate: z30.boolean().optional(),
  teamId: z30.string().optional(),
  memberIds: z30.array(z30.string()).optional()
}).strict();
var CHANNEL_DESCRIPTION_MAX_LENGTH = 200;
var updateChannelSchema = z30.object({
  name: z30.string().min(1).max(100).optional(),
  description: z30.string().max(CHANNEL_DESCRIPTION_MAX_LENGTH).nullable().optional()
}).strict().refine((v) => v.name !== void 0 || v.description !== void 0, {
  message: "name \uB610\uB294 description \uC911 \uD558\uB098\uB294 \uD544\uC694\uD569\uB2C8\uB2E4"
});
var transferOwnershipSchema = z30.object({
  targetUserId: z30.string()
}).strict();
var createChannelGroupSchema = z30.object({
  name: z30.string().min(1).max(100)
}).strict();
var updateChannelGroupSchema = z30.object({
  name: z30.string().min(1).max(100).optional(),
  position: z30.number().int().min(0).optional()
}).strict();
var moveChannelToGroupSchema = z30.object({
  groupId: z30.string().nullable().optional()
}).strict();
var setChannelNotifyLevelSchema = z30.object({
  level: z30.enum(CHANNEL_NOTIFY_LEVELS)
}).strict();
var sendMessageAttachmentSchema = z30.object({
  type: z30.enum(["FILE", "IMAGE", "URL"]),
  path: z30.string(),
  fileName: z30.string().optional(),
  fileSize: z30.number().optional(),
  mimeType: z30.string().optional()
}).strict();
var sendMessageSchema = z30.object({
  content: z30.string().min(1).max(MESSAGE_CONTENT_MAX_LENGTH),
  parentId: z30.string().optional(),
  attachments: z30.array(sendMessageAttachmentSchema).optional(),
  mentionedUserIds: z30.array(z30.string()).optional(),
  /** @전체 — 서버가 발신자를 뺀 채널 멤버 전원을 멘션 대상으로 센다. */
  mentionAll: z30.boolean().optional(),
  /** 인용해 답장할 원문. 같은 채널의 메시지여야 한다. */
  quotedMessageId: z30.string().optional()
}).strict();
var inviteMemberSchema = z30.object({
  targetUserId: z30.string()
}).strict();
var kickMemberSchema = z30.object({
  targetUserId: z30.string()
}).strict();
var editMessageSchema = z30.object({
  content: z30.string().min(1).max(MESSAGE_CONTENT_MAX_LENGTH)
}).strict();
var toggleReactionSchema = z30.object({
  emoji: z30.string().max(8).regex(
    /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(\u200D(\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*$/u,
    "emoji must be a valid emoji character"
  )
}).strict();

// src/dto/team.ts
import { z as z31 } from "zod";
var updateTeamSchema = z31.object({
  name: z31.string().optional(),
  type: z31.enum(["department", "task-force", "DEPARTMENT", "TASK_FORCE"]).optional(),
  /**
   * 팀 기본 내부 마감 앞당김(일). 이 팀의 시나리오가 오프셋을 비워두면(null) 상속하는 값.
   * 상한 365 는 시나리오 오버라이드(createScenarioSchema.deadlineOffsetDays)와 같은 게이트 —
   * 한쪽만 열어두면 팀 기본값으로 시나리오 상한을 우회할 수 있다.
   * nullable 아님: 팀 기본값은 상속 대상이 없는 뿌리라 항상 숫자다(미설정 = 0).
   */
  defaultDeadlineOffsetDays: z31.number().int().min(0).max(365).optional(),
  /**
   * 시나리오 퀘스트 자동 수락에 서브 책임자를 포함할지 (ef842774 CP3).
   * 주 책임자는 옵션 무관 항상 자동 수락. 기본 true — 소유권 가드는 오프셋과 동일(팀장 본인 팀만).
   */
  autoAcceptScenarioSubOwners: z31.boolean().optional()
}).strict();
var updateTeamSettingsSchema = updateTeamSchema.pick({ defaultDeadlineOffsetDays: true, autoAcceptScenarioSubOwners: true }).extend({ difficultyPricing: teamDifficultyPricingSchema.nullable().optional() }).strict();
var createDepartmentSchema = z31.object({
  name: z31.string().min(1),
  leaderId: z31.string().optional()
}).strict();
var createTaskForceSchema = z31.object({
  name: z31.string().min(1),
  leaderId: z31.string().optional(),
  /** 만들 때 함께 넣을 구성원. 팀장은 leaderId 로 따로 두므로 여기 넣어도 한 번만 들어간다. */
  memberIds: z31.array(z31.string().min(1)).max(100).optional(),
  purpose: z31.string(),
  startDate: z31.string().min(1),
  expectedEndDate: z31.string().min(1)
}).strict();
var addMemberSchema = z31.object({
  userId: z31.string().min(1)
}).strict();
var transferLeadershipSchema = z31.object({
  newLeaderUserId: z31.string().min(1)
}).strict();
var updateTeamCapabilitiesSchema = z31.object({
  capabilities: z31.array(z31.enum(TeamCapability))
}).strict();

// src/dto/team-ops.ts
import { z as z32 } from "zod";
var teamOpsDateSchema = z32.string().refine(isValidDateOnly, {
  message: "Expected a valid calendar date in YYYY-MM-DD format"
});
var teamOpsReadScopeSchema = z32.enum(["EXECUTIVE_ALL_TEAMS", "LEADER_MEMBER_SCOPED"]);
var teamOpsRosterVisibilitySchema = z32.discriminatedUnion("scope", [
  z32.object({
    scope: z32.literal("EXECUTIVE_ALL_TEAMS")
  }).strict(),
  z32.object({
    scope: z32.literal("LEADER_MEMBER_SCOPED"),
    visibleTeamIds: z32.array(z32.string().min(1))
  }).strict()
]);
var teamOpsRosterQuerySchema = z32.object({
  callerUserId: z32.string().min(1),
  scope: teamOpsReadScopeSchema,
  teamIds: z32.array(z32.string().min(1)).optional()
}).strict();
var teamOpsTeamTypeSchema = z32.enum([TeamType.DEPARTMENT, TeamType.TASK_FORCE]);
var teamOpsTaskForceMetadataSchema = z32.object({
  purpose: z32.string(),
  startDate: teamOpsDateSchema,
  expectedEndDate: teamOpsDateSchema
}).strict();
var teamOpsRosterTeamSchema = z32.object({
  id: z32.string().min(1),
  name: z32.string(),
  leaderUserId: z32.string().min(1).nullable(),
  leaderName: z32.string().nullable(),
  teamType: teamOpsTeamTypeSchema,
  memberCount: z32.number().int().nonnegative(),
  tf: teamOpsTaskForceMetadataSchema.optional()
}).strict();
var teamOpsRosterUserSchema = z32.object({
  id: z32.string().min(1),
  username: z32.string(),
  level: z32.enum([UserLevel.INTERN, UserLevel.ASSOCIATE, UserLevel.MANAGER, UserLevel.EXECUTIVE]),
  roles: z32.array(z32.enum([UserRole.PLAYER, UserRole.OPERATOR, UserRole.ADMIN])),
  status: z32.literal(UserStatus.ACTIVE),
  workType: z32.enum([WorkType.FIXED, WorkType.FREE]).optional(),
  excludeFromAttendance: z32.boolean().optional(),
  hireDate: z32.string().optional()
}).strict();
var teamOpsRosterMembershipSchema = z32.object({
  membershipId: z32.string().min(1),
  teamId: z32.string().min(1),
  teamName: z32.string(),
  teamType: teamOpsTeamTypeSchema,
  teamRole: z32.enum([TeamRole.MEMBER, TeamRole.LEADER]),
  user: teamOpsRosterUserSchema
}).strict();
var teamOpsRosterFiltersSchema = z32.object({
  excludedTeamTypes: z32.tuple([z32.literal(TeamType.CLUB)]),
  excludedUserStatuses: z32.tuple([
    z32.literal(UserStatus.PENDING_OFFBOARD),
    z32.literal(UserStatus.OFFBOARDED),
    z32.literal(UserStatus.PURGED)
  ])
}).strict();
var teamOpsRosterResponseSchema = z32.object({
  visibility: teamOpsRosterVisibilitySchema,
  teams: z32.array(teamOpsRosterTeamSchema),
  memberships: z32.array(teamOpsRosterMembershipSchema),
  filters: teamOpsRosterFiltersSchema
}).strict();
var teamOpsWorklogMonitoringQuerySchema = z32.object({
  teamIds: z32.array(z32.string().min(1)).min(1),
  from: teamOpsDateSchema,
  to: teamOpsDateSchema
}).strict();
var teamOpsWorklogTaskSchema = z32.object({
  id: z32.string().min(1),
  title: z32.string(),
  status: z32.enum(["DONE", "PENDING"]),
  value: z32.number(),
  /**
   * 일지 행이 태스크를 체크포인트로 묶는 축. 없으면 묶음 밖에 선다.
   * 근거: docs/design/work-revisibility.md ③ "행 모양"
   */
  checkpointId: z32.string().nullable(),
  /** 묶음 머리에 그리는 방향 한 줄. */
  checkpointTitle: z32.string().nullable(),
  /**
   * 그 퀘스트 안에서 몇 번째 체크포인트인가(1부터). 화면이 `CP01` 로 그린다.
   * DB `order` 가 아니다 — 0 시작과 1 시작이 섞여 있고 동순위도 있어 정본 정렬의 자리를 쓴다.
   */
  checkpointNumber: z32.number().int().positive().nullable(),
  /**
   * 묶음 머리 칩이 같은 퀘스트인지 가르는 축. 제목이 겹쳐도 이 값으로 판정한다.
   * 체크포인트가 없는 태스크는 묶음 밖에 서므로 칩도 없고 이 값도 null 이다.
   */
  questId: z32.string().nullable(),
  /** 묶음 머리에 그리는 퀘스트 칩 문구 — 태스크 제목이 맥락을 반복하지 않게 여기가 든다. */
  questTitle: z32.string().nullable(),
  /** 왜 칸 — 착수 시점의 판단 한 문장. 있을 때만 둘째 줄을 그린다. */
  memo: z32.string().nullable()
}).strict();
var teamOpsSubmittedWorklogSchema = z32.object({
  date: teamOpsDateSchema,
  status: z32.literal("SUBMITTED"),
  submittedAt: z32.string(),
  revisionCount: z32.number().int().nonnegative(),
  /** 마지막으로 고친 시각(ISO) — 정정 이력 행의 최신 `editedAt`. 고친 적 없으면 null. */
  lastRevisedAt: z32.string().nullable().optional(),
  body: z32.string().optional(),
  summary: z32.string().nullable().optional(),
  /** 요약 재생성 요청 대상 식별자(= WorkLog.id). 미제공(구 응답)이면 재생성 버튼을 노출하지 않는다. */
  workLogId: z32.string().min(1).optional(),
  /**
   * AI 요약 상태 — 'none' | 'pending' | 'done' | 'failed'.
   * WorkLog.summaryStatus 가 Prisma/도메인 모두 자유 문자열이라 여기서도 문자열로 둔다.
   * 열거형으로 좁히면 경계에서만 타입이 갈라져 매퍼가 캐스팅해야 한다.
   */
  summaryStatus: z32.string().optional(),
  tasks: z32.array(teamOpsWorklogTaskSchema).optional(),
  attachmentsCount: z32.number().int().nonnegative().optional(),
  source: z32.literal("WORKLOG"),
  /** 해당 일자 KST 출근 기록 존재 여부 — 미제공(구 응답)은 미확정으로 취급 */
  attended: z32.boolean().optional()
}).strict();
var teamOpsDraftWorklogSchema = z32.object({
  date: teamOpsDateSchema,
  status: z32.literal("DRAFT"),
  submittedAt: z32.null(),
  revisionCount: z32.number().int().nonnegative(),
  body: z32.never().optional(),
  summary: z32.never().optional(),
  workLogId: z32.never().optional(),
  summaryStatus: z32.never().optional(),
  tasks: z32.never().optional(),
  attachmentsCount: z32.never().optional(),
  source: z32.literal("WORKLOG"),
  attended: z32.boolean().optional()
}).strict();
var teamOpsMissingWorklogSchema = z32.object({
  date: teamOpsDateSchema,
  status: z32.literal("NONE"),
  submittedAt: z32.null(),
  revisionCount: z32.literal(0),
  body: z32.never().optional(),
  summary: z32.never().optional(),
  workLogId: z32.never().optional(),
  summaryStatus: z32.never().optional(),
  tasks: z32.never().optional(),
  attachmentsCount: z32.never().optional(),
  source: z32.literal("MISSING_SOURCE_FALLBACK"),
  attended: z32.boolean().optional()
}).strict();
var teamOpsWorklogDaySchema = z32.discriminatedUnion("status", [
  teamOpsSubmittedWorklogSchema,
  teamOpsDraftWorklogSchema,
  teamOpsMissingWorklogSchema
]);
var teamOpsWorklogMonitoringEntrySchema = z32.object({
  teamId: z32.string().min(1),
  userId: z32.string().min(1),
  userName: z32.string(),
  logs: z32.array(teamOpsWorklogDaySchema)
}).strict();
var teamOpsWorklogMonitoringResponseSchema = z32.object({
  teamIds: z32.array(z32.string().min(1)),
  from: teamOpsDateSchema,
  to: teamOpsDateSchema,
  entries: z32.array(teamOpsWorklogMonitoringEntrySchema)
}).strict();
var teamOpsAttendanceBulkQuerySchema = z32.object({
  teamIds: z32.array(z32.string().min(1)).min(1),
  date: teamOpsDateSchema
}).strict();
var teamOpsAttendanceStatusSchema = z32.enum(["PRESENT", "ABSENT", "LEAVE"]);
var teamOpsAttendanceSourceSchema = z32.enum([
  "ATTENDANCE",
  "LEAVE_OVERRIDE",
  "MISSING_SOURCE_FALLBACK"
]);
var teamOpsAttendanceBulkEntrySchema = z32.object({
  teamId: z32.string().min(1),
  userId: z32.string().min(1),
  userName: z32.string(),
  /** 같은 팀에 동명이인이 있을 때만 서버가 채운다. */
  emailLocalPart: z32.string().optional(),
  status: teamOpsAttendanceStatusSchema,
  clockInAt: z32.string().nullable(),
  source: teamOpsAttendanceSourceSchema
}).strict();
var teamOpsAttendanceBulkResponseSchema = z32.object({
  teamIds: z32.array(z32.string().min(1)),
  date: teamOpsDateSchema,
  entries: z32.array(teamOpsAttendanceBulkEntrySchema)
}).strict();

// src/dto/club.ts
import { z as z33 } from "zod";
var createClubSchema = z33.object({
  name: z33.string().min(1).max(120),
  tagline: z33.string().max(200).optional(),
  meetingCycle: z33.enum(["weekly", "biweekly", "monthly"]).optional(),
  description: z33.string().max(2e3).optional(),
  rules: z33.string().max(2e3).optional()
}).strict();
var clubTransferLeadershipSchema = z33.object({
  newLeaderUserId: z33.uuid()
}).strict();

// src/dto/club-poll.ts
import { z as z34 } from "zod";
var createPollSchema = z34.object({
  title: z34.string().min(1).max(200),
  description: z34.string().max(2e3).optional(),
  deadline: Iso8601Schema.optional(),
  isMultiple: z34.boolean().default(false),
  optionContents: z34.array(z34.string().min(1).max(200)).min(1)
}).strict();
var confirmPollSchema = z34.object({
  optionId: z34.uuid(),
  startAt: Iso8601Schema.optional(),
  endAt: Iso8601Schema.optional()
}).strict();
var voteSchema = z34.object({
  optionId: z34.uuid()
}).strict();

// src/dto/club-feed.ts
import { z as z35 } from "zod";
var feedAttachmentInputSchema = z35.object({
  path: z35.string().min(1),
  filename: z35.string().optional(),
  mimeType: z35.string().optional()
}).strict();
var createFeedSchema = z35.object({
  title: z35.string().min(1),
  summary: z35.string().optional(),
  startAt: Iso8601Schema,
  endAt: Iso8601Schema,
  duration: z35.number().int().min(0).optional(),
  attendeeUserIds: z35.array(z35.uuid()),
  attachments: z35.array(feedAttachmentInputSchema).optional()
}).strict();
var updateFeedSchema = z35.object({
  title: z35.string().min(1).optional(),
  summary: z35.string().optional(),
  startAt: Iso8601Schema.optional(),
  endAt: Iso8601Schema.optional(),
  duration: z35.number().int().min(0).optional(),
  attendeeUserIds: z35.array(z35.uuid()).optional(),
  attachments: z35.array(feedAttachmentInputSchema).optional()
}).strict();
var approveFeedCapExceededSchema = z35.object({
  userId: z35.uuid(),
  remaining: z35.number().int().min(0),
  // 승인 전 남은 한도(밀리초)
  exceeded: z35.number().int().min(0)
  // 추가 시 cap 초과분(밀리초)
}).strict();
var approveFeedResponseSchema = z35.object({
  approved: z35.array(z35.uuid()),
  skipped: z35.array(z35.uuid()),
  capExceeded: z35.array(approveFeedCapExceededSchema)
}).strict();

// src/dto/leave.ts
import { z as z36 } from "zod";
var sameLengthAsPaths = (v) => v.attachmentNames === void 0 || v.attachmentNames.length === (v.attachmentPaths?.length ?? 0);
var attachmentNamesMismatch = {
  message: "\uCCA8\uBD80 \uC774\uB984\uC758 \uAC1C\uC218\uAC00 \uCCA8\uBD80 \uD30C\uC77C\uC758 \uAC1C\uC218\uC640 \uB2E4\uB985\uB2C8\uB2E4",
  path: ["attachmentNames"]
};
var createLeaveRequestSchema = z36.object({
  leaveType: z36.enum(LeaveType),
  startDate: Iso8601Schema,
  endDate: Iso8601Schema,
  days: z36.number().min(0.5),
  reason: z36.string().optional(),
  attachmentPaths: z36.array(z36.string()).max(3).optional(),
  attachmentNames: z36.array(z36.string().max(255)).max(3).optional(),
  approverId: z36.uuid().optional(),
  referrerIds: z36.array(z36.uuid()).optional()
}).strict().refine((d) => d.leaveType === LeaveType.SICK || d.approverId !== void 0, {
  message: "\uC2B9\uC778\uAD8C\uC790\uB97C \uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
  path: ["approverId"]
}).refine(sameLengthAsPaths, attachmentNamesMismatch);
var grantLeaveSchema = z36.object({
  userId: z36.uuid(),
  leaveType: z36.enum(LeaveType),
  startDate: Iso8601Schema,
  endDate: Iso8601Schema,
  days: z36.number().min(0.5),
  reason: z36.string().optional()
}).strict();
var adjustBalanceSchema = z36.object({
  amount: z36.number(),
  reason: z36.string().optional()
}).strict();
var adminCancelLeaveSchema = z36.object({
  reason: z36.string().min(1)
}).strict();
var requestProofResubmitSchema = z36.object({
  reason: z36.string().min(1)
}).strict();
var reviewLeaveRequestSchema = z36.object({
  comment: z36.string().optional()
}).strict();
var submitProofSchema = z36.object({
  attachmentPaths: z36.array(z36.string()).max(3).optional(),
  attachmentNames: z36.array(z36.string().max(255)).max(3).optional()
}).strict().refine(sameLengthAsPaths, attachmentNamesMismatch);

// src/dto/upload.ts
import { z as z37 } from "zod";
var ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
];
var ALLOWED_BUCKETS = [
  "proof-attachments",
  "quest-attachments",
  "quest-task-attachments",
  "chat-attachments",
  "bug-attachments",
  "leave-attachments",
  "worklog-attachments",
  "guideline-attachments",
  "club-attachments"
];
var MAX_FILE_SIZE = 10 * 1024 * 1024;
var createSignedUploadUrlSchema = z37.object({
  filename: z37.string(),
  mimeType: z37.enum(ALLOWED_MIME_TYPES, {
    message: `\uD5C8\uC6A9\uB418\uC9C0 \uC54A\uB294 \uD30C\uC77C \uD615\uC2DD\uC785\uB2C8\uB2E4. \uD5C8\uC6A9: ${ALLOWED_MIME_TYPES.join(", ")}`
  }),
  bucket: z37.enum(ALLOWED_BUCKETS, {
    message: `\uD5C8\uC6A9\uB418\uC9C0 \uC54A\uB294 \uC800\uC7A5 \uC704\uCE58\uC785\uB2C8\uB2E4. \uD5C8\uC6A9: ${ALLOWED_BUCKETS.join(", ")}`
  }).optional(),
  fileSize: z37.number().max(MAX_FILE_SIZE, { message: "\uD30C\uC77C \uD06C\uAE30\uB294 10MB\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }).optional()
}).strict();

// src/dto/agent.ts
import { z as z38 } from "zod";
var createAgentSchema = z38.object({
  name: z38.string().min(1).max(100),
  slug: z38.string().max(100).optional(),
  role: z38.string().max(50).optional(),
  description: z38.string().max(500).optional(),
  avatarUrl: z38.url().optional(),
  connectionType: z38.enum([AgentConnectionType.OPENCLAW, AgentConnectionType.API]),
  provider: z38.string().optional(),
  model: z38.string().optional(),
  endpoint: z38.url().optional(),
  config: z38.record(z38.string(), z38.unknown()).optional()
}).strict();
var updateAgentSchema = z38.object({
  name: z38.string().max(100).optional(),
  description: z38.string().max(500).optional(),
  avatarUrl: z38.url().optional(),
  connectionType: z38.enum([AgentConnectionType.OPENCLAW, AgentConnectionType.API]).optional(),
  provider: z38.string().optional(),
  model: z38.string().optional(),
  endpoint: z38.url().optional(),
  status: z38.enum([AgentStatus.ACTIVE, AgentStatus.INACTIVE, AgentStatus.ERROR]).optional(),
  config: z38.record(z38.string(), z38.unknown()).optional()
}).strict();

// src/dto/assistant.ts
import { z as z39 } from "zod";
var agentSlugSchema = z39.string().regex(/^[a-z0-9-]{1,32}$/);
var createConversationSchema = z39.object({
  title: z39.string().max(100).optional(),
  agentSlug: agentSlugSchema.optional()
}).strict();
var updateConversationSchema = z39.object({
  title: z39.string().min(1).max(100).optional(),
  status: z39.enum(["ACTIVE", "CLOSED"]).optional()
}).strict();
var sendAssistantMessageSchema = z39.object({
  content: z39.string().min(1).max(4e3),
  metadata: z39.record(z39.string(), z39.unknown()).optional(),
  agentSlug: agentSlugSchema.optional(),
  fromMessageId: z39.string().uuid().optional(),
  pinnedMessageIds: z39.array(z39.string().uuid()).max(50).optional()
}).strict();
var updateConversationEntitySchema = z39.object({
  entityType: z39.string().optional(),
  entityId: z39.string().optional()
}).strict();
var dokkaebiSignalSchema = z39.object({
  type: z39.string().min(1),
  pattern: z39.string().min(1),
  prediction: z39.string().optional(),
  confidence: z39.number().min(0).max(1),
  suggestedAction: z39.string().optional(),
  metadata: z39.record(z39.string(), z39.unknown()).optional()
}).strict();

// src/dto/realtime.ts
import { z as z40 } from "zod";
var REALTIME_TOKEN_TTL_SECONDS = 3600;
var REALTIME_TOKEN_REFRESH_LEAD_SECONDS = 300;
var realtimeTokenResponseSchema = z40.object({
  token: z40.string().min(1),
  expiresIn: z40.number().int().positive()
}).strict();

// src/dto/notification-opt-in.ts
var NOTIFICATION_OPT_IN_TOPICS = ["ADCODE_QUEST_CREATED"];
function isNotificationOptInTopic(value) {
  return typeof value === "string" && NOTIFICATION_OPT_IN_TOPICS.includes(value);
}

// src/dto/notification.ts
import { z as z41 } from "zod";
var worklogNudgeSchema = z41.object({
  targetUserIds: z41.array(z41.string().uuid()).nonempty(),
  logDate: z41.string().regex(/^\d{4}-\d{2}-\d{2}$/, "logDate must be in YYYY-MM-DD format")
}).strict();

// src/dto/piecework.ts
import { z as z42 } from "zod";
var audienceScopeSchema = z42.enum([PieceworkAudienceScope.TEAM, PieceworkAudienceScope.GLOBAL]);
var cycleSchema = z42.enum([PieceworkCycle.WEEKLY, PieceworkCycle.MONTHLY]);
var createPieceworkActivitySchema = z42.object({
  title: z42.string().max(120),
  description: z42.string().max(2e3).nullable().optional(),
  rewardPerSubmission: z42.number().int().min(1),
  audienceScope: audienceScopeSchema,
  cycle: cycleSchema.nullable().default(null),
  cycleStartAt: Iso8601Schema.nullable().default(null),
  cycleEndAt: Iso8601Schema.nullable().default(null),
  requiresApproval: z42.boolean().default(false),
  maxSubmissionsPerCycle: z42.number().int().positive().nullable().default(null)
}).strict().refine(
  (v) => v.cycle === null ? v.cycleStartAt === null && v.cycleEndAt === null : v.cycleStartAt !== null && v.cycleEndAt !== null,
  { message: "cycleStartAt/cycleEndAt must align with cycle", path: ["cycle"] }
);
var updatePieceworkActivitySchema = z42.object({
  title: z42.string().max(120).optional(),
  description: z42.string().max(2e3).nullable().optional(),
  rewardPerSubmission: z42.number().int().min(1).optional(),
  cycleEndAt: Iso8601Schema.nullable().optional(),
  requiresApproval: z42.boolean().optional(),
  maxSubmissionsPerCycle: z42.number().int().positive().nullable().optional()
}).strict();
var submitPieceworkSchema = z42.object({
  uploadIds: z42.array(z42.uuid()).max(10).default([]),
  memo: z42.string().max(2e3).nullable().optional()
}).strict();
var approvePieceworkSchema = z42.object({
  /** approve | reject — REJECTED는 reason 필수 */
  decision: z42.enum(["APPROVED", "REJECTED"]),
  reason: z42.string().max(500).optional()
}).strict();
var decidePieceworkActivitySchema = z42.object({
  decision: z42.enum(["APPROVED", "REJECTED"]),
  reason: z42.string().min(1).max(500).optional()
}).strict();

// src/dto/point.ts
import { z as z43 } from "zod";
var adjustPointsSchema = z43.object({
  userId: z43.string(),
  // 규칙만 둔다. 화면(`grantPoints.schema.ts`)이 경계값을 읽어 한국어 문구를 덧씌운다(이강주 결정 B, 2026-10-13).
  amount: z43.number().int(),
  note: z43.string().min(1),
  /** 되돌릴 대상 행 (레인 1 복원) */
  correctsId: z43.string().uuid().optional(),
  /** 되돌릴 원거래가 없을 때의 용도 (레인 2 보전) */
  reasonCategory: z43.enum(ADJUST_REASON_CATEGORIES).optional()
}).strict().superRefine((v, ctx) => {
  const hasTarget = v.correctsId !== void 0;
  const hasCategory = v.reasonCategory !== void 0;
  if (v.amount > 0) {
    if (hasTarget && hasCategory) {
      ctx.addIssue({
        code: z43.ZodIssueCode.custom,
        path: ["reasonCategory"],
        message: "\uC815\uC815 \uB300\uC0C1\uACFC \uC0AC\uC720 \uCE74\uD14C\uACE0\uB9AC\uB294 \uD568\uAED8 \uC9C0\uC815\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"
      });
    } else if (!hasTarget && !hasCategory) {
      ctx.addIssue({
        code: z43.ZodIssueCode.custom,
        path: ["correctsId"],
        message: "\uC591\uC218 \uC870\uC815\uC740 \uC815\uC815 \uB300\uC0C1 \uB610\uB294 \uC0AC\uC720 \uCE74\uD14C\uACE0\uB9AC\uB97C \uC9C0\uC815\uD574\uC57C \uD569\uB2C8\uB2E4"
      });
    }
    return;
  }
  if (hasTarget || hasCategory) {
    ctx.addIssue({
      code: z43.ZodIssueCode.custom,
      path: [hasTarget ? "correctsId" : "reasonCategory"],
      message: "\uCC28\uAC10\uC5D0\uB294 \uC815\uC815 \uB300\uC0C1\xB7\uC0AC\uC720 \uCE74\uD14C\uACE0\uB9AC\uB97C \uC9C0\uC815\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"
    });
  }
});
var grantPointsSchema = z43.object({
  userId: z43.string(),
  amount: z43.number().int().min(1),
  note: z43.string().optional()
}).strict();
var POINT_HISTORY_EXPORT_MAX_ROWS = 1e4;
var POINT_TEAM_ATTRIBUTION_IS_POINT_IN_TIME = {
  PAYOUT: true,
  QUEST: true,
  MEMBERSHIP: false,
  MEMBERSHIP_MULTI: false,
  NONE: false
};
var POINT_TEAM_ATTRIBUTION_LABELS = {
  PAYOUT: "\uC815\uC0B0\uAE30\uB85D",
  QUEST: "\uD018\uC2A4\uD2B8\uD300",
  MEMBERSHIP: "\uD604\uC7AC\uC18C\uC18D",
  MEMBERSHIP_MULTI: "\uD604\uC7AC\uC18C\uC18D(\uACB8\uC9C1)",
  NONE: "\uBBF8\uADC0\uC18D"
};
var POINT_TEAM_UNATTRIBUTED = "__UNATTRIBUTED__";
var POINT_TEAM_UNATTRIBUTED_LABEL = "(\uBBF8\uADC0\uC18D)";
function summarizePointTeamAttribution(items) {
  const summary = {
    PAYOUT: { count: 0, amount: 0 },
    QUEST: { count: 0, amount: 0 },
    MEMBERSHIP: { count: 0, amount: 0 },
    MEMBERSHIP_MULTI: { count: 0, amount: 0 },
    NONE: { count: 0, amount: 0 }
  };
  for (const item of items) {
    const bucket = summary[item.attribution];
    bucket.count += 1;
    bucket.amount += item.amount;
  }
  return summary;
}

// src/dto/page-view.ts
import { z as z44 } from "zod";
var createPageViewSchema = z44.object({
  path: z44.string().min(1).max(200)
}).strict();
var queryPageViewStatsSchema = z44.object({
  userId: z44.uuid().optional(),
  startDate: Iso8601Schema.optional(),
  endDate: Iso8601Schema.optional(),
  limit: z44.coerce.number().int().min(1).max(200).default(50)
}).strict();
var PAGE_VIEW_RETENTION_DAYS = 90;

// src/dto/payout.ts
import { z as z45 } from "zod";
var markPaidSchema = z45.object({
  payoutIds: z45.array(z45.string()).min(1)
}).strict();
var payoutVoidReasonSchema = z45.string().min(2).max(200);
var voidPayoutSchema = z45.object({
  reason: payoutVoidReasonSchema
}).strict();
var BULK_VOID_MAX = 100;
var bulkVoidPayoutSchema = z45.object({
  payoutIds: z45.array(z45.string()).min(1).max(BULK_VOID_MAX),
  reason: payoutVoidReasonSchema
}).strict();
var bulkVoidPayoutSkippedSchema = z45.object({
  id: z45.string(),
  reason: z45.string()
}).strict();
var bulkVoidPayoutResponseSchema = z45.object({
  voided: z45.array(z45.string()),
  skipped: z45.array(bulkVoidPayoutSkippedSchema)
}).strict();
var taxYearSchema = z45.coerce.number().int().min(2e3).max(2100);
var taxMonthSchema = z45.coerce.number().int().min(1).max(12);
var taxIncomeSummaryQuerySchema = z45.object({
  userId: z45.uuid(),
  year: taxYearSchema,
  month: taxMonthSchema
}).strict();
var taxIncomeBulkQuerySchema = z45.object({
  year: taxYearSchema,
  month: taxMonthSchema
}).strict();
var TAX_INCOME_BULK_MAX = 500;

// src/dto/rank.ts
import { z as z46 } from "zod";
var updatePointsSchema = z46.object({
  points: z46.number().int("Points must be an integer").min(REWARD_POINTS_MIN, "Points must be at least 1"),
  reason: z46.string().optional()
}).strict();
var LEADERBOARD_PERIODS = ["week", "month", "all"];
var isLeaderboardPeriod = (value) => LEADERBOARD_PERIODS.includes(value);

// src/dto/reservations.ts
import { z as z47 } from "zod";
var createReservationSchema = z47.object({
  roomId: z47.uuid(),
  title: z47.string().max(120),
  startsAt: Iso8601Schema,
  endsAt: Iso8601Schema,
  attendeeUserIds: z47.array(z47.uuid()).default([])
}).strict();
var listReservationsQuerySchema = z47.object({
  roomId: z47.uuid(),
  date: z47.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD")
}).strict();
var RESERVATION_RANGE_MAX_DAYS = 31;
var DAY_MS = 24 * 60 * 60 * 1e3;
var calendarDateSchema = z47.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD").refine(
  (s) => {
    const t = Date.parse(`${s}T00:00:00Z`);
    return !Number.isNaN(t) && new Date(t).toISOString().startsWith(s);
  },
  {
    message: "date must be a calendar date"
  }
);
var listReservationsRangeQuerySchema = z47.object({
  from: calendarDateSchema,
  to: calendarDateSchema
}).strict().refine(({ from, to }) => from <= to, {
  message: "to must not be before from",
  path: ["to"]
}).refine(
  ({ from, to }) => (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS + 1 <= RESERVATION_RANGE_MAX_DAYS,
  { message: `range must be at most ${RESERVATION_RANGE_MAX_DAYS} days`, path: ["to"] }
);
var updateReservationSchema = z47.object({
  title: z47.string().max(120),
  startsAt: Iso8601Schema,
  endsAt: Iso8601Schema
}).strict();
var updateReservationAttendeesSchema = z47.object({
  attendeeUserIds: z47.array(z47.uuid()).default([])
}).strict();

// src/dto/rooms.ts
import { z as z48 } from "zod";
var HH_MM_PATTERN = /^\d{2}:\d{2}$/;
var createRoomSchema = z48.object({
  name: z48.string().min(1).max(120),
  floorLevel: z48.number().int(),
  floorLabel: z48.string().max(40).nullable().optional(),
  capacity: z48.number().int().min(1),
  openTime: z48.string().regex(HH_MM_PATTERN, "openTime must be in HH:mm format").optional(),
  closeTime: z48.string().regex(HH_MM_PATTERN, "closeTime must be in HH:mm format").optional(),
  isActive: z48.boolean().optional(),
  sortOrder: z48.number().int().nullable().optional()
}).strict();
var updateRoomSchema = z48.object({
  name: z48.string().min(1).max(120).optional(),
  floorLevel: z48.number().int().optional(),
  floorLabel: z48.string().max(40).nullable().optional(),
  capacity: z48.number().int().min(1).optional(),
  openTime: z48.string().regex(HH_MM_PATTERN, "openTime must be in HH:mm format").optional(),
  closeTime: z48.string().regex(HH_MM_PATTERN, "closeTime must be in HH:mm format").optional(),
  isActive: z48.boolean().optional(),
  sortOrder: z48.number().int().nullable().optional()
}).strict();

// src/dto/proof.ts
import { z as z49 } from "zod";
var proofAttachmentSchema = z49.object({
  type: z49.enum(["URL", "FILE"]),
  path: z49.string(),
  // metadata.bucket 은 서버가 정성 CP 검토 스냅샷에서만 적는 서명 버킷이다 — 클라이언트 값을
  // 믿으면 다른 비공개 버킷 파일을 증빙 첨부로 꾸며 서명 URL 을 받을 수 있어 입구에서 지운다.
  metadata: z49.record(z49.string(), z49.unknown()).optional().transform((m) => {
    if (!m) return m;
    const { bucket: _bucket, ...rest } = m;
    return rest;
  })
}).strict();
var createProofSchema = z49.object({
  questId: z49.string().optional(),
  checkpointId: z49.string().optional(),
  description: z49.string().max(PROOF_DESCRIPTION_MAX_LENGTH),
  attachments: z49.array(proofAttachmentSchema).optional()
}).strict();
var approveProofSchema = z49.object({
  pointsAwarded: z49.number().min(POINTS_MIN),
  /**
   * 검수 화면이 본 증빙 버전(Proof.contentRevision). 지금 버전과 다르면 409 STALE_REVIEW.
   * 생략하면 비교하지 않는다 — 버전을 모르는 예전 호출(MCP 플러그인)을 끊지 않으려는 것.
   */
  reviewedRevision: z49.number().int().min(0).optional()
}).strict();
var rejectProofSchema = z49.object({
  comment: z49.string().optional()
}).strict();
var updateProofDraftSchema = z49.object({
  description: z49.string().max(PROOF_DESCRIPTION_MAX_LENGTH),
  attachments: z49.array(proofAttachmentSchema).optional(),
  retainedAttachmentIds: z49.array(z49.string().uuid()).optional()
}).strict();
var autoSubmitQuestProofSchema = z49.object({
  questId: z49.uuid()
}).strict();
var autoSubmitCheckpointProofSchema = z49.object({
  checkpointId: z49.uuid()
}).strict();

// src/dto/proof-submission-stats.ts
import { z as z50 } from "zod";
var queryProofSubmissionStatsSchema = z50.object({
  userId: z50.uuid().optional(),
  startDate: Iso8601Schema.optional(),
  endDate: Iso8601Schema.optional()
}).strict();

// src/dto/product.ts
import { z as z51 } from "zod";
var productTargetInputSchema = z51.object({
  kind: z51.enum(["USER", "TEAM"]),
  refId: z51.string().uuid()
}).strict();
var productSubsumeWorkInputSchema = z51.object({
  teamId: z51.string().uuid(),
  work: z51.string().min(1).max(200)
}).strict();
var createProductSchema = z51.object({
  name: z51.string().min(1).max(200),
  description: z51.string().max(2048).nullable().optional(),
  targets: z51.array(productTargetInputSchema).max(20).optional(),
  /** 만들 때 무엇을 없앨지까지 정해져 있는 경우는 드물어 대개 비어 온다. */
  subsumeWorks: z51.array(productSubsumeWorkInputSchema).max(20).optional()
}).strict();
var productPostureSchema = z51.enum(["PRIMARY", "MAINTAIN", "FROZEN"]);
var updateProductSchema = z51.object({
  name: z51.string().min(1).max(200).optional(),
  description: z51.string().max(2048).nullable().optional(),
  /**
   * 주면 대상 사용자 집합을 **통째로 갈아끼운다**. 빈 배열 = 전부 뗌("해당 없음"으로 되돌림),
   * 생략 = 그대로 둠. 추가·삭제 엔드포인트를 따로 두지 않는 이유는 화면이 목록 편집 하나뿐이라
   * 부분 연산이 필요해진 적이 없어서다.
   */
  targets: z51.array(productTargetInputSchema).max(20).optional(),
  /**
   * 삼킬 일 집합도 **통째로 갈아끼운다**. 빈 배열 = 선언 철회, 생략 = 그대로 둠.
   * 태도와 달리 사유를 따로 받지 않는다 — 목록 편집 한 건마다 사유를 물으면 그 칸이
   * 형식적으로 채워지고, 이 선언의 주체·시각은 태도 전이 이력이 이미 지고 있다.
   */
  subsumeWorks: z51.array(productSubsumeWorkInputSchema).max(20).optional(),
  posture: productPostureSchema.optional(),
  postureReason: z51.string().min(1).max(500).optional()
}).strict().refine((v) => v.posture === void 0 || v.postureReason !== void 0, {
  message: "\uD0DC\uB3C4\uB97C \uBC14\uAFB8\uB824\uBA74 \uC0AC\uC720\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4",
  path: ["postureReason"]
}).refine((v) => v.postureReason === void 0 || v.posture !== void 0, {
  message: "\uC0AC\uC720\uB9CC\uC73C\uB85C\uB294 \uC120\uC5B8\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  path: ["posture"]
});

// src/dto/scenario-product.ts
import { z as z52 } from "zod";
var createScenarioProductSchema = z52.object({
  name: z52.string().min(1).max(200),
  description: z52.string().max(2048).nullable().optional(),
  externalUrl: z52.string().max(2048).nullable().optional()
}).strict();
var updateScenarioProductSchema = z52.object({
  name: z52.string().min(1).max(200).optional(),
  description: z52.string().max(2048).nullable().optional(),
  externalUrl: z52.string().max(2048).nullable().optional()
}).strict();

// src/dto/quest-task.ts
import { z as z53 } from "zod";
var QUEST_TASK_TITLE_MAX = 100;
var GATE_RESULT_PATTERN = /(typecheck|eslint|tsc|lint|초록|\d+\s*건\s*통과)/i;
var CASE_COUNT_PATTERN = /\d+\s*케이스/;
var questTaskTitleSchema = z53.string().min(1).max(
  QUEST_TASK_TITLE_MAX,
  `\uC81C\uBAA9\uC740 \uACB0\uACFC \uD55C \uBB38\uC7A5\uC785\uB2C8\uB2E4 \u2014 ${QUEST_TASK_TITLE_MAX}\uC790 \uC774\uB0B4\uB85C \uC904\uC774\uACE0, \uCC29\uC218 \uC2DC\uC810\uC758 \uD310\uB2E8\uC740 \uC65C \uCE78(memo)\uC5D0 \uC801\uC2B5\uB2C8\uB2E4`
).refine((title) => !GATE_RESULT_PATTERN.test(title), {
  message: "\uC81C\uBAA9\uC5D0 \uAC8C\uC774\uD2B8 \uD1B5\uACFC \uC5EC\uBD80\uB97C \uC801\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4 \u2014 \uBB34\uC5C7\uC774 \uD1B5\uACFC\uD588\uB294\uC9C0\uB294 \uCF54\uB4DC\xB7PR\xB7CI \uC5D0 \uC788\uACE0, \uC81C\uBAA9\uC5D0\uB294 \uACB0\uACFC \uD55C \uBB38\uC7A5\uB9CC \uC801\uC2B5\uB2C8\uB2E4"
}).refine((title) => !CASE_COUNT_PATTERN.test(title), {
  message: "\uACA8\uB0E5\uC744 'N\uCF00\uC774\uC2A4' \uB85C \uBB36\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4 \u2014 \uACA8\uB0E5\uC774 \uC5EC\uB7FF\uC774\uBA74 \uD0DC\uC2A4\uD06C\uB97C \uB098\uB208\uB2E4\uB294 \uB73B\uC774\uACE0, \uC81C\uBAA9\uC5D0\uB294 \uACB0\uACFC \uD55C \uBB38\uC7A5\uB9CC \uC801\uC2B5\uB2C8\uB2E4"
});
function questTaskTitleError(title) {
  if (title === "") return null;
  const result = questTaskTitleSchema.safeParse(title);
  return result.success ? null : result.error.issues[0]?.message ?? null;
}
var createQuestTaskSchema = z53.object({
  title: questTaskTitleSchema,
  questId: z53.uuid().nullable().optional(),
  checkpointId: z53.uuid().nullable().optional(),
  value: z53.number().min(1e-4).max(999999).nullable().optional(),
  memo: z53.string().max(120).nullable().optional(),
  rosterEntryId: z53.uuid().nullable().optional()
}).strict().superRefine((body, ctx) => {
  if (body.rosterEntryId == null) return;
  if (body.value != null) {
    ctx.addIssue({
      code: "custom",
      path: ["value"],
      message: "\uBA85\uB2E8 \uD589\uC5D0 \uBD99\uC740 \uD0DC\uC2A4\uD06C\uC5D0\uB294 \uAC12\uC744 \uB458 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"
    });
  }
  if (body.checkpointId != null) {
    ctx.addIssue({
      code: "custom",
      path: ["checkpointId"],
      message: "\uBA85\uB2E8 \uD589\uC5D0 \uBD99\uC740 \uD0DC\uC2A4\uD06C\uB294 \uCCB4\uD06C\uD3EC\uC778\uD2B8\uC5D0 \uBD99\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4"
    });
  }
});
var updateQuestTaskSchema = z53.object({
  title: questTaskTitleSchema.optional(),
  memo: z53.string().max(120).nullable().optional(),
  value: z53.number().min(1e-4).max(999999).nullable().optional(),
  varianceReason: z53.string().max(1e3).nullable().optional()
}).strict();
var completeQuestTaskSchema = z53.object({
  actualValue: z53.number().min(0).max(999999).optional(),
  // 누적 완료 건수. 서버가 같은 CP 의 본인 DONE 합을 빼서 actualValue 를 확정한다(deltaFromCumulative).
  // 시트를 드래그해서 센 값을 그대로 넣는 자리 — 모달·마감·MCP 가 같은 변환을 타도록 서버에 둔다.
  cumulativeValue: z53.number().min(0).max(999999).optional(),
  varianceReason: z53.string().max(1e3).nullable().optional()
}).strict().refine((d) => d.actualValue === void 0 || d.cumulativeValue === void 0, {
  message: "actualValue \uC640 cumulativeValue \uB294 \uB458 \uC911 \uD558\uB098\uB9CC \uBCF4\uB0BC \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  path: ["cumulativeValue"]
});
var closeDaySchema = z53.object({
  doneTaskIds: z53.array(z53.uuid()),
  reflection: z53.string().max(5e3).optional(),
  // taskId → 오늘 실제 진행 수치(부분값). FE가 자연스레 만드는 Record 형태.
  carriedValues: z53.record(z53.uuid(), z53.number().min(0).max(999999)).optional(),
  // taskId → 완료 처리(체크)한 태스크의 실제 작업량. 체크 경로는 status/closeReason 만
  // 찍고 actualValue 를 안 건드려, 계획치 없이 체크인한 정량 태스크가 마감에서 체크되면
  // effectiveTaskValue 가 value(null) 폴백으로 0 이 됐다. carriedValues 와 대칭.
  doneValues: z53.record(z53.uuid(), z53.number().min(0).max(999999)).optional()
}).strict();
var resolveStaleTaskSchema = z53.object({
  resolution: z53.enum(["COMPLETED", "SWEPT"])
}).strict();
var createQuestTaskFeedbackSchema = z53.object({
  body: z53.string().min(1).max(2e3)
}).strict();
var createQuestTaskFeedbackCommentSchema = z53.object({
  body: z53.string().min(1).max(2e3)
}).strict();

// src/dto/quest-question.ts
import { z as z54 } from "zod";
var createQuestQuestionSchema = z54.object({
  body: z54.string().min(1).max(2e3)
}).strict();
var createQuestQuestionCommentSchema = z54.object({
  body: z54.string().min(1).max(2e3)
}).strict();

// src/dto/quest-proposal.ts
import { z as z55 } from "zod";
var createQuestProposalSchema = z55.object({
  teamId: z55.uuid(),
  title: QuestProposalTitleSchema,
  description: QuestProposalDescriptionSchema
}).strict();
var resolveQuestProposalSchema = z55.discriminatedUnion("action", [
  z55.object({
    action: z55.literal("adopt"),
    adoptedQuestId: z55.uuid()
  }).strict(),
  z55.object({
    action: z55.literal("decline"),
    declineReason: z55.string().trim().min(1).max(500)
  }).strict()
]);
var listQuestProposalsQuerySchema = z55.object({
  teamId: z55.uuid().optional(),
  status: z55.enum(["OPEN", "ADOPTED", "DECLINED"]).optional()
}).strict();

// src/dto/task-statistics.ts
var CHECKIN_INTERVAL_GRADE_KEYS = [
  QuestDifficulty.COMMON,
  QuestDifficulty.UNCOMMON,
  QuestDifficulty.RARE,
  QuestDifficulty.EPIC,
  QuestDifficulty.LEGENDARY,
  "UNGRADED",
  "UNLINKED"
];
var CHECKIN_INTERVAL_MIN_SAMPLE = 5;

// src/dto/telemetry-event.ts
import { z as z56 } from "zod";
var CLIENT_TELEMETRY_EVENT_NAMES = [
  "quest_publish_success",
  "quest_publish_abandon",
  "quest_creation_started",
  "quest_creation_submitted",
  "quest_approval_request_succeeded",
  "quest_approval_request_failed",
  "reward_mode_selected",
  "reward_toggle_engaged",
  "distribution_modal_opened",
  "quest_card_expand_toggle",
  "quest_archive_toggle",
  "checkpoint_accordion_toggle",
  "section_accordion_toggle",
  "proof_writing_started",
  "proof_submission_attempted",
  "proof_submit_success",
  "proof_submit_failure",
  /**
   * 목록에서 퀘스트를 골라 상세를 연 행동. `rarity` 와 함께 남긴다.
   *
   * 등급 계단(RarityDot 8~12px, PR #1934)이 실제로 읽히는지 재는 유일한 경로다 —
   * 그 전까지 이 목록에는 등급 축 이벤트가 하나도 없어서 시각 변경의 효과를 관측할
   * 수단 자체가 없었다. 판정은 "선택 분포 vs 발행 분포" 로 한다: LEGENDARY 가
   * 발행의 0.9% 인데 선택에서 그보다 높은 몫을 차지하면 눈에 띈다는 신호다.
   */
  "quest_list_select",
  /**
   * 업무명 검색 결과가 도착한 순간. 검색어마다 한 번, 결과 건수와 검색어 길이만 싣는다.
   *
   * 검색은 지나간 일을 다시 찾는 자리라(`docs/design/work-revisibility.md` ⑤ "다시 찾는 데 걸린
   * 시간과 찾지 못한 사례") 쓰이는지와 찾았는지가 그 효과다. 찾은 사례는 뒤따르는
   * `quest_list_select { searching: true }`, 못 찾은 사례는 그것이 없는 이 이벤트다.
   */
  "quest_search",
  /**
   * 위키 사용 계측. 쓰기는 판(revision)·토론 행으로 사후에 셀 수 있지만 읽기·찾기는 어디에도
   * 남지 않아, 전원 공개(#3243) 뒤 위키가 쓰이는지를 볼 수단이 없었다.
   * - view: 읽기 화면에 문서가 뜬 순간, 문서마다 한 번(`document_id`·`area`)
   * - search: 찾기 결과가 도착한 순간, 검색어마다 한 번(`result_count`·`query_length`)
   * - edit_open / edit_save: 편집기를 연 것과 저장에 성공한 것(`action` = create·document·section).
   *   둘의 차이가 열고 저장하지 않은 편집이다.
   */
  "wiki_document_view",
  "wiki_search",
  "wiki_edit_open",
  "wiki_edit_save",
  /**
   * 오늘 화면 계측. 체크인·체크아웃 횟수와 마감 성공은 태스크 행(createdVia·closeReason)·
   * close_day 감사 행·업무일지 submittedAt 으로 이미 셀 수 있어 다시 보내지 않고, 서버가 못 보는
   * 단계만 남긴다. 그만둔 것을 단정하는 이름(abandon·cancel)은 두지 않는다.
   * - checkin_form_open: 체크인 폼이 있는 카드가 펼쳐진 순간(`quest_id`·`action` = click·deep_link·auto).
   *   실제 체크인은 같은 사람·퀘스트의 createdVia=WEB 태스크 행과 대조한다
   * - close_day_open / confirm: 마감 창을 연 것과 마감을 누른 것
   * - close_day_failure: 실패한 단계(`section` = memo_flush·close_day·work_log_submit)와 `error_type`
   */
  "today_checkin_form_open",
  "today_close_day_open",
  "today_close_day_confirm",
  "today_close_day_failure"
];
var SERVER_TELEMETRY_EVENT_NAMES = [
  "quest_creation_succeeded",
  "quest_creation_failed"
];
var TELEMETRY_EVENT_NAMES = [
  ...CLIENT_TELEMETRY_EVENT_NAMES,
  ...SERVER_TELEMETRY_EVENT_NAMES
];
var telemetryEventNameSchema = z56.enum(TELEMETRY_EVENT_NAMES);
var clientTelemetryEventNameSchema = z56.enum(CLIENT_TELEMETRY_EVENT_NAMES);
var PROOF_SUBMISSION_PATHS = ["inline_v1", "modal_v1"];
var proofSubmissionPathSchema = z56.enum(PROOF_SUBMISSION_PATHS);
var PROOF_FAILURE_STAGES = ["upload", "api"];
var proofFailureStageSchema = z56.enum(PROOF_FAILURE_STAGES);
var telemetryPayloadSchema = z56.object({
  quest_id: z56.string().max(64).nullish(),
  checkpoint_id: z56.string().max(64).nullish(),
  proof_id: z56.string().max(64).nullish(),
  document_id: z56.string().max(64).nullish(),
  /** 위키 문서 분야 키(GENERAL·PAGE 등) */
  area: z56.string().max(32).nullish(),
  /** 아코디언 구획 키 등 화면 내 위치 */
  section: z56.string().max(64).nullish(),
  /** expand·collapse·open·close 같은 토글 결과 상태 */
  action: z56.string().max(32).nullish(),
  /** 퀘스트 공개 범위(TEAM·GLOBAL 등) */
  scope: z56.string().max(32).nullish(),
  /**
   * 퀘스트 등급(소문자 `common`~`legendary`). 개인을 지목하지 않는 분기값이라 허용 기준에 든다.
   * **null 을 버리지 않는다** — `difficulty` 미설정 비율 자체가 재야 할 값이다(추정으로 메우지 않는다).
   */
  rarity: z56.string().max(32).nullish(),
  /** Error.name 만. message·stack 은 싣지 않는다 */
  error_type: z56.string().max(64).nullish(),
  reward_points: z56.number().nullish(),
  cp_count: z56.number().nullish(),
  has_deadline: z56.boolean().nullish(),
  /** 검색어가 있는 상태에서 일어난 행동인가 */
  searching: z56.boolean().nullish(),
  result_count: z56.number().nullish(),
  /** 검색어 원문은 태스크 제목이 자유 텍스트로 쌓이므로 싣지 않고 길이만 */
  query_length: z56.number().nullish(),
  creation_attempt_id: z56.uuid().nullish(),
  proof_attempt_id: z56.uuid().nullish(),
  submission_id: z56.uuid().nullish(),
  creation_path: z56.literal("web_form_v1").nullish(),
  proof_path: proofSubmissionPathSchema.nullish(),
  failure_stage: proofFailureStageSchema.nullish()
});
var createTelemetryEventSchema = z56.object({
  name: clientTelemetryEventNameSchema,
  payload: telemetryPayloadSchema.default({})
}).strict();
var TELEMETRY_EVENT_RETENTION_DAYS = 90;
var queryTelemetryEventStatsSchema = z56.object({
  name: telemetryEventNameSchema.optional(),
  userId: z56.uuid().optional(),
  startDate: Iso8601Schema.optional(),
  endDate: Iso8601Schema.optional(),
  limit: z56.coerce.number().int().min(1).max(200).default(50)
}).strict();
var queryQuestCreationStatsSchema = z56.object({
  userId: z56.uuid().optional(),
  startDate: Iso8601Schema.optional(),
  endDate: Iso8601Schema.optional()
}).strict();

// src/dto/telemetry-observation.ts
import { z as z57 } from "zod";
var telemetryObservationMetricSchema = z57.enum(["QUEST_CREATION", "EVENT_COUNTS"]);
var telemetryObservationStatusSchema = z57.enum(["OPEN", "CLOSED"]);
var telemetryObservationOutcomeSchema = z57.enum([
  "CONTINUE",
  "PROPOSE_QUEST",
  "KEEP_INSTRUMENTATION",
  "RETIRE_INSTRUMENTATION"
]);
var timestamp = z57.iso.datetime({ offset: true });
var condition = z57.string().trim().min(1).max(2e3);
var createTelemetryObservationQuestionSchema = z57.strictObject({
  question: z57.string().trim().min(1).max(500),
  startDate: timestamp,
  reviewAt: timestamp,
  reviewCondition: condition,
  metric: telemetryObservationMetricSchema,
  eventName: telemetryEventNameSchema.optional()
}).superRefine((value, ctx) => {
  if (new Date(value.reviewAt) <= new Date(value.startDate)) {
    ctx.addIssue({
      code: "custom",
      path: ["reviewAt"],
      message: "\uC7AC\uD655\uC778 \uC2DC\uAC01\uC740 \uAD00\uCC30 \uC2DC\uC791 \uC2DC\uAC01 \uC774\uD6C4\uC5EC\uC57C \uD569\uB2C8\uB2E4"
    });
  }
  if (value.metric === "QUEST_CREATION" && value.eventName !== void 0) {
    ctx.addIssue({
      code: "custom",
      path: ["eventName"],
      message: "\uC774\uBCA4\uD2B8 \uD544\uD130\uB294 \uBC1C\uC0DD\uB7C9 \uC9D1\uACC4\uC5D0\uB9CC \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4"
    });
  }
});
var queryTelemetryObservationQuestionsSchema = z57.strictObject({
  dueOnly: z57.enum(["true", "false"]).transform((value) => value === "true").optional()
});
var reviewFields = {
  expectedVersion: z57.number().int().positive().max(2147483646),
  rationale: condition
};
var createTelemetryObservationReviewSchema = z57.discriminatedUnion("outcome", [
  z57.strictObject({
    ...reviewFields,
    outcome: z57.literal("CONTINUE"),
    nextReviewAt: timestamp,
    nextReviewCondition: condition
  }),
  z57.strictObject({
    ...reviewFields,
    outcome: z57.enum(["PROPOSE_QUEST", "KEEP_INSTRUMENTATION", "RETIRE_INSTRUMENTATION"])
  })
]);
var telemetryObservationQuestionSchema = z57.object({
  id: z57.uuid(),
  creatorId: z57.uuid().nullable(),
  question: z57.string(),
  startDate: timestamp,
  reviewAt: timestamp,
  reviewCondition: z57.string(),
  metric: telemetryObservationMetricSchema,
  eventName: z57.string().min(1).max(200).nullable(),
  status: telemetryObservationStatusSchema,
  version: z57.number().int().positive(),
  createdAt: timestamp,
  updatedAt: timestamp
});

// src/dto/telemetry-observation-review.ts
import { z as z58 } from "zod";
var timestamp2 = z58.iso.datetime({ offset: true });
var count = z58.number().int().nonnegative();
var observationWindow = z58.object({
  startDate: timestamp2,
  endDate: timestamp2,
  retentionLimited: z58.boolean()
});
var telemetryObservationQuerySchema = z58.object({
  metric: telemetryObservationMetricSchema,
  startDate: timestamp2,
  endDate: timestamp2,
  eventName: z58.string().min(1).max(200).optional()
});
var telemetryObservationSnapshotSchema = z58.union([
  z58.object({
    observationWindow: observationWindow.extend({
      truncated: z58.boolean(),
      observedRowCount: count
    }),
    attempts: z58.object({
      started: count,
      submitted: count,
      succeeded: count,
      failedWithoutObservedSuccess: count
    }),
    startedCohort: z58.object({
      submitted: count,
      succeeded: count,
      failedWithoutObservedSuccess: count
    }),
    submissions: z58.object({ total: count, retries: count }),
    approvalRequests: z58.object({
      clientObservedSucceeded: count,
      clientObservedFailedWithoutSuccess: count
    })
  }),
  z58.object({
    observationWindow,
    rows: z58.array(z58.object({ name: z58.string().min(1).max(200), count, uniqueUsers: count }))
  })
]);
var telemetryObservationReviewSchema = z58.object({
  id: z58.uuid(),
  questionId: z58.uuid(),
  reviewerId: z58.uuid().nullable(),
  questionVersion: z58.number().int().positive(),
  outcome: telemetryObservationOutcomeSchema,
  rationale: z58.string(),
  reviewCondition: z58.string(),
  query: telemetryObservationQuerySchema,
  snapshot: telemetryObservationSnapshotSchema,
  nextReviewAt: timestamp2.nullable(),
  nextReviewCondition: z58.string().nullable(),
  createdAt: timestamp2
});
var telemetryObservationQuestionDetailSchema = telemetryObservationQuestionSchema.extend({
  reviews: z58.array(telemetryObservationReviewSchema),
  reviewsTruncated: z58.boolean()
});

// src/dto/monthly-retro.ts
var MONTHLY_RETRO_UNDATED = "undated";
var MONTHLY_RETRO_STATUSES = [
  "COMPLETED",
  "LOCKED",
  "ARCHIVED",
  "FAILED"
];
var MONTHLY_RETRO_METRIC_KEYS = [
  "taskCount",
  "taskValue",
  "varianceReason",
  "carriedOrSwept",
  "continuation"
];

// src/dto/scenario.ts
import { z as z59 } from "zod";
var ScenarioReferenceSchema = z59.discriminatedUnion("status", [
  z59.object({ status: z59.literal("AVAILABLE"), id: z59.string().uuid(), title: z59.string() }).strict(),
  z59.object({ status: z59.literal("UNAVAILABLE") }).strict()
]);
var STOP_RULE_THRESHOLD_MIN = 1;
var STOP_RULE_THRESHOLD_MAX = SCENARIO_TARGET_TOTAL_VIEWS_MAX;
var stopRuleSchema = z59.object({
  metric: z59.enum([StopRuleMetric.VIEWS, StopRuleMetric.FOLLOWERS]),
  threshold: z59.number().int().min(STOP_RULE_THRESHOLD_MIN).max(STOP_RULE_THRESHOLD_MAX),
  direction: z59.enum([StopRuleDirection.BELOW, StopRuleDirection.ABOVE])
}).strict();
var stopRulesSchema = z59.array(stopRuleSchema).max(STOP_RULES_MAX).refine((rules) => new Set(rules.map((r) => r.metric)).size === rules.length, {
  message: "\uAC19\uC740 \uC911\uB2E8 \uAE30\uC900\uC744 \uB450 \uBC88 \uB123\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
});
var contractTermsShape = {
  contractSlotCount: z59.number().int().min(SCENARIO_CREATOR_COUNT_MIN).max(SCENARIO_CREATOR_COUNT_MAX).nullable().optional(),
  requiredFollowerCount: z59.number().int().min(1).nullable().optional(),
  advertiserSelectsRoster: z59.boolean().optional(),
  productShipping: z59.boolean().optional(),
  requirementNote: z59.string().nullable().optional(),
  difficultyConditions: z59.array(z59.enum(CAMPAIGN_DIFFICULTY_CONDITIONS)).optional(),
  campaignDifficulty: z59.enum([CampaignDifficulty.NORMAL, CampaignDifficulty.HARD, CampaignDifficulty.VERY_HARD]).optional()
};
var refineCampaignDifficulty = (value, ctx) => {
  if (value.campaignDifficulty === void 0) return;
  const derived = deriveCampaignDifficulty(value.difficultyConditions ?? []);
  if (derived !== value.campaignDifficulty) {
    ctx.addIssue({
      code: "custom",
      path: ["campaignDifficulty"],
      message: `\uCCB4\uD06C\uB41C \uC870\uAC74\uC73C\uB85C\uB294 ${derived} \uC785\uB2C8\uB2E4. \uC870\uAC74 \uBAA9\uB85D\uACFC \uB09C\uC774\uB3C4\uAC00 \uC5B4\uAE0B\uB0A9\uB2C8\uB2E4.`
    });
  }
};
var campaignSheetUrlSchema = z59.string().trim().refine((url) => url === "" || url.startsWith("https://docs.google.com/spreadsheets/"), {
  message: "\uCEA0\uD398\uC778 \uC2DC\uD2B8\uC5D0\uB294 Google \uC2A4\uD504\uB808\uB4DC\uC2DC\uD2B8 \uC8FC\uC18C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694."
}).transform((url) => url || null).nullable().optional();
var createScenarioSchema = z59.object({
  ...contractTermsShape,
  /**
   * 광고주 확인 방식의 초기값 (섭외 IA §5, 퀘스트 5b8ba30f). 생략 = 기본값 `보고 후 진행`.
   * 수정 DTO 는 초안(DRAFT)에서만 받는다(23baa0d6) — 발행 뒤에 바꾸는 길은 이력이 남는 전용 경로 하나다.
   */
  advertiserReviewMode: z59.enum(ADVERTISER_REVIEW_MODES).optional(),
  /**
   * 기본값이 아닌 확인 방식을 고른 이유(선택, d49243c1). 상한은 전용 변경 사유와 같은 500자.
   * 공란·기본값은 도메인이 null 로 접는다 — 여기서 거부하지 않는다(발행을 막지 않는다).
   */
  advertiserReviewModeInitialReason: z59.string().trim().max(500).nullable().optional(),
  title: z59.string(),
  description: z59.string().optional(),
  teamIds: z59.array(z59.string()).min(1),
  advertiserId: z59.uuid(),
  /** 광고주 아래 브랜드(선택). 서버가 같은 광고주의 브랜드인지 본다. */
  brandId: z59.uuid().nullable().optional(),
  creatorCount: z59.number().int().min(1).max(9999).optional(),
  // 계약 합계 조회수 목표. `min(1)` 이 "목표 없음은 0 이 아니라 생략(null)" 을 강제한다 —
  // 0 을 통과시키면 진척률 분모가 0 이 되어 화면이 조용히 NaN 을 그린다.
  // 상한을 두지 않는 이유: creatorCount 의 9999 는 사람 수라 현실 상한이 있지만 조회수에는 없다.
  targetTotalViews: z59.number().int().min(1).max(SCENARIO_TARGET_TOTAL_VIEWS_MAX).nullable().optional(),
  // 부스팅 중단 기준. 생략·null·[] = 기준 없는 캠페인(정상 경로).
  stopRules: stopRulesSchema.nullable().optional(),
  startsAt: Iso8601Schema.optional(),
  /** 대조용. 도메인이 `targetCountries` 에서 파생한 값과 다르면 거부한다 */
  campaignType: z59.enum([ScenarioCampaignType.DOMESTIC, ScenarioCampaignType.GLOBAL]).optional(),
  /** 국내도 ['KR'] 을 명시한다. 생략 = 국내(도메인 기본값), 빈 배열 = 미입력이라 거부 */
  targetCountries: z59.array(z59.string().regex(/^[A-Z]{2}$/)).min(1).optional(),
  guidelineUrl: z59.string().optional(),
  campaignSheetInternalUrl: campaignSheetUrlSchema,
  campaignSheetExternalUrl: campaignSheetUrlSchema,
  guidelineNote: z59.string().optional(),
  isSequential: z59.boolean().optional(),
  deadline: Iso8601Schema.optional(),
  advertiserDeadline: Iso8601Schema.optional(),
  internalDeadline: Iso8601Schema.optional(),
  /** 생략 = 팀 기본값(Team.defaultDeadlineOffsetDays) 상속. 숫자면 그 값이 팀 기본값을 이긴다. */
  deadlineOffsetDays: z59.number().int().min(0).max(365).optional(),
  internalDeadlinePinned: z59.boolean().optional(),
  products: z59.array(createScenarioProductSchema).optional(),
  round: z59.number().int().optional(),
  /**
   * 임시저장 — true 면 `status=DRAFT` 로 남기고 활성화 체이닝·kickoff 이벤트·slotNumber 부여를
   * 모두 건너뛴다. 생략/false 는 기존 동작(즉시 ACTIVE) 그대로.
   * plan 2026-08-14T04-51-41Z-a3f21c7d T1-1.
   */
  asDraft: z59.boolean().optional()
}).strict().superRefine(refineCampaignDifficulty);
var updateScenarioSchema = z59.object({
  title: z59.string().optional(),
  description: z59.string().optional(),
  campaignSheetInternalUrl: campaignSheetUrlSchema,
  campaignSheetExternalUrl: campaignSheetUrlSchema,
  /** 캠페인 시작일. 초안(DRAFT)에서만 받는다 — 발행 뒤에는 캠페인 타임라인이 바꾼다. */
  startsAt: Iso8601Schema.optional(),
  deadline: Iso8601Schema.optional(),
  advertiserDeadline: Iso8601Schema.optional(),
  internalDeadline: Iso8601Schema.optional(),
  /**
   * null = 시나리오 오버라이드 해제 → 팀 기본값 상속으로 복귀 (편집 화면의 빈칸).
   * 하한·상한이 create 와 어긋나 있었다(update 만 음수 통과) — 같은 게이트로 맞춘다.
   */
  deadlineOffsetDays: z59.number().int().min(0).max(365).nullable().optional(),
  internalDeadlinePinned: z59.boolean().optional(),
  // 도메인 UpdateScenarioParams.round 는 `number | null`(해제 허용)인데 .nullable() 이 빠져
  // FE 의 회차 해제(null 전송)가 400 으로 죽고 있었다. 도메인 계약에 맞춰 복구.
  round: z59.number().int().nullable().optional(),
  creatorCount: z59.number().int().min(1).max(9999).optional(),
  /**
   * 계약 합계 조회수 목표. null = 해제(빈칸), undefined = 미변경.
   * creatorCount 와 달리 nullable 인 이유 — 조회수 조항이 없는 계약이 정상이다.
   */
  targetTotalViews: z59.number().int().min(1).max(SCENARIO_TARGET_TOTAL_VIEWS_MAX).nullable().optional(),
  /**
   * 목표 조회수를 바꾸는 사유 (cb097c4f). 조회수 실적(측정된 애드코드)이 있는 캠페인에서 목표를
   * 바꿀 때만 서버가 요구하고(없으면 409 `TARGET_VIEWS_CHANGE_REASON_REQUIRED`), 그때 개정
   * 이력에 함께 남긴다. 실적이 없거나 목표가 그대로면 무시한다 — 필요 여부는 서버가 판정한다.
   */
  targetTotalViewsChangeReason: z59.string().trim().min(1).max(500).optional(),
  /**
   * 부스팅 중단 기준. null·[] = 해제(기준 없음), undefined = 미변경.
   * 부분 수정은 없다 — 목록을 통째로 교체한다.
   */
  stopRules: stopRulesSchema.nullable().optional(),
  /** 진행 책임자 지정·이전·회수. null = 회수 (선례: bug-report assigneeId). */
  progressOwnerId: z59.uuid().nullable().optional(),
  /**
   * 진행 서브 책임자 전체 교체 (ef842774 CP4). undefined = 미변경, [] = 전원 해제.
   * 주 책임자와 달리 null 이 없는 이유 — 집합이라 "회수"가 빈 배열로 표현된다.
   */
  progressSubOwnerIds: z59.array(z59.uuid()).optional(),
  /**
   * 아래 넷은 **DRAFT 전용** — 임시저장 중인 초안의 캠페인 성격을 고쳐 저장하기 위한 필드다.
   * ACTIVE 시나리오에 넘기면 도메인 `updateScenario` 가 throw 한다.
   * plan 2026-08-14T04-51-41Z-a3f21c7d T1-2.
   */
  campaignType: z59.enum([ScenarioCampaignType.DOMESTIC, ScenarioCampaignType.GLOBAL]).optional(),
  targetCountries: z59.array(z59.string().regex(/^[A-Z]{2}$/)).min(1).optional(),
  isSequential: z59.boolean().optional(),
  /**
   * 광고주 교체 — 위 셋과 같은 DRAFT 전용 축이다. 초안이 1인당 1개라 새로 쓴 입력도 같은 행에
   * 실리는데, 광고주만 못 따라오면 발행 결과가 옛 광고주로 나간다.
   * 사용 가능 여부(휴면·병합·시스템)는 서비스가 create 와 같은 가드로 본다.
   */
  advertiserId: z59.uuid().optional(),
  /**
   * 브랜드 교체. null 은 비우기. 광고주만 바꾸고 이 값을 안 보내면 서버가 브랜드를 비운다 —
   * 옛 광고주의 브랜드가 남으면 둘이 어긋난다.
   */
  brandId: z59.uuid().nullable().optional(),
  /**
   * 광고주 확인 방식 — 위 넷과 같은 DRAFT 전용 축이다(23baa0d6). 초안 단계의 값은 생성 초기값이라
   * 변경 이력을 남기지 않고, ACTIVE 에 넘기면 도메인 `updateScenario` 가 throw 한다.
   */
  advertiserReviewMode: z59.enum(ADVERTISER_REVIEW_MODES).optional(),
  /** 확인 방식의 최초 사유(d49243c1) — 방식과 같은 DRAFT 전용 축. null = 비움, 생략 = 미변경. */
  advertiserReviewModeInitialReason: z59.string().trim().max(500).nullable().optional(),
  ...contractTermsShape
}).strict().superRefine(refineCampaignDifficulty);
var reorderQuestsSchema = z59.object({
  questIds: z59.array(z59.string()).min(1)
}).strict();
var requestAdcodeSchema = z59.object({
  sheetUrl: z59.url().refine((v) => /^https?:\/\//i.test(v), {
    message: "http(s) URL\uB9CC \uD5C8\uC6A9\uB429\uB2C8\uB2E4"
  }).optional(),
  description: z59.string().max(2048).nullable().optional()
}).strict();
var requestVaSchema = z59.object({
  title: z59.string().trim().min(1, { message: "\uC791\uC5C5 \uC81C\uBAA9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694" }).max(200),
  description: z59.string().max(2048).nullable().optional(),
  // sheetUrl 은 선택 — FE 가 미입력 시 null 을 실어 보내므로 nullable(ADCODE 는 필수라 optional 만).
  sheetUrl: z59.url().refine((v) => /^https?:\/\//i.test(v), {
    message: "http(s) URL\uB9CC \uD5C8\uC6A9\uB429\uB2C8\uB2E4"
  }).nullable().optional()
}).strict();
var rejectAdcodeRequestSchema = z59.object({
  comment: z59.string().max(2048).optional()
}).strict();
var createAdcodeCommentSchema = z59.object({
  body: z59.string().trim().min(1).max(ADCODE_REQUEST_COMMENT_MAX_LENGTH)
}).strict();
var updateScenarioOutreachFormSchema = z59.object({
  url: z59.string().max(2e3).nullable()
}).strict();

// src/dto/scenario-acceptance.ts
import { z as z60 } from "zod";
var acceptanceScenarioRefSchema = z60.object({
  scenarioId: z60.string(),
  title: z60.string(),
  code: z60.number()
}).strict();
var scenarioAcceptanceRowSchema = z60.object({
  userId: z60.string(),
  name: z60.string(),
  /**
   * 병행 중인 퀘스트 **건수** — `ongoing` 과 같은 술어(`ACCEPTED` × `quest.status='ACTIVE'`,
   * 이 시나리오 제외)를 쓰되 **시나리오로 접지 않고 퀘스트 단위로 센다.**
   *
   * 그래서 `ongoing.length` 와 다를 수 있고, 그 차이가 곧 정보다 —
   * ① 한 캠페인에서 퀘스트를 여러 개 집으면 캠페인 1개에 건수 2 이상
   * ② 시나리오가 없는 퀘스트(애드코드·VA)는 이름을 낼 자리가 없어 `ongoing` 에는 안 뜨지만
   *    실제 부하라서 건수에는 잡힌다
   *
   * ⚠ 팀 운영 로스터의 `TeamWorkloadMemberRow.questsInProgress` 와 **같은 수가 아니다.**
   * 그쪽은 `status='ACCEPTED'` 만 보고 퀘스트 상태를 안 봐서(`quest-query.service.ts`)
   * 분배가 끝난 `LOCKED` 퀘스트까지 세고, 시나리오 스코프도 없다.
   */
  ongoingQuestCount: z60.number(),
  ongoing: z60.array(acceptanceScenarioRefSchema),
  previous: acceptanceScenarioRefSchema.nullable(),
  /** 직전 캠페인의 종료 시각. 없으면 null. `previous` 와 항상 함께 null 이다. */
  previousEndedAt: Iso8601Schema.nullable()
}).strict();
var scenarioAcceptancePanelSchema = z60.object({
  scenarioId: z60.string(),
  rows: z60.array(scenarioAcceptanceRowSchema)
}).strict();

// src/dto/scenario-template.ts
import { z as z61 } from "zod";
var scenarioTemplateQuestItemSchema = z61.object({
  title: z61.string().min(1),
  description: z61.string().nullable().optional(),
  difficulty: z61.string().nullable().optional(),
  rewardPoints: z61.number().int().min(0).optional(),
  estimatedHours: z61.number().int().nullable().optional(),
  defaultAssigneeId: z61.uuid().nullable().optional(),
  orderIndex: z61.number().int().min(0).optional()
}).strict();
var createScenarioTemplateSchema = z61.object({
  name: z61.string().min(1),
  description: z61.string().nullable().optional(),
  ownerTeamId: z61.uuid(),
  defaultAdvertiserId: z61.uuid().nullable().optional(),
  defaultCreatorCount: z61.number().int().min(SCENARIO_TEMPLATE_CREATOR_COUNT_MIN).max(SCENARIO_TEMPLATE_CREATOR_COUNT_MAX),
  defaultDurationDays: z61.number().int().min(SCENARIO_TEMPLATE_DURATION_DAYS_MIN),
  defaultGuidelineUrl: z61.string().nullable().optional(),
  defaultCampaignType: z61.enum([ScenarioCampaignType.DOMESTIC, ScenarioCampaignType.GLOBAL]).optional(),
  defaultTargetRegion: z61.array(z61.string().regex(/^[A-Z]{2}$/)).max(SCENARIO_TEMPLATE_TARGET_REGION_MAX).optional(),
  defaultIsSequential: z61.boolean().optional(),
  defaultDescription: z61.string().nullable().optional(),
  teamIds: z61.array(z61.uuid()).optional(),
  questItems: z61.array(scenarioTemplateQuestItemSchema).optional()
}).strict();
var updateScenarioTemplateSchema = z61.object({
  name: z61.string().min(1).optional(),
  description: z61.string().nullable().optional(),
  defaultAdvertiserId: z61.uuid().nullable().optional(),
  defaultCreatorCount: z61.number().int().min(SCENARIO_TEMPLATE_CREATOR_COUNT_MIN).max(SCENARIO_TEMPLATE_CREATOR_COUNT_MAX).optional(),
  defaultDurationDays: z61.number().int().min(SCENARIO_TEMPLATE_DURATION_DAYS_MIN).optional(),
  defaultGuidelineUrl: z61.string().nullable().optional(),
  defaultCampaignType: z61.enum([ScenarioCampaignType.DOMESTIC, ScenarioCampaignType.GLOBAL]).optional(),
  defaultTargetRegion: z61.array(z61.string().regex(/^[A-Z]{2}$/)).max(SCENARIO_TEMPLATE_TARGET_REGION_MAX).optional(),
  defaultIsSequential: z61.boolean().optional(),
  defaultDescription: z61.string().nullable().optional(),
  teamIds: z61.array(z61.uuid()).optional(),
  questItems: z61.array(scenarioTemplateQuestItemSchema).optional()
}).strict();
var instantiateTemplateSchema = z61.object({
  advertiserId: z61.uuid(),
  startsAt: z61.string(),
  creatorCount: z61.number().int().min(SCENARIO_TEMPLATE_CREATOR_COUNT_MIN).max(SCENARIO_TEMPLATE_CREATOR_COUNT_MAX).optional(),
  additionalTeamIds: z61.array(z61.uuid()).optional(),
  titleOverride: z61.string().min(1).optional()
}).strict();
var saveAsTemplateSchema = z61.object({
  ownerTeamId: z61.uuid(),
  nameOverride: z61.string().min(1).optional()
}).strict();

// src/dto/scenario-operational-signal.ts
import { z as z62 } from "zod";
var NOTE_MAX_LENGTH = 200;
var OTHER_DETAIL_MAX_LENGTH = 60;
var createOperationalIssueReportSchema = z62.object({
  area: z62.enum([
    ScenarioOperationalIssueArea.RECRUITING,
    ScenarioOperationalIssueArea.CONTENT_DELIVERY
  ]),
  reason: z62.enum([
    ScenarioRecruitingIssueReason.NO_RESPONSE,
    ScenarioRecruitingIssueReason.NEGOTIATION_STALLED,
    ScenarioRecruitingIssueReason.OTHER,
    ScenarioContentDeliveryIssueReason.DELIVERY_DELAY,
    ScenarioContentDeliveryIssueReason.REVISION_LOOP
  ]),
  helpNeeded: z62.boolean(),
  reasonOtherDetail: z62.string().max(OTHER_DETAIL_MAX_LENGTH).optional(),
  note: z62.string().max(NOTE_MAX_LENGTH).optional()
}).strict();
var updateIssueReportStatusSchema = z62.object({
  status: z62.enum([
    ScenarioOperationalIssueReportStatus.RESOLVED,
    ScenarioOperationalIssueReportStatus.CANCELLED
  ])
}).strict();
var createOperationalReportCommentSchema = z62.object({
  body: z62.string().trim().min(1).max(SCENARIO_OPERATIONAL_ISSUE_REPORT_COMMENT_MAX_LENGTH)
}).strict();
var DROP_COUNT_MIN = 1;
var DROP_COUNT_MAX = 99;
var REASON_MAX_LENGTH = 80;
var createDropSignalSchema = z62.object({
  count: z62.number().int().min(DROP_COUNT_MIN).max(DROP_COUNT_MAX),
  replacementRequired: z62.boolean(),
  reason: z62.string().max(REASON_MAX_LENGTH).optional(),
  note: z62.string().max(NOTE_MAX_LENGTH).optional()
}).strict();
var updateDropSignalStatusSchema = z62.object({
  status: z62.enum([
    ScenarioOperationalIssueReportStatus.RESOLVED,
    ScenarioOperationalIssueReportStatus.CANCELLED
  ])
}).strict();

// src/dto/scenario-timeline.ts
import { z as z63 } from "zod";
var timelineEventMetadataSchema = z63.looseObject(
  {}
);
var createTimelineEventSchema = z63.object({
  type: z63.enum(TimelineEventType),
  body: z63.string().optional(),
  metadata: timelineEventMetadataSchema.optional(),
  questId: z63.uuid().optional(),
  occurredAt: Iso8601Schema
}).strict();
var updateTimelineEventSchema = z63.object({
  type: z63.enum(TimelineEventType).optional(),
  body: z63.string().nullable().optional(),
  metadata: timelineEventMetadataSchema.optional(),
  questId: z63.uuid().nullable().optional()
}).strict();

// src/dto/quest-template.ts
import { z as z64 } from "zod";
var createTemplateCheckpointSchema = z64.object({
  title: z64.string(),
  description: z64.string().nullable().optional(),
  rewardRatio: z64.number().min(0).max(1),
  position: z64.number().min(0)
}).strict();
var createQuestTemplateSchema = z64.object({
  name: z64.string(),
  title: z64.string(),
  description: z64.string().nullable().optional(),
  scope: z64.enum([QuestScope.TEAM, QuestScope.GLOBAL, QuestScope.SYSTEM]),
  targetType: z64.enum([QuestTargetType.USER, QuestTargetType.TEAM]),
  rewardPoints: z64.number().min(0),
  checkpoints: z64.array(createTemplateCheckpointSchema).optional()
}).strict();
var updateQuestTemplateSchema = z64.object({
  name: z64.string().optional(),
  title: z64.string().optional(),
  description: z64.string().nullable().optional(),
  scope: z64.enum([QuestScope.TEAM, QuestScope.GLOBAL, QuestScope.SYSTEM]).optional(),
  targetType: z64.enum([QuestTargetType.USER, QuestTargetType.TEAM]).optional(),
  rewardPoints: z64.number().min(0).optional()
}).strict();
var instantiateQuestSchema = z64.object({
  teamId: z64.string().nullable().optional(),
  deadline: z64.string().nullable().optional(),
  rewardPoints: z64.number().min(0).optional(),
  difficulty: z64.enum([
    QuestDifficulty.COMMON,
    QuestDifficulty.UNCOMMON,
    QuestDifficulty.RARE,
    QuestDifficulty.EPIC,
    QuestDifficulty.LEGENDARY
  ]).optional(),
  /** 유단가로 실체화하면 필수 — 서버가 검사한다 */
  pricingGround: pricingGroundSchema.optional()
}).strict();

// src/dto/settings.ts
import { z as z65 } from "zod";
var createHolidaySchema = z65.object({
  date: Iso8601Schema,
  name: z65.string(),
  isRecurring: z65.boolean().optional()
}).strict();
var syncHolidaysSchema = z65.object({
  year: z65.number()
}).strict();
var updateHolidaySchema = z65.object({
  date: Iso8601Schema.optional(),
  name: z65.string().optional(),
  isRecurring: z65.boolean().optional(),
  paidType: z65.enum([
    HolidayPaidType.UNPAID,
    HolidayPaidType.COMPANY_DESIGNATED,
    HolidayPaidType.LEGALLY_PAID
  ]).optional()
}).strict();
var updateSettingsSchema = z65.object({
  coreTimeStart: z65.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "coreTimeStart must be in HH:mm format").optional(),
  coreTimeEnd: z65.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "coreTimeEnd must be in HH:mm format").optional(),
  standardWorkHours: z65.number().int().min(1).max(24).optional(),
  maxDailyWorkHours: z65.number().int().min(1).max(24).optional(),
  payDay: z65.number().int().min(1).max(31).optional()
}).strict();
var upsertWorkScheduleSchema = z65.object({
  userId: z65.string().min(1),
  date: z65.string().min(1),
  scheduleType: z65.string().min(1),
  note: z65.string().nullable().optional()
}).strict();
var generateHolidaySchedulesSchema = z65.object({
  year: z65.number(),
  userIds: z65.array(z65.string())
}).strict();

// src/dto/user.ts
import { z as z66 } from "zod";
var batchRegisterUserSchema = z66.object({
  username: z66.string().min(2).max(50),
  email: EmailSchema,
  // 위계는 대표 판정·넓은 조회를 여는 축이라 직급 변경(update-level)과 같은 넷만 받는다(감사 C21).
  level: z66.enum([UserLevel.INTERN, UserLevel.ASSOCIATE, UserLevel.MANAGER, UserLevel.EXECUTIVE]).optional(),
  // 구 @IsOptional @IsArray + 선언 타입 roles?: string[].
  // @IsArray 자체는 요소를 검증하지 않으나, 원본 선언 타입과 다운스트림 서비스
  // 시그니처(roles?: string[])가 string[] 계약을 정의하므로 z.array(z.string()) 로 미러한다.
  roles: z66.array(z66.string()).optional(),
  teamId: z66.uuid().optional(),
  /**
   * 입사(예정)일. 'YYYY-MM-DD' 는 KST 자정으로, ISO datetime 은 그대로 해석한다.
   * 미래면 계정이 PENDING_ONBOARD(입사예정)로 만들어져 입사일 자정에 cron 이 연다.
   * 생략하면 종전대로 생성 시각이 입사일이 된다(@default(now())).
   */
  hireDate: Iso8601Schema.optional()
}).strict();
var batchRegisterSchema = z66.object({
  users: z66.array(batchRegisterUserSchema)
}).strict();
var updateHireDateSchema = z66.object({
  hireDate: Iso8601Schema
}).strict();
var updateOnboardingGuideSchema = z66.object({
  visible: z66.boolean()
}).strict();
var updateEmailSchema = z66.object({
  email: EmailSchema
}).strict();
var updateLevelSchema = z66.object({
  level: z66.enum([UserLevel.INTERN, UserLevel.ASSOCIATE, UserLevel.MANAGER, UserLevel.EXECUTIVE]),
  effectiveDate: Iso8601Schema.optional()
}).strict();
var updateRolesSchema = z66.object({
  roles: z66.array(z66.enum([UserRole.PLAYER, UserRole.OPERATOR, UserRole.ADMIN])).nonempty()
}).strict();
var updateWorkTypeSchema = z66.object({
  workType: z66.enum([WorkType.FIXED, WorkType.FREE])
}).strict();
var updateExcludeFromAttendanceSchema = z66.object({
  excludeFromAttendance: z66.boolean()
}).strict();

// src/dto/worklog.ts
import { z as z67 } from "zod";
var WORKLOG_LOG_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
var createWorkLogSchema = z67.object({
  logDate: z67.string().regex(WORKLOG_LOG_DATE_PATTERN, "logDate must be in YYYY-MM-DD format"),
  content: z67.string().optional()
}).strict();
var updateWorkLogSchema = z67.object({
  content: z67.string()
}).strict();
var addAttachmentSchema = z67.object({
  path: z67.string(),
  filename: z67.string(),
  mimeType: z67.string().optional(),
  size: z67.number().int().optional()
}).strict();

// src/dto/campaign-retro.ts
import { z as z68 } from "zod";
var note = z68.string().trim().max(1e4).nullable().optional().transform((v) => v || null);
var createCampaignRetroObservationSchema = z68.object({
  advertiserReason: note,
  interpretation: note,
  rosterEntryIds: z68.array(z68.string().uuid()).max(100).default([]).refine((ids) => new Set(ids).size === ids.length, "\uAC19\uC740 \uD589\uC744 \uC911\uBCF5\uD574\uC11C \uC5F0\uACB0\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.")
}).strict().refine(
  (v) => v.advertiserReason !== null || v.interpretation !== null,
  "\uAD11\uACE0\uC8FC \uC0AC\uC720\uB098 \uB0B4\uBD80 \uD574\uC11D\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."
);
var tags = z68.array(z68.string().trim().min(1).max(80)).max(20).default([]);
var createSelectionGuidelineSchema = z68.object({
  content: z68.string().trim().min(1).max(1e4),
  sourceRetroId: z68.string().uuid(),
  productTags: tags,
  categoryTags: tags
}).strict();
var updateCampaignRetroDeadlineSchema = z68.object({
  dueAt: z68.string().datetime(),
  expectedDueAt: z68.string().datetime()
}).strict();
var lesson = (message) => z68.string({ error: message }).trim().min(1, message).max(2e3);
var recordCampaignReportSchema = z68.object({
  worked: lesson("\uC798 \uBA39\uD78C \uAC83\uC744 \uD55C \uC904 \uC801\uC5B4 \uC8FC\uC138\uC694."),
  avoid: lesson("\uB2E4\uC2DC \uC548 \uD560 \uAC83\uC744 \uD55C \uC904 \uC801\uC5B4 \uC8FC\uC138\uC694.")
}).strict();
var campaignRetroListQuerySchema = z68.object({
  teamId: z68.string().uuid().optional(),
  advertiserId: z68.string().uuid().optional()
}).strict();
var selectionGuidelineQuerySchema = z68.object({ advertiserId: z68.string().uuid().optional() }).strict();

// src/dto/campaign-manager-bonus.ts
import { z as z69 } from "zod";
var managerBonusListQuerySchema = z69.object({
  limit: z69.coerce.number().int().min(1).max(100).default(20),
  cursor: z69.uuid().optional()
}).strict();
var managerBonusRowCountsSchema = z69.array(
  z69.strictObject({
    stageCount: z69.number().int().positive(),
    myStageCount: z69.number().int().positive(),
    rowCount: z69.number().int().positive()
  }).refine((line) => line.myStageCount <= line.stageCount)
).min(1);

// src/dto/campaign-performance-report.ts
import { z as z70 } from "zod";
var confirmPerformanceReportSchema = z70.object({
  note: z70.string().trim().max(1e3).nullable().optional(),
  driveUrl: z70.url().max(2e3).nullable().optional(),
  /** 결과 보고 전 캠페인의 첫 확정에만 받는다(D5). 정정판에서는 무시한다. */
  lessons: recordCampaignReportSchema.optional()
}).strict();

// src/dto/campaign-target-slot.ts
import { z as z71 } from "zod";
function normalizeMutationAliases(value, revisionField) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  const input = value;
  const actionId2 = input.actionId ?? input.eventId;
  const revision = input[revisionField] ?? input.expectedRevision;
  const actionIdsConflict = input.actionId !== void 0 && input.eventId !== void 0 && input.actionId !== input.eventId;
  const revisionsConflict = input[revisionField] !== void 0 && input.expectedRevision !== void 0 && input[revisionField] !== input.expectedRevision;
  const normalized = Object.fromEntries(
    Object.entries(input).filter(
      ([key]) => !["actionId", "eventId", revisionField, "expectedRevision"].includes(key)
    )
  );
  return {
    ...normalized,
    actionId: actionIdsConflict ? void 0 : actionId2,
    [revisionField]: revisionsConflict ? void 0 : revision
  };
}
var actionId = z71.uuid();
var expectedRevision = z71.number().int().min(0);
var slotMutation = (shape) => z71.preprocess(
  (value) => normalizeMutationAliases(value, "expectedSlotRevision"),
  z71.object({ actionId, expectedSlotRevision: expectedRevision, ...shape }).strict()
);
var boardMutation = (shape) => z71.preprocess(
  (value) => normalizeMutationAliases(value, "expectedBoardRevision"),
  z71.object({ actionId, expectedBoardRevision: expectedRevision, ...shape }).strict()
);
var reason = z71.string().trim().min(1).max(2e3);
var initializeCampaignTargetSlotsSchema = boardMutation({
  contractSlotCount: z71.number().int().min(1).max(9999),
  conditions: targetSlotRequirementsSchema,
  reason
});
var updateCampaignSlotConditionsSchema = boardMutation({
  conditions: targetSlotRequirementsSchema,
  reason
});
var updateTargetSlotOverridesSchema = boardMutation({
  conditions: targetSlotOverridesSchema,
  reason
});
var updateTargetSlotParticipantSchema = boardMutation({
  conditions: targetSlotParticipantConditionsSchema,
  reason
});
var assignCampaignTargetSlotSchema = slotMutation({
  rosterEntryId: z71.uuid(),
  campaignConditionRevisionId: z71.uuid(),
  slotConditionRevisionId: z71.uuid().nullable(),
  rosterConditionRevisionId: z71.uuid().nullable()
});
var cancelCampaignTargetSlotSchema = slotMutation({ reason });
var targetSlotCandidatesQuerySchema = z71.object({
  cursor: z71.string().trim().min(1).optional(),
  limit: z71.coerce.number().int().min(1).max(100).default(50)
});
var targetSlotChangedEventSchema = z71.object({
  schemaVersion: z71.literal(1),
  eventId: z71.uuid(),
  scenarioId: z71.uuid(),
  boardRevision: z71.number().int().nonnegative()
});

// src/dto/campaign-video-tracking.ts
import { z as z72 } from "zod";
var videoVisibilityReviewSchema = z72.strictObject({
  requestId: z72.uuid(),
  checkId: z72.uuid(),
  reviewScope: z72.enum(["LATEST", "HISTORY"]).optional(),
  verdict: z72.enum(["PRIVATE", "PUBLIC", "PLATFORM_RESTRICTED", "RESTORED"]),
  reason: z72.string().trim().min(1).max(1e3)
});
var campaignVideoTrackingMetricsSchema = z72.object({
  views: z72.number().int().nonnegative().nullable(),
  likes: z72.number().int().nonnegative().nullable(),
  comments: z72.number().int().nonnegative().nullable(),
  shares: z72.number().int().nonnegative().nullable(),
  saves: z72.number().int().nonnegative().nullable()
});
var trackingRequestIdSchema = z72.string().uuid();
var trackingVideoUrlSchema = z72.string().trim().min(1).max(2e3);
var trackingReasonSchema = z72.string().trim().max(1e3);
var videoTrackingRegisterRequestSchema = z72.object({
  requestId: trackingRequestIdSchema,
  videoUrl: trackingVideoUrlSchema
});
var videoTrackingConfirmReplacementRequestSchema = z72.object({
  requestId: trackingRequestIdSchema,
  videoUrl: trackingVideoUrlSchema,
  reason: trackingReasonSchema.optional()
});
var videoTrackingControlRequestSchema = z72.object({
  requestId: trackingRequestIdSchema,
  issuedAt: z72.string().datetime({ offset: true }),
  reason: trackingReasonSchema.optional()
});
var videoTrackingCorrectLinkRequestSchema = videoTrackingRegisterRequestSchema.extend({
  reason: trackingReasonSchema.optional()
});
var videoTrackingConfirmRequestSchema = videoTrackingRegisterRequestSchema;
var VIDEO_TRACKING_INBOX_REASONS = [
  "REGISTRATION_NO_RESPONSE",
  "REGISTRATION_FAILED",
  "TRACKING_ERROR",
  "CONSECUTIVE_MISSING",
  "REPLACEMENT_FAILED"
];
var VIDEO_TRACKING_ERROR_MESSAGES = {
  INVALID_URL: "\uD2F1\uD1A1 \uC601\uC0C1 \uB9C1\uD06C\uAC00 \uC544\uB2D9\uB2C8\uB2E4.",
  UNSUPPORTED_PLATFORM: "\uD2F1\uD1A1 \uC601\uC0C1 \uB9C1\uD06C\uAC00 \uC544\uB2D9\uB2C8\uB2E4.",
  VIDEO_NOT_FOUND: "\uC601\uC0C1\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC9C0\uC6CC\uC84C\uAC70\uB098 \uB9C1\uD06C\uAC00 \uD2C0\uB838\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  VIDEO_RESTRICTED: "\uBCFC \uC218 \uC5C6\uB294 \uC601\uC0C1\uC785\uB2C8\uB2E4. \uBE44\uACF5\uAC1C\uC774\uAC70\uB098 \uC5F0\uB839 \uC81C\uD55C\uC774 \uAC78\uB838\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  ALREADY_TRACKED: "\uB2E4\uB978 \uCEA0\uD398\uC778\uC774\uB098 \uC560\uB4DC\uCF54\uB4DC\uC5D0\uC11C \uC774\uBBF8 \uCD94\uC801\uD558\uB294 \uC601\uC0C1\uC785\uB2C8\uB2E4.",
  RETRY_EXHAUSTED: "\uD2F1\uD1A1\uC5D0\uC11C \uC601\uC0C1\uC744 \uC5EC\uB7EC \uBC88 \uD655\uC778\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
  REPLACES_TARGET_INVALID: "\uAD50\uCCB4\uD560 \uAE30\uC874 \uC601\uC0C1\uC758 \uC5F0\uACB0\uC774 \uB9DE\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."
};
function parseTiktokVideoLink(raw) {
  const match = /^https:\/\/(?:www\.)?tiktok\.com(\/[^?#\s]*)?(?:[?#]\S*)?$/i.exec(raw.trim());
  if (!match) return notTiktok();
  const videoId = /^\/@[^/]+\/video\/(\d+)\/?$/.exec(match[1] ?? "")?.[1];
  if (!videoId) {
    return {
      ok: false,
      problem: "NO_VIDEO_ID",
      message: "TikTok \uC601\uC0C1 \uBC88\uD638\uB97C \uC77D\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC601\uC0C1 \uD398\uC774\uC9C0\uC758 \uC8FC\uC18C\uB97C \uB123\uC5B4 \uC8FC\uC138\uC694."
    };
  }
  return { ok: true, videoId };
}
function notTiktok() {
  return {
    ok: false,
    problem: "NOT_TIKTOK",
    message: "TikTok \uC601\uC0C1 \uB9C1\uD06C\uAC00 \uC544\uB2D9\uB2C8\uB2E4. https://www.tiktok.com/@\uACC4\uC815/video/\uC22B\uC790 \uD615\uD0DC\uB85C \uB123\uC5B4 \uC8FC\uC138\uC694."
  };
}
function videoTrackingErrorMessage(code) {
  return (code && VIDEO_TRACKING_ERROR_MESSAGES[code]) ?? "\uB4F1\uB85D\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.";
}

// src/dto/campaign-timeline.ts
import { z as z73 } from "zod";
var isoDate2 = z73.iso.date();
var campaignTimelineEntrySchema = z73.strictObject({
  label: z73.string().trim().min(1).max(CAMPAIGN_TIMELINE_LABEL_MAX),
  startsOn: isoDate2.nullable(),
  endsOn: isoDate2.nullable(),
  milestone: z73.enum(Object.values(CampaignTimelineMilestone)).nullable()
});
var campaignTimelineSaveSchema = z73.strictObject({
  requestId: z73.uuid(),
  /** 화면이 읽은 판 번호. 그사이 다른 사람이 저장했으면 충돌로 거절한다. */
  expectedRevision: z73.number().int().nonnegative(),
  entries: z73.array(campaignTimelineEntrySchema).min(1).max(CAMPAIGN_TIMELINE_MAX_ENTRIES),
  /** 캠페인 시작·종료 날짜를 바꿀 때만 필요하다 — 광고주 확인 내용 같은 근거. */
  reason: z73.string().trim().min(1).max(1e3).nullable()
});

// src/dto/team-schedule.ts
import { z as z74 } from "zod";
var TEAM_SCHEDULE_MAX_DAYS = 62;
var dayIndex = (date) => Date.parse(`${date}T00:00:00Z`) / 864e5;
var teamScheduleQuerySchema = z74.strictObject({ from: z74.iso.date(), to: z74.iso.date() }).refine(({ from, to }) => from <= to, { message: "\uB05D \uB0A0\uC9DC\uAC00 \uC2DC\uC791 \uB0A0\uC9DC\uBCF4\uB2E4 \uC55E\uC12D\uB2C8\uB2E4." }).refine(({ from, to }) => dayIndex(to) - dayIndex(from) < TEAM_SCHEDULE_MAX_DAYS, {
  message: `\uD55C \uBC88\uC5D0 ${TEAM_SCHEDULE_MAX_DAYS}\uC77C\uAE4C\uC9C0\uB9CC \uC870\uD68C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.`
});

// src/dto/reply-template.ts
import { z as z75 } from "zod";
var filledText = (max) => z75.string().max(max).refine((value) => value.trim() !== "", { message: "\uBE48 \uAC12\uC740 \uBC1B\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4" });
var withoutUnknownVariables = (schema) => schema.refine(
  (value) => typeof value !== "string" || findUnknownTemplateVariables(value).length === 0,
  { message: "\uD5C8\uC6A9 \uBAA9\uB85D \uBC16\uC758 \uBCC0\uC218\uB294 \uC4F8 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4" }
);
var aspectRatioSchema = z75.string().refine(isAllowedCropAspectRatio, {
  message: "\uCD9C\uB825 \uBE44\uC728\uC740 \uAC00\uB85C:\uC138\uB85C \uD615\uC2DD\uC774\uACE0 2:1 \uC5D0\uC11C 4:5 \uC0AC\uC774\uC5EC\uC57C \uD569\uB2C8\uB2E4"
});
var representativeImageSchema = z75.object({
  assetPath: z75.string().min(1).max(500),
  crop: z75.object({
    x: z75.number(),
    y: z75.number(),
    zoom: z75.number().positive(),
    aspectRatio: aspectRatioSchema
  }).strict(),
  altText: z75.string().max(200)
}).strict();
var replyTemplateContentSchema = z75.object({
  title: filledText(100),
  subject: withoutUnknownVariables(z75.string().max(200)).nullable().optional(),
  body: withoutUnknownVariables(filledText(1e4)),
  /** 대표 이미지 — 팀 공용 층도 든다. 없이도 저장·발송할 수 있다(IA §3-5). */
  image: representativeImageSchema.nullable().optional()
});
var updateReplyTemplateSchema = replyTemplateContentSchema.partial().strict();
var createReplyTemplateSchema = replyTemplateContentSchema.extend({
  layer: z75.enum(REPLY_TEMPLATE_LAYERS),
  /**
   * 세 층 모두 든다 — 저장 칸이 팀을 비울 수 없고(`ReplyTemplate.teamId`), 토큰에 팀이 없어
   * 서버가 대신 고를 수 없다. 그 팀에 쓸 자격이 있는지는 서버가 이 값으로 판정한다.
   */
  teamId: z75.string().uuid(),
  /** 캠페인 층만 든다. */
  scenarioId: z75.string().uuid().optional(),
  /**
   * 캠페인 층만 든다 — 기본 템플릿이 나라마다 하나씩이라(IA §6-1) 어느 나라의 것인지가
   * 필요하다. `Scenario.targetCountries` 와 같은 ISO 3166-1 alpha-2 두 글자다.
   */
  country: z75.string().regex(/^[A-Z]{2}$/, { message: "\uB098\uB77C\uB294 \uB300\uBB38\uC790 \uB450 \uAE00\uC790\uC785\uB2C8\uB2E4" }).optional(),
  stage: z75.enum(REPLY_TEMPLATE_STAGES),
  kind: z75.enum(REPLY_TEMPLATE_KINDS),
  /** 복제 원본. 새로 쓰는 템플릿은 비운다. */
  parentId: z75.string().uuid().optional()
}).strict().superRefine((value, ctx) => {
  if (value.kind === "FIRST_CONTACT" && value.stage !== "RECRUIT") {
    ctx.addIssue({
      code: z75.ZodIssueCode.custom,
      path: ["stage"],
      message: "\uCCAB \uC12D\uC678\uBA54\uC77C\uC740 \uC12D\uC678 \uAD6C\uAC04\uC5D0\uB9CC \uB461\uB2C8\uB2E4"
    });
  }
  if (value.layer === "CAMPAIGN" && value.scenarioId === void 0) {
    ctx.addIssue({
      code: z75.ZodIssueCode.custom,
      path: ["scenarioId"],
      message: "\uCEA0\uD398\uC778 \uD15C\uD50C\uB9BF\uC740 \uCEA0\uD398\uC778\uC744 \uC9C0\uC815\uD574\uC57C \uD569\uB2C8\uB2E4"
    });
  }
  if (value.layer !== "CAMPAIGN" && value.scenarioId !== void 0) {
    ctx.addIssue({
      code: z75.ZodIssueCode.custom,
      path: ["scenarioId"],
      message: "\uCEA0\uD398\uC778 \uCE35\uC774 \uC544\uB2CC \uD15C\uD50C\uB9BF\uC5D0\uB294 \uCEA0\uD398\uC778\uC744 \uBD99\uC774\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4"
    });
  }
  if (value.layer === "CAMPAIGN" && value.country === void 0) {
    ctx.addIssue({
      code: z75.ZodIssueCode.custom,
      path: ["country"],
      message: "\uCEA0\uD398\uC778 \uD15C\uD50C\uB9BF\uC740 \uB098\uB77C\uB97C \uC9C0\uC815\uD574\uC57C \uD569\uB2C8\uB2E4"
    });
  }
  if (value.layer !== "CAMPAIGN" && value.country !== void 0) {
    ctx.addIssue({
      code: z75.ZodIssueCode.custom,
      path: ["country"],
      message: "\uCEA0\uD398\uC778 \uCE35\uC774 \uC544\uB2CC \uD15C\uD50C\uB9BF\uC5D0\uB294 \uB098\uB77C\uB97C \uBD99\uC774\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4"
    });
  }
});

// src/dto/outreach-draft.ts
import { z as z76 } from "zod";
var OUTREACH_CTA_PRESETS = ["PRIMARY"];
var OUTREACH_CTA_ALIGNS = ["left", "center", "right"];
var OUTREACH_CTA_WIDTHS = ["auto", "full"];
var dateOnly = z76.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: "\uB0A0\uC9DC\uB294 YYYY-MM-DD \uD615\uC2DD\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4"
});
var outreachDraftCtaSchema = z76.object({
  label: z76.string().min(1).max(60),
  align: z76.enum(OUTREACH_CTA_ALIGNS),
  width: z76.enum(OUTREACH_CTA_WIDTHS),
  preset: z76.enum(OUTREACH_CTA_PRESETS)
}).strict();
var outreachDraftContentSchema = z76.object({
  subject: z76.string().max(200).nullable().optional(),
  /** 제품·캠페인 상세 하나. 자유형 블록을 늘리는 편집기가 아니다. */
  body: z76.string().max(1e4),
  image: representativeImageSchema.nullable().optional(),
  /** 캠페인 기본값이 채우고 이번 초안에서 바꿀 수 있다. 비워 둘 수도 있다. */
  productUrl: z76.string().url().nullable().optional(),
  /** 이번 초안이 제안하는 일정. 캠페인 기준 축은 후행 퀘스트 `8039bf58` 의 몫이다. */
  proposedDraftDueDate: dateOnly.nullable().optional(),
  proposedUploadDate: dateOnly.nullable().optional(),
  /**
   * 이번 제안 금액 — 본문의 `{금액}` 자리에 들어가는 크리에이터 협업비(퀘스트 52d91374).
   * 받는 사람마다 같은 값이다. **숫자만** 든다 — 통화 기호·단위는 본문 문장이 쓴다
   * (`${금액}` · `{금액}원`). 초안이 나라마다 따로라 그 나라 문장이 통화를 안다.
   */
  proposedAmount: z76.string().regex(/^\d{1,9}(\.\d{1,2})?$/, { message: "\uAE08\uC561\uC740 \uC22B\uC790\uB9CC \uC801\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4" }).nullable().optional(),
  /**
   * 본문에 실릴 때의 날짜 모양 — **작성자가 고른다**(2026-09-21). 시스템이 나라로 정하지
   * 않는 이유는 메일 언어를 작성자에게 맡긴 2026-09-18 결정과 같다. 칸이 없던 옛 초안은
   * 저장값 그대로인 `ISO` 로 읽힌다.
   */
  scheduleDateFormat: z76.enum(OUTREACH_DATE_FORMATS).optional(),
  /**
   * 보내는 사람 주소. **스티비가 주소록마다 인증한 주소여야 한다** — 2026-09-28 실측에서
   * 회사 주소 여섯 개가 한 주소록에서 전부 `사용할 수 없는 발신자` 로 거부됐고, 그 자리에
   * 인증된 주소는 하나뿐이었다. 로그인한 사람의 업무 주소를 그대로 실으면 이메일 생성부터
   * 막히므로 작성자가 고른다. 비면 대량 발송이 막힌다 — 인증 안 된 주소로 가면 등록만 되고
   * 발송이 조용히 멈춘다(2026-09-28 실측 1회차). 이 칸이 없던 옛 초안도 같다.
   */
  senderEmail: z76.string().email().max(254).nullable().optional(),
  /**
   * 보낼 스티비 주소록 번호. **발신자와 같은 자리에서 정해진다** — 스티비가 발신자를 주소록마다
   * 인증하므로 보내는 사람은 이 주소록의 인증 발신자 중에서 고른다(fact #2956). 발송 요청은
   * 이 값을 다시 받지 않고 서버가 초안에서 읽는다. 칸이 없던 옛 초안은 비어 있어 발송이 막힌다.
   */
  listId: z76.number().int().positive().nullable().optional(),
  cta: outreachDraftCtaSchema
}).strict();
var saveOutreachDraftSchema = z76.object({
  /**
   * 발송 나라 — ISO 3166-1 alpha-2. 초안 하나가 나라 하나다(2026-09-18).
   * 메일 언어는 작성자가 나라를 보고 정하므로 언어를 고르거나 기록하는 칸은 두지 않는다.
   */
  country: z76.string().regex(/^[A-Z]{2}$/, { message: "\uB098\uB77C\uB294 ISO \uB450 \uAE00\uC790\uC785\uB2C8\uB2E4" }),
  content: outreachDraftContentSchema
}).strict();
var outreachTestSendSchema = z76.object({
  to: z76.string().email()
}).strict();
var REACH_CHANNELS = ["EMAIL", "OTHER_MEDIA"];
var REACH_CHANNEL_LABELS = {
  EMAIL: "\uC774\uBA54\uC77C",
  OTHER_MEDIA: "\uB2E4\uB978 \uB9E4\uCCB4"
};
var OUTREACH_SELECTION_MODES = ["PICKED", "ALL_MATCHING"];
var OUTREACH_SELECTION_MAX_IDS = 5e3;
var OUTREACH_PICK_COUNT_MAX = 1e4;
var externalCreatorId = z76.string().min(1).max(64);
var saveOutreachSelectionSchema = z76.object({
  country: z76.string().regex(/^[A-Z]{2}$/, { message: "\uB098\uB77C\uB294 ISO \uB450 \uAE00\uC790\uC785\uB2C8\uB2E4" }),
  /** 이 선택이 어느 스냅샷 위에서 만들어졌나. 스냅샷이 만료돼도 선택은 남는다. */
  snapshotId: z76.string().min(1).max(128),
  /**
   * `PICKED` 는 고른 사람만, `ALL_MATCHING` 은 조건에 맞는 전체에서 뺀 사람만 센다.
   * 전체 선택이 조건 + 제외 목록인 것은 IA §3-5 원칙 ① 이다.
   */
  mode: z76.enum(OUTREACH_SELECTION_MODES),
  includedIds: z76.array(externalCreatorId).max(OUTREACH_PICK_COUNT_MAX),
  pickCount: z76.number().int().positive().max(OUTREACH_PICK_COUNT_MAX).optional(),
  excludedIds: z76.array(externalCreatorId).max(OUTREACH_SELECTION_MAX_IDS)
}).strict().refine(
  (value) => value.pickCount === void 0 || value.mode === "PICKED" && value.includedIds.length === 0,
  { message: "\uC778\uC6D0 \uC9C0\uC815\uC740 \uC9C1\uC811 \uC120\uD0DD \uBAA8\uB4DC\uC5D0\uC11C \uBA85\uB2E8 \uC5C6\uC774 \uC694\uCCAD\uD574 \uC8FC\uC138\uC694." }
);
var OUTREACH_PRESENCE_LEASE_MS = 12e4;
var OUTREACH_PRESENCE_HEARTBEAT_MS = 3e4;
function outreachPresenceLeaseExpiry(now) {
  return new Date(now.getTime() + OUTREACH_PRESENCE_LEASE_MS);
}

// src/dto/campaign-video-review-verdict.ts
import { z as z77 } from "zod";
var EMAIL_ADDRESS_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
var PHONE_NUMBER_PATTERN = /\b(?:\+?82[-\s]?)?(?:0?1[016789]|0?2|0?[3-6][1-5]|0?70|0?80)[-\s]?\d{3,4}[-\s]?\d{4}\b/;
var KOREAN_ADDRESS_PATTERN = /(?:서울(?:특별시)?|부산(?:광역시)?|대구(?:광역시)?|인천(?:광역시)?|광주(?:광역시)?|대전(?:광역시)?|울산(?:광역시)?|세종(?:특별자치시)?|제주(?:특별자치도)?|경기(?:도)?|강원(?:특별자치도|도)?|충청[남북]도|충[남북]|전라[남북]도|전[남북]|경상[남북]도|경[남북])\s+[가-힣0-9]+(?:시|군|구)\s+[가-힣0-9]+(?:읍|면|동|로|길)(?:\s+\d+(?:-\d+)?)?/;
var reasonSchema = z77.string().trim().min(1).max(2e3).refine(
  (reason2) => !EMAIL_ADDRESS_PATTERN.test(reason2) && !PHONE_NUMBER_PATTERN.test(reason2) && !KOREAN_ADDRESS_PATTERN.test(reason2),
  "\uBC18\uB824\xB7\uCDE8\uC18C \uC0AC\uC720\uC5D0\uB294 \uC774\uBA54\uC77C, \uC804\uD654\uBC88\uD638, \uC8FC\uC18C\uB97C \uC785\uB825\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
);
var judgeCampaignVideoReviewRevisionSchema = z77.discriminatedUnion("verdict", [
  z77.object({
    verdict: z77.literal("APPROVED"),
    // READY 파일이 여럿인 회차에서 게시본으로 보관할 원본 (approved-original-archive §5).
    originalFileId: z77.uuid().optional()
  }).strict(),
  z77.object({
    verdict: z77.literal("REJECTED"),
    reason: reasonSchema.optional(),
    itemIds: z77.array(z77.uuid()).max(100).refine((ids) => new Set(ids).size === ids.length, "\uBC18\uB824 \uD56D\uBAA9\uC740 \uC911\uBCF5\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.").default([])
  }).strict()
]);
var cancelCampaignVideoReviewRevisionSchema = z77.object({ reason: reasonSchema }).strict();

// src/dto/campaign-roster-approved-original.ts
import { z as z78 } from "zod";
var createApprovedOriginalUploadSchema = z78.object({
  source: z78.enum([
    ApprovedOriginalSource.OFFLINE_APPROVED,
    ApprovedOriginalSource.CREATOR_RESEND
  ]),
  receivedVia: z78.enum([
    ApprovedOriginalReceivedVia.EMAIL,
    ApprovedOriginalReceivedVia.KAKAO,
    ApprovedOriginalReceivedVia.OTHER
  ]),
  receivedAt: z78.string().datetime({ offset: true }),
  fileOrigin: z78.enum([
    ApprovedOriginalFileOrigin.ORIGINAL_FILE,
    ApprovedOriginalFileOrigin.PLATFORM_OWNER_DOWNLOAD
  ]),
  mimeType: z78.enum(SUPPORTED_VIDEO_MIME_TYPES),
  sizeBytes: z78.string().regex(/^[1-9]\d*$/, "sizeBytes must be a positive integer string").refine(
    (value) => !/^[1-9]\d*$/.test(value) || BigInt(value) <= MAX_VIDEO_REVIEW_FILE_BYTES,
    "sizeBytes exceeds the 5 GB video limit"
  )
}).strict();

// src/dto/wiki.ts
import { z as z80 } from "zod";

// src/dto/wiki-area.ts
import { z as z79 } from "zod";
var wikiAreaKeySchema = z79.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);
var label = z79.string().transform(normalizeWikiTitle).pipe(z79.string().min(1).max(60));
var sortOrder = z79.number().int().min(0).max(1e6);
var createWikiAreaSchema = z79.strictObject({ label, sortOrder: sortOrder.optional() });
var updateWikiAreaSchema = z79.strictObject({ label: label.optional(), sortOrder: sortOrder.optional() }).refine((value) => value.label !== void 0 || value.sortOrder !== void 0, {
  message: "\uBC14\uAFC0 \uC774\uB984\uC774\uB098 \uC21C\uC11C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694"
});

// src/dto/wiki.ts
var WIKI_TITLE_MAX = 100;
var WIKI_BODY_MAX = 3e4;
var titleSchema = z80.string().transform(normalizeWikiTitle).pipe(
  z80.string().min(1, { message: "\uC81C\uBAA9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694" }).max(WIKI_TITLE_MAX, { message: `\uC81C\uBAA9\uC740 ${WIKI_TITLE_MAX}\uC790\uAE4C\uC9C0 \uC4F8 \uC218 \uC788\uC2B5\uB2C8\uB2E4` }).refine((title) => !/[[\]|#]/.test(title), {
    message: "\uC81C\uBAA9\uC5D0\uB294 [ ] | # \uB97C \uC4F8 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB9C1\uD06C \uBB38\uBC95\uACFC \uBD80\uB52A\uD799\uB2C8\uB2E4"
  })
);
var revisionNumber = z80.number().int().min(1);
var documentContentSchema = z80.strictObject({
  title: titleSchema,
  /**
   * 종류는 서버가 분야 · 일어난 날에서 정한다(`deriveWikiKind`, 2026-10-03). 옛 클라이언트(플러그인 0.9.4
   * 이하)가 보내는 값은 받되 쓰지 않는다 — strictObject 라 칸을 없애면 그 클라이언트가 400 을 맞는다.
   */
  kind: z80.enum(WIKI_DOCUMENT_KINDS).optional(),
  /** 분야 — 필수. 「페이지/」 제목만 비워도 페이지로 들어간다(`resolveWikiArea`). */
  area: wikiAreaKeySchema.optional(),
  wikiBody: z80.string().max(WIKI_BODY_MAX, { message: "\uBCF8\uBB38\uC774 \uB108\uBB34 \uAE41\uB2C8\uB2E4" }),
  /** 문서 머리의 "이 창 열기" 버튼 — 페이지 분야(기능 문서)에만. */
  viewKey: z80.string().min(1).max(64).nullable().optional(),
  /** 적으면 사건 문서가 된다. 페이지 문서에는 적지 않는다. */
  occurredOn: z80.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "\uB0A0\uC9DC\uB294 YYYY-MM-DD \uB85C \uC801\uC5B4 \uC8FC\uC138\uC694" }).nullable().optional(),
  /** 규칙 정본 링크 — 본문에 규칙을 옮기지 않고 여기 건다(설계 ② 4). */
  sourceLinks: z80.array(z80.string().trim().min(1).max(500)).max(20).default([]),
  /** 이 판에서 무엇을 바꿨는지 한 줄. */
  summary: z80.string().trim().max(200).nullable().optional()
});
function checkClassification(value, ctx) {
  if (isWikiPageTitle(value.title) && value.area && value.area !== "PAGE") {
    ctx.addIssue({
      code: "custom",
      path: ["area"],
      message: "\u300C\uD398\uC774\uC9C0/\u300D \uB85C \uC2DC\uC791\uD558\uB294 \uBB38\uC11C\uC758 \uBD84\uC57C\uB294 \uD398\uC774\uC9C0\uC785\uB2C8\uB2E4"
    });
    return;
  }
  const area = resolveWikiArea(value.title, value.area);
  if (!area) {
    ctx.addIssue({ code: "custom", path: ["area"], message: "\uBD84\uC57C\uB97C \uACE8\uB77C \uC8FC\uC138\uC694" });
    return;
  }
  if (area === "PAGE" && value.occurredOn) {
    ctx.addIssue({
      code: "custom",
      path: ["occurredOn"],
      message: "\uD398\uC774\uC9C0 \uBB38\uC11C\uC5D0\uB294 \uC77C\uC5B4\uB09C \uB0A0\uC744 \uC801\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4"
    });
  }
  if (area !== "PAGE" && value.viewKey) {
    ctx.addIssue({
      code: "custom",
      path: ["viewKey"],
      message: "\uCC3D \uC5F4\uAE30 \uBC84\uD2BC\uC740 \uD398\uC774\uC9C0 \uBB38\uC11C\uC5D0\uB9CC \uB2F5\uB2C8\uB2E4"
    });
  }
}
var createWikiDocumentSchema = documentContentSchema.superRefine(checkClassification);
var updateWikiDocumentSchema = documentContentSchema.extend({ baseRevision: revisionNumber }).superRefine(checkClassification);
var updateWikiDocumentToolSchema = updateWikiDocumentSchema.safeExtend({ id: z80.uuid() });
var wikiRevisionGuardSchema = z80.strictObject({ baseRevision: revisionNumber });
var revertWikiDocumentSchema = z80.strictObject({
  baseRevision: revisionNumber,
  toRevision: revisionNumber
});
var listWikiDocumentsQuerySchema = z80.strictObject({
  kind: z80.enum(WIKI_DOCUMENT_KINDS).optional(),
  /** 분류 화면 — 이 분야 문서만, 넘겨주기 문서는 빼고. */
  area: wikiAreaKeySchema.optional()
});
var getWikiDocumentQuerySchema = z80.strictObject({ id: z80.uuid().optional(), title: titleSchema.optional() }).refine((query) => query.id !== void 0 !== (query.title !== void 0), {
  message: "\uBB38\uC11C ID \uB610\uB294 \uC81C\uBAA9 \uC911 \uD558\uB098\uB9CC \uC785\uB825\uD574 \uC8FC\uC138\uC694"
});
var searchWikiQuerySchema = z80.strictObject({
  q: z80.string().transform(normalizeWikiTitle).pipe(
    z80.string().min(1, { message: "\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694" }).max(100, { message: "\uAC80\uC0C9\uC5B4\uB294 100\uC790\uAE4C\uC9C0 \uC4F8 \uC218 \uC788\uC2B5\uB2C8\uB2E4" })
  )
});
var wikiByTitleQuerySchema = z80.strictObject({
  follow: z80.enum(["true", "false"]).optional().transform((v) => v === "true")
});
var recentWikiChangesQuerySchema = z80.strictObject({
  limit: z80.coerce.number().int().min(1).max(100).default(50)
});
var WIKI_IMAGE_BUCKET = "wiki-images";
var WIKI_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp"
];
var WIKI_IMAGE_EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp"
};
var wikiImageExtension = (mimeType) => WIKI_IMAGE_EXTENSIONS[mimeType];
var WIKI_IMAGE_SRC_PREFIX = "/wiki/images/";
var WIKI_IMAGE_FILE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:png|jpg|gif|webp)$/;
var isWikiImageFile = (file) => WIKI_IMAGE_FILE.test(file);
var wikiImageSrc = (file) => `${WIKI_IMAGE_SRC_PREFIX}${file}`;
function wikiImageFileFromSrc(src) {
  if (!src?.startsWith(WIKI_IMAGE_SRC_PREFIX)) return null;
  const file = src.slice(WIKI_IMAGE_SRC_PREFIX.length);
  return isWikiImageFile(file) ? file : null;
}
var createWikiImageUploadSchema = z80.strictObject({
  mimeType: z80.enum(WIKI_IMAGE_MIME_TYPES, {
    message: "\uADF8\uB9BC\uC740 PNG \xB7 JPEG \xB7 GIF \xB7 WebP \uB9CC \uC62C\uB9B4 \uC218 \uC788\uC2B5\uB2C8\uB2E4"
  }),
  fileSize: z80.number().int().positive().max(MAX_FILE_SIZE, { message: "\uADF8\uB9BC\uC740 10MB \uAE4C\uC9C0 \uC62C\uB9B4 \uC218 \uC788\uC2B5\uB2C8\uB2E4" })
});
var updateWikiSectionSchema = z80.strictObject({
  baseRevision: revisionNumber,
  wikiBody: z80.string().max(WIKI_BODY_MAX, { message: "\uBCF8\uBB38\uC774 \uB108\uBB34 \uAE41\uB2C8\uB2E4" }),
  summary: z80.string().trim().max(200).nullable().optional()
});
var WIKI_DISCUSSION_TITLE_MAX = 100;
var WIKI_COMMENT_MAX = 5e3;
var commentBodySchema = z80.string().trim().min(1, { message: "\uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694" }).max(WIKI_COMMENT_MAX, { message: `\uB0B4\uC6A9\uC740 ${WIKI_COMMENT_MAX}\uC790\uAE4C\uC9C0 \uC4F8 \uC218 \uC788\uC2B5\uB2C8\uB2E4` });
var createWikiDiscussionSchema = z80.strictObject({
  title: z80.string().trim().min(1, { message: "\uD1A0\uB860 \uC81C\uBAA9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694" }).max(WIKI_DISCUSSION_TITLE_MAX, {
    message: `\uD1A0\uB860 \uC81C\uBAA9\uC740 ${WIKI_DISCUSSION_TITLE_MAX}\uC790\uAE4C\uC9C0 \uC4F8 \uC218 \uC788\uC2B5\uB2C8\uB2E4`
  }),
  /** 첫 댓글. */
  wikiBody: commentBodySchema,
  /** 토론이 가리키는 문단 제목 — 비우면 문서 전체. */
  sectionTitle: z80.string().trim().max(200).nullable().optional().transform((v) => v || null)
});
var createWikiDiscussionCommentSchema = z80.strictObject({ wikiBody: commentBodySchema });
var listWikiDiscussionsQuerySchema = z80.strictObject({
  status: z80.enum(WIKI_DISCUSSION_STATUSES).optional()
});

// src/dto/wiki-metrics.ts
import { z as z81 } from "zod";
var count2 = z81.number().int().nonnegative();
var timestamp3 = z81.iso.datetime();
var wikiDocumentMetricsSchema = z81.object({
  documentId: z81.uuid(),
  uniqueReaders: count2,
  from: timestamp3,
  to: timestamp3
}).readonly();
var wikiMetricsSchema = z81.object({
  window: z81.object({ asOf: timestamp3, readerSince: timestamp3, searchSince: timestamp3 }).readonly(),
  search: z81.object({
    total: count2,
    zeroResults: count2,
    zeroResultRate: z81.number().min(0).max(100).nullable()
  }).readonly(),
  missing: z81.object({
    count: count2,
    baselineAt: timestamp3.nullable(),
    newCount: count2.nullable(),
    resolvedCount: count2.nullable(),
    mostReferenced: z81.array(z81.object({ title: z81.string(), sourceCount: count2 }).readonly()).readonly()
  }).readonly(),
  documents: z81.array(z81.object({ documentId: z81.uuid(), title: z81.string(), uniqueReaders: count2 }).readonly()).readonly()
}).readonly();

// src/dto/short-link.ts
import { z as z82 } from "zod";
var SHORT_LINK_PREVIEW_BOT_PATTERN = /kakaotalk-scrap|facebookexternalhit|Slackbot|Twitterbot|WhatsApp|TelegramBot|Discordbot|bot|crawler|preview/i;
var SHORT_LINK_ALIAS_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/;
var SHORT_LINK_RESERVED_ALIASES = [
  "s",
  "api",
  "auth",
  "health",
  "oauth",
  "mcp",
  "short-links",
  "admin",
  "advertiser-brands",
  "advertisers",
  "agents",
  "assistant",
  "attendance",
  "audit-logs",
  "bug-reports",
  "campaign-roster-entries",
  "campaigns",
  "channel-groups",
  "channels",
  "checkpoints",
  "clubs",
  "holidays",
  "knowledge",
  "leaves",
  "link-preview",
  "mail-connections",
  "notifications",
  "page-views",
  "payouts",
  "piecework",
  "points",
  "presence",
  "products",
  "proofs",
  "quest-tasks",
  "quest-templates",
  "quests",
  "ranks",
  "realtime",
  "reply-templates",
  "reservations",
  "rooms",
  "scenario-templates",
  "scenarios",
  "schedules",
  "settings",
  "system-settings",
  "task-statistics",
  "teams",
  "telemetry-events",
  "upload",
  "users",
  "webhooks",
  "wiki",
  "work-logs"
];
function isShortLinkTargetAllowed(value, policy = {}) {
  if (value.length === 0 || value.length > 2048 || /[\s\\]/.test(value) || Array.from(value).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))
    return false;
  if (value.startsWith("/")) return !value.startsWith("//");
  const parsed = z82.url({ normalize: true }).safeParse(value);
  if (!parsed.success) return false;
  const parts = /^(https?):\/\/([^/?#]*)(?:[/?#]|$)/.exec(parsed.data);
  const protocol = parts?.[1];
  const authority = parts?.[2];
  if (!authority || authority.includes("@")) return false;
  const host = (authority.startsWith("[") ? authority.slice(0, authority.indexOf("]") + 1) : authority.split(":")[0] ?? "").toLowerCase().replace(/\.$/, "");
  if (host === policy.shortLinkHost?.toLowerCase().replace(/\.$/, "")) return false;
  const local = host === "localhost" || host.endsWith(".localhost") || host === "127.0.0.1" || host === "[::1]";
  if (policy.allowLocal && local) return protocol === "http" || protocol === "https";
  if (protocol !== "https" || local || !host.includes(".") && !host.startsWith("[")) return false;
  if (/\.(local|internal|test|invalid)$/.test(host)) return false;
  if (host.startsWith("[")) return /^\[[23][0-9a-f]{3}:/.test(host);
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    const [a = 0, b = 0] = host.split(".").map(Number);
    if (a === 0 || a === 10 || a === 127 || a >= 224 || a === 169 && b === 254 || a === 172 && b >= 16 && b <= 31 || a === 192 && b === 168 || a === 100 && b >= 64 && b <= 127 || a === 198 && (b === 18 || b === 19))
      return false;
  }
  return true;
}
var shortLinkSubjectKeySchema = z82.string().regex(/^roster-row:[0-9a-f-]{36}$/).refine((value) => z82.uuid().safeParse(value.slice("roster-row:".length)).success);
function shortLinkSchemas(policy = {}) {
  const targetUrl = z82.string().max(2048).refine((value) => isShortLinkTargetAllowed(value, policy), {
    message: "\uD5C8\uC6A9\uB41C https \uC8FC\uC18C \uB610\uB294 \uC571 \uB0B4\uBD80 \uACBD\uB85C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694."
  });
  const settings = z82.object({
    targetUrl,
    label: z82.string().max(200).nullable().optional(),
    expiresAt: z82.iso.datetime({ offset: true }).nullable().optional()
  });
  return {
    create: settings.extend({
      alias: z82.string().regex(SHORT_LINK_ALIAS_PATTERN).refine((value) => !SHORT_LINK_RESERVED_ALIASES.some((reserved) => reserved === value)).optional(),
      subjectKey: shortLinkSubjectKeySchema.optional()
    }).strict(),
    update: settings.partial().extend({ revoke: z82.literal(true).optional() }).strict().refine((value) => Object.keys(value).length > 0)
  };
}
var { create: createShortLinkSchema, update: updateShortLinkSchema } = shortLinkSchemas();
var listShortLinksSchema = z82.object({ subjectKey: shortLinkSubjectKeySchema.optional() }).strict();

// src/dto/quest-dependency-history.ts
import { z as z83 } from "zod";
var questDependencyReasonSchema = z83.object({
  reason: z83.string().trim().min(1).max(2e3).optional()
}).strict().default({});
var questDependencyHistoryQuerySchema = z83.object({
  cursor: z83.string().regex(/^[1-9]\d{0,18}$/).pipe(z83.string().refine((value) => BigInt(value) <= 9223372036854775807n)).optional(),
  limit: z83.coerce.number().int().min(1).max(100).default(30)
}).strict();

export {
  advertiserContactSchema,
  createAdvertiserSchema,
  mergeFromDraftSchema,
  mergeAdvertiserSchema,
  updateAdvertiserContactSchema,
  updateAdvertiserSchema,
  advertiserBrandBodySchema,
  createCorrectionSchema,
  reviewCorrectionSchema,
  approveCorrectionSchema,
  resubmitCorrectionSchema,
  createSegmentCorrectionSchema,
  AUDIT_LOG_EXPORT_MAX_ROWS,
  clockInSchema,
  clockOutSchema,
  resolveMissedClockOutSchema,
  resolveMissedClockInSchema,
  segmentEditItemSchema,
  editAttendanceSchema,
  createAttendanceSchema,
  reassignSegmentSchema,
  findAggregateAttendancesSchema,
  reviewOffDaySchema,
  batchReviewOffDaySchema,
  createCompensationSchema,
  weeklySummaryQuerySchema,
  periodSummaryQuerySchema,
  REVIEWED_HISTORY_MAX_DAYS,
  dayScheduleDateSchema,
  dayScheduleMonthSchema,
  dayScheduleContentSchema,
  dayScheduleSharedTeamSchema,
  createDayScheduleSchema,
  updateDayScheduleSchema,
  queryAuditLogsSchema,
  AUDIT_LOG_PII_RETENTION_DAYS,
  AUDIT_LOG_ROW_RETENTION_DAYS,
  AUDIT_LOG_SYSTEM_ACTION,
  AUDIT_LOG_ACCESS_ACTION,
  AUDIT_PATH_SCOPED_SENSITIVE_KEYS,
  APPROVAL_HISTORY_AUDIT_TARGETS,
  AUDIT_LOG_ROW_RETENTION_EXEMPTIONS,
  verifyPasskeyRegistrationSchema,
  renamePasskeySchema,
  passkeyLoginOptionsSchema,
  passkeyLoginVerifySchema,
  rejectQuestSchema,
  revokeWorldQuestSchema,
  withdrawQuestSchema,
  EXPIRY_CLOSE_EVENT_PREFIX,
  EXPIRY_CLOSE_DISPOSITIONS,
  expiryCloseQuestSchema,
  addQuestDependencySchema,
  PRICING_REASON_CODES,
  PRICING_GROUND_PREFIX,
  pricingGroundSchema,
  formatPricingGround,
  approveQuestSchema,
  createQuestAttachmentSchema,
  createQuestBaseSchema,
  validateQuestCreationIds,
  createQuestSchema,
  CHECKPOINT_UNIT_MAX,
  createCheckpointSchema,
  updateQuestSchema,
  updateCheckpointSchema,
  acceptQuestSchema,
  assignCheckpointSchema,
  rejectCheckpointSchema,
  approveCheckpointSchema,
  reorderCheckpointsSchema,
  challengeCandidateSchema,
  BULK_ARCHIVE_MAX_IDS,
  bulkArchiveQuestsSchema,
  bulkArchiveQuestsResponseSchema,
  distributionKindSchema,
  questDistributionKindSchema,
  createBugReportSchema,
  createEventSchema,
  editBugReportSchema,
  updateBugReportSchema,
  convertBugReportToQuestSchema,
  issueCampaignAdcodeSchema,
  updateBoostingStageSchema,
  updateAdcodeMetricsSchema,
  campaignAdcodeDryRunRowSchema,
  CAMPAIGN_ADCODE_DRY_RUN_MAX_ROWS,
  campaignAdcodeDryRunSchema,
  CampaignAdcodeDryRunOutcome,
  CampaignAdcodeDryRunConflictCode,
  CampaignAdcodeDryRunUnresolvedCode,
  CAMPAIGN_ADCODE_DRY_RUN_PREVIEW_LIMIT,
  campaignAdcodeImportSchema,
  CampaignAdcodeImportOutcome,
  ROSTER_MANUAL_COMMUNICATION_CHANNELS,
  rosterManualCommunicationChannelSchema,
  ROSTER_MANUAL_COMMUNICATION_EVENT_TYPES,
  rosterManualCommunicationEventTypeSchema,
  ROSTER_MANUAL_COMMUNICATION_TARGET_ROLES,
  rosterManualCommunicationTargetRoleSchema,
  createCampaignRosterManualCommunicationSchema,
  rosterManualCommunicationStatsQuerySchema,
  createCampaignRosterEntrySchema,
  ROSTER_LOCKABLE_FIELDS,
  CAMPAIGN_ROSTER_SENSITIVE_KEYS,
  updateCampaignRosterEntrySchema,
  ROSTER_LOCK_ACTIONS,
  confirmCreatorResolutionSchema,
  relinkRosterCreatorSchema,
  createCampaignRosterSnapshotRevisionSchema,
  createCampaignRosterPriceRevisionSchema,
  createCampaignRosterScheduleRevisionSchema,
  createConditionAgreementSchema,
  createCampaignRosterUploadTakedownSchema,
  createCampaignRosterDropSchema,
  createCampaignRosterDropRestoreSchema,
  reassignCampaignRosterAssigneeSchema,
  campaignRosterProfileLinkResolveSchema,
  CampaignRosterWarningCode,
  CREATOR_COLLECTION_STATES,
  requestRosterCreatorCollectionSchema,
  ROSTER_SHIPPING_EXPORT_FIELDS,
  trackingImportRequestSchema,
  campaignRosterImportRowSchema,
  CAMPAIGN_ROSTER_IMPORT_MAX_ROWS,
  campaignRosterImportSchema,
  CampaignRosterImportOutcome,
  ROSTER_CHANGE_KINDS,
  openAdvertiserReviewRoundSchema,
  recordAdvertiserReviewVerdictsSchema,
  changeAdvertiserReviewModeSchema,
  createCampaignRosterVideoRightSchema,
  bulkCreateCampaignRosterVideoRightSchema,
  createCampaignRosterExtraPostSchema,
  CampaignVideoReviewSourceType,
  CampaignVideoReviewFileKind,
  CampaignVideoReviewFileState,
  MAX_VIDEO_REVIEW_FILE_BYTES,
  SUPPORTED_VIDEO_MIME_TYPES,
  createCampaignVideoReviewRevisionSchema,
  createCampaignVideoReviewUploadSessionSchema,
  SETTLEMENT_REQUEST_MAX_ENTRIES,
  createCampaignSettlementRequestSchema,
  SETTLEMENT_MARKABLE_STATUSES,
  markCampaignSettlementStatusSchema,
  bulkMarkCampaignSettlementStatusSchema,
  markSettlementDocumentsSchema,
  settlementQueueQuerySchema,
  CREATOR_MAIL_CONNECTION_STATUSES,
  startCreatorMailConnectionSchema,
  CREATOR_MAIL_CONNECT_FAILURES,
  creatorMailThreadRevisionSchema,
  sendCreatorMailSchema,
  saveCreatorMailDraftSchema,
  takeOverCreatorMailDraftSchema,
  OUTREACH_ROUND_MAX_HANDLES,
  OUTREACH_RETENTION_POLICY,
  OUTREACH_HANDLE_FIELD_KEYS,
  outreachBulkSendSchema,
  outreachBulkReserveSchema,
  CREATOR_LIST_DOWNLOAD_ROUTE,
  creatorListDownloadPath,
  CREATOR_LIST_CHANNELS,
  CREATOR_LIST_FILE_LIMIT,
  CREATOR_LIST_FILE_LIMIT_MAX,
  CREATOR_LIST_CONTACT_BATCH,
  CREATOR_LIST_RETENTION_DAYS,
  CREATOR_LIST_REDOWNLOAD_DAYS,
  CREATOR_LIST_CONTACT_LOG_NOTICE,
  creatorListQueryRequestSchema,
  creatorListBuildSchema,
  CREATOR_SEARCH_QUERY_MIN,
  CREATOR_SEARCH_QUERY_MAX,
  searchCreatorProjectionsSchema,
  CREATOR_PII_FIELD_KEYS,
  CREATOR_PII_FIELD_MAX_LENGTH,
  creatorRelationPiiSchema,
  upsertCreatorRelationPiiSchema,
  CREATOR_PII_RETENTION_MONTHS,
  CREATOR_PII_PURGE_CAUSES,
  CREATOR_PII_PURGE_BLOCKED,
  CANDIDATE_QUERY_STATES,
  OUTREACH_REINCLUDE_REASONS,
  OUTREACH_OUTLIER_GROUP_CODES,
  OUTREACH_OUTLIER_PRECEDENCE,
  OUTREACH_OUTLIER_GROUP_RULES,
  OUTREACH_OUTLIER_OTHER_TEAM_EVENTS,
  OUTREACH_OUTLIER_OTHER_TEAM_DAYS,
  OUTREACH_OUTLIER_PREVIEW_DEFAULT,
  OUTREACH_OUTLIER_PAGE_MAX,
  outreachOutlierSummaryRequestSchema,
  outreachOutlierRowsRequestSchema,
  createCreatorRelationSchema,
  RELATION_CONTACT_CHANNELS,
  RELATION_CONTACT_CHANNEL_LABELS,
  recordOffRosterContactSchema,
  changeCreatorRelationStateSchema,
  blockCreatorRelationSchema,
  unblockCreatorRelationSchema,
  outreachBlockCreatorRelationSchema,
  outreachUnblockCreatorRelationSchema,
  assignCreatorRelationOwnerSchema,
  createCreatorNotepadEntrySchema,
  createCreatorRelationNoteSchema,
  CREATOR_RELATION_SEEDING_TEAM_IDS_KEY,
  updateSeedingTeamIdsSchema,
  CREATOR_RELATION_SEEDING_TEAM_INVALID,
  CREATOR_RELATION_HANDLE_RELATIONS_OVERFLOW,
  parseSeedingTeamIds,
  CREATOR_RELATION_SENSITIVE_KEYS,
  creatorRelationPersonSchema,
  creatorRelationRefSchema,
  creatorRelationIntimacyMeasureSchema,
  creatorCampaignPriceSchema,
  creatorRelationCardSectionsSchema,
  creatorRelationCardSchema,
  creatorRelationRegionSchema,
  creatorRelationListItemSchema,
  creatorRelationListViewerSchema,
  CREATOR_RELATION_LIST_TAKE,
  CREATOR_RELATION_LIST_SORTS,
  CREATOR_RELATION_VIEWS,
  creatorRelationListQuerySchema,
  creatorRelationListCursorSchema,
  creatorRelationListResponseSchema,
  creatorRelationSummaryQuerySchema,
  creatorRelationRegionsResponseSchema,
  creatorRelationSummaryResponseSchema,
  creatorRelationOtherTeamContactsQuerySchema,
  creatorRelationOtherTeamContactSchema,
  creatorRelationOtherTeamContactsResponseSchema,
  creatorRelationQueueResponseSchema,
  creatorRelationTimelineQuerySchema,
  creatorRelationHistoryCursorSchema,
  creatorRelationHistoryItemSchema,
  creatorRelationHistoryPageSchema,
  creatorNotepadEntryResponseSchema,
  VIEW_METRICS_FRESHNESS,
  VIEW_METRICS_LAST_COLLECTION,
  VIEW_METRICS_REFRESH_STATES,
  VIEW_METRICS_COVERAGE,
  VIEW_METRICS_SAMPLE_STATUS,
  CREATOR_VIEW_METRICS_IDS_MAX,
  creatorViewMetricsQuerySchema,
  VIEW_METRICS_DISPLAY_STATES,
  deriveViewMetricsDisplayState,
  isViewMetricsSampleInsufficient,
  VIEW_METRICS_SNAPSHOT_OUTCOMES,
  VIEW_METRICS_SNAPSHOT_CAPTURED_ON,
  VIEW_METRICS_UNAVAILABLE_REASONS,
  createChannelSchema,
  CHANNEL_DESCRIPTION_MAX_LENGTH,
  updateChannelSchema,
  transferOwnershipSchema,
  createChannelGroupSchema,
  updateChannelGroupSchema,
  moveChannelToGroupSchema,
  setChannelNotifyLevelSchema,
  sendMessageAttachmentSchema,
  sendMessageSchema,
  inviteMemberSchema,
  kickMemberSchema,
  editMessageSchema,
  toggleReactionSchema,
  updateTeamSchema,
  updateTeamSettingsSchema,
  createDepartmentSchema,
  createTaskForceSchema,
  addMemberSchema,
  transferLeadershipSchema,
  updateTeamCapabilitiesSchema,
  teamOpsReadScopeSchema,
  teamOpsRosterVisibilitySchema,
  teamOpsRosterQuerySchema,
  teamOpsRosterTeamSchema,
  teamOpsRosterMembershipSchema,
  teamOpsRosterFiltersSchema,
  teamOpsRosterResponseSchema,
  teamOpsWorklogMonitoringQuerySchema,
  teamOpsWorklogTaskSchema,
  teamOpsWorklogDaySchema,
  teamOpsWorklogMonitoringEntrySchema,
  teamOpsWorklogMonitoringResponseSchema,
  teamOpsAttendanceBulkQuerySchema,
  teamOpsAttendanceStatusSchema,
  teamOpsAttendanceSourceSchema,
  teamOpsAttendanceBulkEntrySchema,
  teamOpsAttendanceBulkResponseSchema,
  createClubSchema,
  clubTransferLeadershipSchema,
  createPollSchema,
  confirmPollSchema,
  voteSchema,
  feedAttachmentInputSchema,
  createFeedSchema,
  updateFeedSchema,
  approveFeedCapExceededSchema,
  approveFeedResponseSchema,
  createLeaveRequestSchema,
  grantLeaveSchema,
  adjustBalanceSchema,
  adminCancelLeaveSchema,
  requestProofResubmitSchema,
  reviewLeaveRequestSchema,
  submitProofSchema,
  ALLOWED_MIME_TYPES,
  ALLOWED_BUCKETS,
  MAX_FILE_SIZE,
  createSignedUploadUrlSchema,
  createAgentSchema,
  updateAgentSchema,
  createConversationSchema,
  updateConversationSchema,
  sendAssistantMessageSchema,
  updateConversationEntitySchema,
  dokkaebiSignalSchema,
  REALTIME_TOKEN_TTL_SECONDS,
  REALTIME_TOKEN_REFRESH_LEAD_SECONDS,
  realtimeTokenResponseSchema,
  NOTIFICATION_OPT_IN_TOPICS,
  isNotificationOptInTopic,
  worklogNudgeSchema,
  createPieceworkActivitySchema,
  updatePieceworkActivitySchema,
  submitPieceworkSchema,
  approvePieceworkSchema,
  decidePieceworkActivitySchema,
  adjustPointsSchema,
  grantPointsSchema,
  POINT_HISTORY_EXPORT_MAX_ROWS,
  POINT_TEAM_ATTRIBUTION_IS_POINT_IN_TIME,
  POINT_TEAM_ATTRIBUTION_LABELS,
  POINT_TEAM_UNATTRIBUTED,
  POINT_TEAM_UNATTRIBUTED_LABEL,
  summarizePointTeamAttribution,
  createPageViewSchema,
  queryPageViewStatsSchema,
  PAGE_VIEW_RETENTION_DAYS,
  markPaidSchema,
  payoutVoidReasonSchema,
  voidPayoutSchema,
  BULK_VOID_MAX,
  bulkVoidPayoutSchema,
  bulkVoidPayoutSkippedSchema,
  bulkVoidPayoutResponseSchema,
  taxIncomeSummaryQuerySchema,
  taxIncomeBulkQuerySchema,
  TAX_INCOME_BULK_MAX,
  updatePointsSchema,
  LEADERBOARD_PERIODS,
  isLeaderboardPeriod,
  createReservationSchema,
  listReservationsQuerySchema,
  RESERVATION_RANGE_MAX_DAYS,
  listReservationsRangeQuerySchema,
  updateReservationSchema,
  updateReservationAttendeesSchema,
  createRoomSchema,
  updateRoomSchema,
  proofAttachmentSchema,
  createProofSchema,
  approveProofSchema,
  rejectProofSchema,
  updateProofDraftSchema,
  autoSubmitQuestProofSchema,
  autoSubmitCheckpointProofSchema,
  queryProofSubmissionStatsSchema,
  productTargetInputSchema,
  productSubsumeWorkInputSchema,
  createProductSchema,
  productPostureSchema,
  updateProductSchema,
  createScenarioProductSchema,
  updateScenarioProductSchema,
  QUEST_TASK_TITLE_MAX,
  questTaskTitleError,
  createQuestTaskSchema,
  updateQuestTaskSchema,
  completeQuestTaskSchema,
  closeDaySchema,
  resolveStaleTaskSchema,
  createQuestTaskFeedbackSchema,
  createQuestTaskFeedbackCommentSchema,
  createQuestQuestionSchema,
  createQuestQuestionCommentSchema,
  createQuestProposalSchema,
  resolveQuestProposalSchema,
  listQuestProposalsQuerySchema,
  CHECKIN_INTERVAL_GRADE_KEYS,
  CHECKIN_INTERVAL_MIN_SAMPLE,
  CLIENT_TELEMETRY_EVENT_NAMES,
  SERVER_TELEMETRY_EVENT_NAMES,
  TELEMETRY_EVENT_NAMES,
  telemetryEventNameSchema,
  clientTelemetryEventNameSchema,
  PROOF_SUBMISSION_PATHS,
  proofSubmissionPathSchema,
  PROOF_FAILURE_STAGES,
  proofFailureStageSchema,
  telemetryPayloadSchema,
  createTelemetryEventSchema,
  TELEMETRY_EVENT_RETENTION_DAYS,
  queryTelemetryEventStatsSchema,
  queryQuestCreationStatsSchema,
  telemetryObservationMetricSchema,
  telemetryObservationStatusSchema,
  telemetryObservationOutcomeSchema,
  createTelemetryObservationQuestionSchema,
  queryTelemetryObservationQuestionsSchema,
  createTelemetryObservationReviewSchema,
  telemetryObservationQuestionSchema,
  telemetryObservationQuerySchema,
  telemetryObservationSnapshotSchema,
  telemetryObservationReviewSchema,
  telemetryObservationQuestionDetailSchema,
  MONTHLY_RETRO_UNDATED,
  MONTHLY_RETRO_STATUSES,
  MONTHLY_RETRO_METRIC_KEYS,
  ScenarioReferenceSchema,
  STOP_RULE_THRESHOLD_MIN,
  STOP_RULE_THRESHOLD_MAX,
  stopRuleSchema,
  stopRulesSchema,
  createScenarioSchema,
  updateScenarioSchema,
  reorderQuestsSchema,
  requestAdcodeSchema,
  requestVaSchema,
  rejectAdcodeRequestSchema,
  createAdcodeCommentSchema,
  updateScenarioOutreachFormSchema,
  acceptanceScenarioRefSchema,
  scenarioAcceptanceRowSchema,
  scenarioAcceptancePanelSchema,
  scenarioTemplateQuestItemSchema,
  createScenarioTemplateSchema,
  updateScenarioTemplateSchema,
  instantiateTemplateSchema,
  saveAsTemplateSchema,
  createOperationalIssueReportSchema,
  updateIssueReportStatusSchema,
  createOperationalReportCommentSchema,
  createDropSignalSchema,
  updateDropSignalStatusSchema,
  createTimelineEventSchema,
  updateTimelineEventSchema,
  createTemplateCheckpointSchema,
  createQuestTemplateSchema,
  updateQuestTemplateSchema,
  instantiateQuestSchema,
  createHolidaySchema,
  syncHolidaysSchema,
  updateHolidaySchema,
  updateSettingsSchema,
  upsertWorkScheduleSchema,
  generateHolidaySchedulesSchema,
  batchRegisterUserSchema,
  batchRegisterSchema,
  updateHireDateSchema,
  updateOnboardingGuideSchema,
  updateEmailSchema,
  updateLevelSchema,
  updateRolesSchema,
  updateWorkTypeSchema,
  updateExcludeFromAttendanceSchema,
  createWorkLogSchema,
  updateWorkLogSchema,
  addAttachmentSchema,
  createCampaignRetroObservationSchema,
  createSelectionGuidelineSchema,
  updateCampaignRetroDeadlineSchema,
  recordCampaignReportSchema,
  campaignRetroListQuerySchema,
  selectionGuidelineQuerySchema,
  managerBonusListQuerySchema,
  managerBonusRowCountsSchema,
  confirmPerformanceReportSchema,
  initializeCampaignTargetSlotsSchema,
  updateCampaignSlotConditionsSchema,
  updateTargetSlotOverridesSchema,
  updateTargetSlotParticipantSchema,
  assignCampaignTargetSlotSchema,
  cancelCampaignTargetSlotSchema,
  targetSlotCandidatesQuerySchema,
  targetSlotChangedEventSchema,
  videoVisibilityReviewSchema,
  campaignVideoTrackingMetricsSchema,
  videoTrackingRegisterRequestSchema,
  videoTrackingConfirmReplacementRequestSchema,
  videoTrackingControlRequestSchema,
  videoTrackingCorrectLinkRequestSchema,
  videoTrackingConfirmRequestSchema,
  VIDEO_TRACKING_INBOX_REASONS,
  VIDEO_TRACKING_ERROR_MESSAGES,
  parseTiktokVideoLink,
  videoTrackingErrorMessage,
  campaignTimelineEntrySchema,
  campaignTimelineSaveSchema,
  TEAM_SCHEDULE_MAX_DAYS,
  teamScheduleQuerySchema,
  representativeImageSchema,
  updateReplyTemplateSchema,
  createReplyTemplateSchema,
  OUTREACH_CTA_PRESETS,
  OUTREACH_CTA_ALIGNS,
  OUTREACH_CTA_WIDTHS,
  outreachDraftCtaSchema,
  outreachDraftContentSchema,
  saveOutreachDraftSchema,
  outreachTestSendSchema,
  REACH_CHANNELS,
  REACH_CHANNEL_LABELS,
  OUTREACH_SELECTION_MODES,
  OUTREACH_SELECTION_MAX_IDS,
  OUTREACH_PICK_COUNT_MAX,
  saveOutreachSelectionSchema,
  OUTREACH_PRESENCE_LEASE_MS,
  OUTREACH_PRESENCE_HEARTBEAT_MS,
  outreachPresenceLeaseExpiry,
  judgeCampaignVideoReviewRevisionSchema,
  cancelCampaignVideoReviewRevisionSchema,
  createApprovedOriginalUploadSchema,
  wikiAreaKeySchema,
  createWikiAreaSchema,
  updateWikiAreaSchema,
  WIKI_TITLE_MAX,
  WIKI_BODY_MAX,
  createWikiDocumentSchema,
  updateWikiDocumentSchema,
  updateWikiDocumentToolSchema,
  wikiRevisionGuardSchema,
  revertWikiDocumentSchema,
  listWikiDocumentsQuerySchema,
  getWikiDocumentQuerySchema,
  searchWikiQuerySchema,
  wikiByTitleQuerySchema,
  recentWikiChangesQuerySchema,
  WIKI_IMAGE_BUCKET,
  WIKI_IMAGE_MIME_TYPES,
  wikiImageExtension,
  isWikiImageFile,
  wikiImageSrc,
  wikiImageFileFromSrc,
  createWikiImageUploadSchema,
  updateWikiSectionSchema,
  WIKI_DISCUSSION_TITLE_MAX,
  WIKI_COMMENT_MAX,
  createWikiDiscussionSchema,
  createWikiDiscussionCommentSchema,
  listWikiDiscussionsQuerySchema,
  wikiDocumentMetricsSchema,
  wikiMetricsSchema,
  SHORT_LINK_PREVIEW_BOT_PATTERN,
  SHORT_LINK_ALIAS_PATTERN,
  SHORT_LINK_RESERVED_ALIASES,
  isShortLinkTargetAllowed,
  shortLinkSubjectKeySchema,
  shortLinkSchemas,
  createShortLinkSchema,
  updateShortLinkSchema,
  listShortLinksSchema,
  questDependencyReasonSchema,
  questDependencyHistoryQuerySchema
};
//# sourceMappingURL=chunk-F7YIJ3TC.mjs.map